import moment from 'moment';
import {ActivityEndType} from '../backend/enums/activity-end-type';
import {AffiliationType} from '../backend/enums/affiliation-type';
import {BackendLanguageTag} from '../backend/enums/backend-language-tag';
import {CameraFacing} from '../backend/enums/camera-facing';
import {CaregiverRelationshipRole} from '../backend/enums/caregiver-relationship-role';
import {DeviceType} from '../backend/enums/device-type';
import {EntitySpecialPurpose} from '../backend/enums/entity-special-purpose';
import {EntityType} from '../backend/enums/entity-type';
import {InviteState} from '../backend/enums/invite-state';
import {PlaylistType} from '../backend/enums/playlist-type';
import {MobileNetworkType} from '../backend/enums/mobile-network-type';
import {OperativeSystemName} from '../backend/enums/operative-system-name';
import {ProcessStatus} from '../backend/enums/process-status';
import {RegionCode} from '../backend/enums/region-code';
import {RelationshipType} from '../backend/enums/relationship-type';
import {ReminderResponseValue} from '../backend/enums/reminder-response-value';
import {UserGender} from '../backend/enums/user-gender';
import {UserSignInType} from '../backend/enums/user-sign-in-type';
import {UserType} from '../backend/enums/user-type';
import {InviteContactMethod} from '../enums/invite-contact-method';
import {MomentLanguageCode} from '../enums/moment-language-code';
import {SignInError} from '../enums/sign-in-error';
import {TimeLapseType} from '../enums/time-lapse-type';
import {LanguageTranslation} from './types';
import {Religion} from '../backend/enums';
import {CaregiverTaskCompletion} from '../enums/caregiver-task-completion';
import {CareTaskNotDoneReason} from '../backend/enums/care-task-not-done-reason';
import {CareTaskFeature} from '../backend/enums/care-task-feature';
import {RolePermissionType} from '../backend/enums/role-permission-type';
import {PosologySchedule} from '../backend/enums/posology-schedule';
import {Frequency} from '../types';
import {EducationLevel} from '../backend/enums/education-level';
import {MaritalStatus} from '../backend/enums/marital-status';

// Overriding
moment.updateLocale(MomentLanguageCode.esES, {
  // Start week on Sunday
  // https://github.com/react-dates/react-dates/issues/60#issuecomment-244576967
  week: {
    dow: 0,
  },
});

export const es_ES: LanguageTranslation = {
  legal_documents: {
    terms_and_conditions: 'Términos y Condiciones',
    privacy_policy: 'Política de Privacidad',
  },
  products: {
    bluebird: 'Sioslife',
    bluebird_with_gender: 'a Sioslife',
  },
  family_applications: {
    title: 'Aplicaciones',
    entity: 'Entidad',
    choose_entity: 'Seleccione una entidad para ver las aplicaciones de esta persona mayor.',
    no_affiliations:
      'Esta persona mayor no tiene afiliaciones a entidades. Las aplicaciones no están disponibles.',
    missing_affiliations:
      'Las afiliaciones no están disponibles. Actualice su sesión o contacte con soporte.',
    load_error:
      'No se pudieron cargar las aplicaciones. Es posible que se haya denegado el acceso o que la aplicación no esté disponible.',
    retry: 'Reintentar',
    more: 'Cargar más',
    back: 'Volver a las aplicaciones',
    access: 'Acceso de la persona mayor',
    enabled: 'Activado',
    disabled: 'Desactivado',
    enable: 'Activar acceso',
    disable: 'Desactivar acceso',
    confirm:
      '¿Cambiar el acceso de esta persona mayor a la aplicación? Se aplica a todas las entidades; no instala la aplicación ni cambia su disponibilidad en la entidad.',
    write_error: 'No se pudo cambiar el acceso. Inténtelo de nuevo.',
    write_success: 'Acceso actualizado.',
  },
  reminders: {
    group_time: 'Hora',
    group_reminder: 'Recordatorio',
    group_repeats: 'Repetición',
    group_recipients: 'Destinatarios',
    group_actions: 'Acciones',
    no_entity_assigned: 'Sin entidad asignada',
    group_switch_entity: 'Cambie a la entidad del recordatorio para gestionarlo.',
    group_edit: 'Editar grupo de recordatorios',
    group_delete: 'Eliminar grupo de recordatorios',
    group_delete_confirm: '¿Eliminar “{{title}}” para todos los destinatarios ({{count}})?',
    group_partial: 'Algunos cambios fallaron. Reinténtelos o cierre para actualizar.',
    group_future_required: 'Elige una fecha/hora futura.',
    group_advanced:
      'La hora y la repetición de esta programación avanzada son fijas. Puede cambiar la fecha de inicio.',
    my_title: 'Mis recordatorios',
    my_scope: 'Recordatorios creados por ti en todas las entidades.',
    creation_scope:
      'Los nuevos recordatorios se crean para personas mayores de la entidad seleccionada: {{entity}}.',
    recipient: 'Destinatario',
    title: 'Recordatorios',
    subtitle: 'Gestionar los recordatorios de esta persona mayor.',
    add: 'Crear recordatorio',
    bulk_title: 'Crear recordatorios',
    bulk_success: 'Se crearon {{success}} de {{total}} recordatorios.',
    bulk_partial:
      'Se crearon {{success}} de {{total}} recordatorios. Vuelva a intentarlo para las demás personas.',
    edit: 'Editar recordatorio',
    loading: 'Cargando recordatorios…',
    empty_title: 'Todavía no hay recordatorios',
    empty_description: 'Cree un recordatorio para la persona seleccionada.',
    load_more: 'Cargar más',
    retry: 'Intentar de nuevo',
    load_error: 'No se pudieron cargar los recordatorios.',
    senior_unavailable:
      'La persona seleccionada no está disponible. Vuelva a seleccionarla para continuar.',
    delete_title: 'Eliminar recordatorio',
    delete_description:
      '¿Seguro que desea eliminar este recordatorio? Esta acción no se puede deshacer.',
    delete: 'Eliminar',
    cancel: 'Cancelar',
    enable: 'Activar recordatorio',
    disable: 'Desactivar recordatorio',
    status_active: 'Activo',
    status_inactive: 'Desactivado',
    next_run: 'Próxima ejecución',
    save_error: 'No se pudo guardar el recordatorio.',
    save_success: 'Recordatorio guardado correctamente.',
    delete_error: 'No se pudo eliminar el recordatorio.',
    delete_success: 'Recordatorio eliminado correctamente.',
    active_error: 'No se pudo cambiar el estado del recordatorio.',
    active_success: 'Recordatorio activado correctamente.',
    inactive_success: 'Recordatorio desactivado correctamente.',
    response_types: {yes_no: 'Sí o no', ok: 'OK'},
    recurrence: {
      once: 'Una vez',
      per_minute: 'Cada minuto',
      hourly: 'Cada {{count}} hora(s)',
      daily: 'Cada {{count}} día(s)',
      weekly: 'Cada {{count}} semana(s)',
      monthly: 'Cada {{count}} mes(es)',
      yearly: 'Cada {{count}} año(s)',
      current_unsupported: 'Valor actual ({{value}})',
    },
    form: {
      title: 'Título',
      title_placeholder: 'Escriba el título del recordatorio',
      message: 'Mensaje',
      message_placeholder: 'Escriba el mensaje del recordatorio',
      response_type: 'Tipo de respuesta',
      date: 'Fecha inicial',
      time: 'Hora inicial',
      recurrence: 'Repetición',
      recurrence_count: 'Repetir cada',
      save: 'Guardar',
      title_required: 'El título es obligatorio.',
      title_too_long: 'El título no puede superar los {{max}} caracteres.',
      message_required: 'El mensaje es obligatorio.',
      message_too_long: 'El mensaje no puede superar los {{max}} caracteres.',
      date_required: 'La fecha es obligatoria.',
      time_required: 'La hora es obligatoria.',
      invalid_date_time: 'Introduzca una fecha y hora válidas.',
      date_in_past: 'La fecha no puede estar en el pasado.',
      time_in_past: 'La hora no puede estar en el pasado.',
    },
  },
  family_services: {
    title: 'Servicios',
    choose: 'Elija una institución',
    none: 'No hay instituciones asociadas.',
    unavailable: 'Los servicios no están disponibles en sus instituciones.',
    empty: 'No hay servicios disponibles.',
    error: 'No se pudieron cargar los servicios.',
    denied: 'Acceso denegado.',
    not_found: 'Institución o servicio no disponible.',
    retry: 'Reintentar',
    call: 'Llamar',
    no_phone: 'Contacto telefónico no disponible.',
    unsupported: 'Acción de contacto no disponible para este servicio.',
    active: 'Activo',
    inactive: 'Inactivo',
    enable: 'Activar',
    disable: 'Desactivar',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
    save_error: 'No se pudo actualizar el servicio.',
    saved: 'Servicio actualizado.',
    refresh_error: 'Actualizado, pero no se pudo actualizar la lista.',
  },
  family_invite: {
    contact_method: 'Método de contacto',
    phone: 'Teléfono móvil',
    action: 'Invitar a un cuidador',
    context: 'Invitar a un cuidador para {{name}}',
    name: 'Nombre',
    email: 'Correo electrónico',
    next: 'Continuar',
    back: 'Volver',
    send: 'Enviar invitación',
    confirm: '¿Enviar una invitación a {{name}} a {{destination}}?',
    success: 'Invitación enviada. La persona debe aceptarla antes de ser contacto.',
    uncertain:
      'Puede que se haya enviado la invitación. Consulte con la persona antes de enviar otra.',
    invalid: 'Compruebe el nombre y los datos de contacto.',
    denied: 'No tiene permiso para enviar esta invitación.',
    unavailable: 'La persona mayor seleccionada no está disponible.',
  },
  family_profile: {
    user_details: 'Datos de la persona usuaria',
    remove_link: 'Eliminar vínculo',
    remove_link_success: 'Tu relación se ha eliminado.',
    remove_link_confirm:
      '¿Eliminar tu relación con {{name}}? Perderás acceso a su información. Esto no eliminará a la persona mayor ni la relación de otra persona.',
    remove_link_error: 'No se pudo eliminar tu relación. Inténtalo de nuevo.',
    remove_link_denied: 'No tienes permiso para eliminar esta relación.',
    cancel: 'Cancelar',
    institutions: 'Instituciones',
    senior_contacts: 'Contactos mayores',
    senior_empty: 'No se encontraron contactos mayores.',
    title: 'Perfil',
    contacts: 'Cuidadores',
    empty: 'No se encontraron cuidadores.',
    load_more: 'Cargar más',
    loading: 'Cargando…',
    error: 'No se pudieron cargar los datos.',
    denied: 'Acceso denegado.',
    unavailable: 'Esta información no está disponible.',
    retry: 'Reintentar',
    close: 'Cerrar',
  },
  family_media: {
    recording: 'Grabando',
    camera: 'Usar cámara',
    take_photo: 'Hacer foto',
    record_video: 'Grabar vídeo',
    stop_recording: 'Detener grabación',
    camera_error: 'Cámara o micrófono no disponible. Compruebe los permisos e inténtelo de nuevo.',
    download: 'Descargar',
    share: 'Compartir',
    export_error: 'No se pudo preparar el archivo. Inténtalo de nuevo o utiliza otro navegador.',
    title: 'Álbum familiar',
    upload: 'Subir',
    refresh: 'Actualizar',
    loading: 'Cargando el álbum…',
    empty_title: 'Todavía no hay fotos ni vídeos',
    empty_description: 'Aquí aparecerán las fotos y los vídeos compartidos con esta persona mayor.',
    load_error: 'No se pudo cargar el álbum.',
    refresh_error: 'No se pudo actualizar el álbum.',
    load_more: 'Cargar más',
    loading_more: 'Cargando más…',
    retry: 'Intentar de nuevo',
    senior_unavailable:
      'La persona seleccionada no está disponible. Vuelva a seleccionarla para continuar.',
    photo: 'Foto',
    video: 'Vídeo',
    processing: 'Procesando el vídeo…',
    processing_failed: 'No se pudo procesar este contenido.',
    uploaded_by: 'Subido por',
    you: 'Tú',
    created_at: 'Añadido el',
    description: 'Descripción',
    edit: 'Editar',
    delete: 'Eliminar',
    cancel: 'Cancelar',
    save: 'Guardar',
    saving: 'Guardando…',
    upload_photo: 'Subir foto o vídeo',
    upload_progress: 'Subiendo…',
    invalid_file_type: 'Seleccione un archivo JPG, PNG, GIF, MP4 o MOV.',
    upload_request_error: 'No se pudo iniciar la subida.',
    upload_transfer_error: 'No se pudo subir el archivo.',
    upload_record_error:
      'La solicitud para guardar el archivo ha fallado, pero es posible que se haya guardado. Actualice y compruebe el álbum antes de volver a intentarlo para evitar duplicados.',
    upload_success: 'Subida completada. Los vídeos pueden tardar en procesarse.',
    edit_success: 'Descripción actualizada correctamente.',
    edit_error: 'No se pudo actualizar la descripción.',
    router_prompt_edit_title: '¿Salir sin guardar los cambios de este elemento del álbum?',
    router_prompt_edit_message: 'Al salir, perderá los cambios realizados en la descripción.',
    delete_success: 'Elemento del álbum eliminado correctamente.',
    delete_error: 'No se pudo eliminar este elemento del álbum.',
    delete_confirm_title: 'Eliminar elemento del álbum',
    delete_confirm_description:
      '¿Seguro que desea eliminar este elemento? Esta acción no se puede deshacer.',
    detail_not_found: 'No se ha encontrado este elemento del álbum.',
    detail_load_error: 'No se pudo cargar este elemento.',
    detail_unavailable: 'Este elemento no está disponible.',
    signed_url_error: 'No se pudo abrir este elemento. Inténtelo de nuevo.',
  },
  relationship_types: {
    [RelationshipType.Family]: 'Familiar',
    [RelationshipType.Friend]: 'Amigo',
    [RelationshipType.Healthcare]: 'Profesional asistencial',
    [RelationshipType.Service]: 'Servicio',
    [RelationshipType.Other]: 'Otro',
  },
  user_types: {
    [UserType.Caregiver]: 'Cuidador',
    [UserType.Follower]: 'Seguidor',
    [UserType.Senior]: 'Mayor',
  },
  genders: {
    [UserGender.Female]: 'Femenino',
    [UserGender.Male]: 'Masculino',
    [UserGender.Undisclosed]: 'No revelado',
    [UserGender.Unspecified]: 'Otro',
  },
  marital_statuses: {
    [MaritalStatus.CivilUnion]: 'Unión de hecho',
    [MaritalStatus.Divorced]: 'Divorciado',
    [MaritalStatus.Married]: 'Casado',
    [MaritalStatus.Other]: 'Otro',
    [MaritalStatus.Single]: 'Soltero',
    [MaritalStatus.Widowed]: 'Viudo',
  },
  education_levels: {
    [EducationLevel.BachelorsDegree]: 'Grado universitario',
    [EducationLevel.BasicEducation]: 'Educación básica',
    [EducationLevel.DoctoratePhd]: 'Doctorado',
    [EducationLevel.MastersDegree]: 'Máster',
    [EducationLevel.NoFormalEducation]: 'Sin estudios',
    [EducationLevel.Other]: 'Otro',
    [EducationLevel.PostgraduateDiploma]: 'Posgrado',
    [EducationLevel.PrimaryEducation]: 'Educación primaria',
    [EducationLevel.SecondaryEducation]: 'Educación secundaria',
    [EducationLevel.VocationalTechnicalTraining]: 'Formación técnica profesional',
  },
  languages: {
    [BackendLanguageTag.ptPT]: 'Portugués (Portugal)',
    [BackendLanguageTag.enUS]: 'Inglés (EUA)',
    [BackendLanguageTag.esES]: 'Español (España)',
    [BackendLanguageTag.glES]: 'Gallego (España)',
    [BackendLanguageTag.ptBR]: 'Portugués (Brasil)',
  },
  regions: {
    [RegionCode.PT]: 'Portugal',
    [RegionCode.ES]: 'España',
    [RegionCode.GL]: 'Galicia',
    [RegionCode.BR]: 'Brasil',
  },
  roles: {
    [CaregiverRelationshipRole.Administrator]: 'Administrador',
    [CaregiverRelationshipRole.Contact]: 'Contacto',
    [CaregiverRelationshipRole.Member]: 'Miembro',
  },
  religions: {
    [Religion.AfricanTraditionalAndDiasporic]: 'Tradicional y diáspora africana',
    [Religion.Agnostic]: 'Agnosticismo',
    [Religion.Animism]: 'Animismo',
    [Religion.Atheist]: 'Ateísmo',
    [Religion.BahaI]: 'Bahaísmo',
    [Religion.Buddhism]: 'Budismo',
    [Religion.CaoDai]: 'Caodaísmo',
    [Religion.ChineseTraditionalReligion]: 'Tradicional china',
    [Religion.Christianity]: 'Cristianismo',
    [Religion.Hinduism]: 'Hinduismo',
    [Religion.Islam]: 'Islamismo',
    [Religion.Jainism]: 'Jainismo',
    [Religion.Juche]: 'Juche',
    [Religion.Judaism]: 'Judaísmo',
    [Religion.NeoPaganism]: 'Neopaganismo',
    [Religion.Nonreligious]: 'Sem religión',
    [Religion.Rastafari]: 'Rastafari',
    [Religion.Secular]: 'Secularismo',
    [Religion.Shinto]: 'Sintoísmo',
    [Religion.Sikhism]: 'Sijismo',
    [Religion.Spiritism]: 'Espiritismo',
    [Religion.Tenrikyo]: 'Tenrikyō',
    [Religion.UnitarianUniversalism]: 'Unitarismo universalista',
    [Religion.Zoroastrianism]: 'Zoroastrismo',
    [Religion.PrimalIndigenous]: 'Indígena',
    [Religion.Undisclosed]: 'No revelado',
    [Religion.Unspecified]: 'Otro',
  },
  role_permissions_section: {
    overview: 'Visión general',
    seniors: 'Mayores',
    team: 'Equipo',
    'digital-inclusion': 'Inclusión digital',
    settings: 'Ajustes',
    'daily-care-log': 'Registro de cuidados diarios',
    calendar: 'Calendario',
    occurrences: 'Ocurrencias',
    'quick-access-devices': 'Acceso rápido',
  },
  role_permissions: {
    [RolePermissionType.Overview_Activity]: 'Actividad general',
    [RolePermissionType.Overview_Statistics]: 'Estadísticas generales',
    [RolePermissionType.Seniors_List]: 'Lista de Mayores',
    [RolePermissionType.Seniors_Dashboard]: 'Cuadro de mandos',
    [RolePermissionType.Seniors_Dashboard_Activity]: 'Cuadro de mandos > Atividade',
    [RolePermissionType.Seniors_Dashboard_GeneralStatistics]:
      'Cuadro de mandos > Estadísticas generales',
    [RolePermissionType.Seniors_Dashboard_HealthStatistics]:
      'Cuadro de mandos > Estadísticas de salud',
    [RolePermissionType.Seniors_IndividualPlan]: 'Plan individual',
    [RolePermissionType.Seniors_PersonalData_Reminders]: 'Recordatorios',
    [RolePermissionType.Seniors_BADL]: 'Plan de cuidados > ABVD',
    [RolePermissionType.Seniors_IADL]: 'Plan de cuidados > AIVD',
    [RolePermissionType.Seniors_ClinicalSupport]: 'Plan de cuidados > Apoyo Clínico',
    [RolePermissionType.Seniors_PersonalDevelopment]: 'Plan de cuidados > Desarr. personal',
    [RolePermissionType.Seniors_CareAndActivities]: 'Plan de cuidados > Resumen de cuidados',
    [RolePermissionType.Seniors_CareAndActivities_BADL]:
      'Plan de cuidados > Resumen de cuidados > ABVD',
    [RolePermissionType.Seniors_CareAndActivities_IADL]:
      'Plan de cuidados > Resumen de cuidados > AIVD',
    [RolePermissionType.Seniors_CareAndActivities_ClinicalSupport]:
      'Plan de cuidados > Resumen de cuidados > Apoyo Clínico',
    [RolePermissionType.Seniors_CareAndActivities_PersonalDevelopment]:
      'Plan de cuidados > Resumen de cuidados > Desarr. personal',
    [RolePermissionType.Seniors_CareServices]: 'Escala de Servicios',
    [RolePermissionType.Seniors_PersonalData_Profile]: 'Datos personales > Perfil',
    [RolePermissionType.Seniors_PersonalData_Caregivers]: 'Datos personales > Cuidadores',
    [RolePermissionType.Seniors_PersonalData_Health]: 'Datos personales > Salud',
    [RolePermissionType.Seniors_DigitalInclusion_Applications]: 'Inclusión Digital > Aplicaciones',
    [RolePermissionType.Seniors_DigitalInclusion_Devices]: 'Inclusión Digital > Dispositivos',
    [RolePermissionType.Seniors_DigitalInclusion_Playlists]:
      'Inclusión Digital > Listas de reproducción',
    [RolePermissionType.Seniors_DigitalInclusion_Cards]: 'Inclusión Digital > Tarjetas',
    [RolePermissionType.Seniors_PersonalData_Attachments]: 'Datos personales > Adjuntos',
    [RolePermissionType.Team_Professionals]: 'Lista de Profesionales',
    [RolePermissionType.Team_CareServices]: 'Escala General de Servicio',
    [RolePermissionType.Team_Professionals_Profile]: 'Profesional > Perfil',
    [RolePermissionType.Team_Professionals_Applications]: 'Profesional > Aplicaciones',
    [RolePermissionType.Team_Professionals_Cards]: 'Profesional > Tarjetas',
    [RolePermissionType.Team_Professionals_Playlists]: 'Profesional > Listas de Reproducción',
    [RolePermissionType.Team_Professionals_CareServices]: 'Profesional > Escala de Servicio',
    [RolePermissionType.Team_Shifts_Management]: 'Gestión de turnos',
    [RolePermissionType.Team_Shifts_Attributions]: 'Asignación de turnos',
    [RolePermissionType.Team_Permissions]: 'Permisos',
    [RolePermissionType.Team_AttendanceTracker_Registry]: 'Registro de asistencia > Registros',
    [RolePermissionType.Team_AttendanceTracker_Assignments]:
      'Registro de asistencia > Asignaciones',
    [RolePermissionType.DigitalInclusion_Applications]: 'Aplicaciones',
    [RolePermissionType.DigitalInclusion_Media]: 'Fotografías',
    [RolePermissionType.DigitalInclusion_Devices]: 'Dispositivos',
    [RolePermissionType.DigitalInclusion_Cards]: 'Tarjetas',
    [RolePermissionType.DigitalInclusion_Playlists]: 'Listas de reproducción',
    [RolePermissionType.DigitalInclusion_Services]: 'Servicios',
    [RolePermissionType.Settings_Entity_Profile]: 'Institución > Perfil',
    [RolePermissionType.Settings_Entity_Settings]: 'Institución > Configuración',
    [RolePermissionType.Settings_Licenses]: 'Licencias',
    [RolePermissionType.Settings_BADL]: 'ABVD',
    [RolePermissionType.Settings_IADL]: 'AIVD',
    [RolePermissionType.Settings_ClinicalSupport]: 'Apoyo Clínico',
    [RolePermissionType.Settings_PersonalDevelopment]: 'Desa. personal',
    [RolePermissionType.Settings_Templates]: 'Plantillas',
    [RolePermissionType.Settings_Occurrences]: 'Ocurrencias',
    [RolePermissionType.DailyCareLog]: 'Registro de cuidados',
    [RolePermissionType.DailyCareLog_BADL]: 'Registro de cuidados > ABVD',
    [RolePermissionType.DailyCareLog_IADL]: 'Registro de cuidados > AIVD',
    [RolePermissionType.DailyCareLog_PersonalDevelopment]: 'Registro de cuidados > Desa. personal',
    [RolePermissionType.DailyCareLog_ClinicalSupport]: 'Registro de cuidados > Apoio Clínico',
    [RolePermissionType.DailyCareLog_Occurrences]: 'Registro de cuidados > Ocurrencias',
    [RolePermissionType.Calendar]: 'Calendario',
    [RolePermissionType.Calendar_BADL]: 'Calendario > ABVD',
    [RolePermissionType.Calendar_IADL]: 'Calendario > AIVD',
    [RolePermissionType.Calendar_PersonalDevelopment]: 'Calendario > Desa. personal',
    [RolePermissionType.Calendar_ClinicalSupport]: 'Calendario > Apoio Clínico',
    [RolePermissionType.Occurrences]: 'Lista de Ocurrencias',
    [RolePermissionType.SettingsQuickAccessDevices]: 'Acceso Rápido',
  },
  affiliation_types: {
    [AffiliationType.Senior]: 'Mayor',
    [AffiliationType.Caregiver]: 'Cuidador',
    [AffiliationType.Administrator]: 'Administrador',
    [AffiliationType.Manager]: 'Manager',
    [AffiliationType.Operator]: 'Operador',
    [AffiliationType.MedicalProfessional]: 'Profesional de salud',
  },
  screen_paths: {
    professional_caregiver_seniors: 'Mayores',
    professional_caregiver_team: 'Equipo',
    professional_caregiver_roles_and_permissions: 'Cargo y permisos',
    senior_dashboard: 'Cuadro de mandos',
    senior_individual_plan: 'Plan individual',
    senior_care_plan: 'Plan de cuidados',
    senior_care_and_activity: 'Resumen de cuidados',
    senior_personal_data: 'Datos personales',
    senior_profile: 'Perfil',
    senior_caregivers: 'Cuidadores',
    senior_applications: 'Aplicaciones',
    senior_devices: 'Dispositivos',
    senior_activity: 'Atividade',
    senior_user_cards: 'Tarjetas',
    senior_statistics: 'Estadísticas',
    senior_health: 'Salud',
    senior_playlists: 'Listas de Reproducción',
    senior_attachments: 'Adjuntos',
    institution_settings_profile: 'Perfil',
    institution_settings_settings: 'Configuración',
    institution_settings_activity: 'Atividade',
    institution_settings_user_cards: 'Tarjetas',
    institution_settings_devices: 'Dispositivos',
    institution_settings_services: 'Servicios',
    institution_settings_playlist: 'Listas de Reproducción',
    institution_settings_statistics: 'Estadísticas',
    institution_settings_licenses: 'Licencias',
    institution_settings_templates: 'Plantillas',
    institution_settings_occurrences: 'Ocurrencias',
    institution_settings_quick_access_devices: 'Acceso Rápido',
  },
  institution_settings_screen: {
    subtitle: {
      write: 'Puedes configurar los ajustes de la institución',
      read: 'Puedes consultar los ajustes de la institución',
    },
    columns: {
      setting: 'Configuración',
      active: 'Activa',
    },
    attendance_tracker: 'Registro de asistencia',
    load_error_toast: 'No se pudieron cargar los ajustes',
    update_error_toast: 'No se pudo actualizar el ajuste',
  },
  attendance_tracker_registry_screen: {
    subtitle: 'Registros de jornada de los profesionales',
    filters: {
      all_statuses: 'Todos los estados',
      all_day: 'Todo el día',
      all_professionals: 'Todos los profesionales',
    },
    empty: 'No hay registros de jornada en este día.',
    load_error: 'No se pudieron cargar los registros de jornada.',
    retry: 'Reintentar',
    waiting_exit: 'Esperando el registro de salida',
    add: {
      title: 'Añadir registro de jornada',
      professional: 'Profesional',
      professionals_hint:
        'Solo aparecen los profesionales con el registro de jornada activo (obligatorio u opcional).',
      professionals_empty:
        'No hay profesionales con el registro de jornada activo (obligatorio u opcional).',
      professional_placeholder: 'Elija el profesional',
      kind_label: '¿Qué desea registrar?',
      kind_session: 'Sesión completa (entrada y salida)',
      kind_entry: 'Solo la entrada (sesión en curso)',
      kind_exit: 'Salida pendiente',
      open_session_info:
        'Este profesional tiene una entrada abierta desde {{date}}. Registre la salida pendiente.',
      entry_date: 'Fecha de la entrada',
      entry_time: 'Hora de la entrada',
      exit_date: 'Fecha de la salida',
      exit_time: 'Hora de la salida',
      reason: 'Motivo',
      reason_placeholder: 'Ej.: el profesional olvidó registrar la salida',
      notice:
        'Este registro quedará identificado como registrado por {{admin}} en nombre de {{professional}} y no tiene ubicación.',
      self_warning:
        'No puede registrar su propia jornada por aquí, para evitar que registre en su propio nombre. Para registrar su entrada o salida, utilice el {{app}}.',
      self_action: 'Abrir el {{app}}',
      cancel: 'Cancelar',
      continue: 'Continuar',
      back: 'Volver',
      confirm: 'Registrar',
      confirm_title: 'Confirme el registro',
      confirm_description:
        'Una vez registrado, el registro de jornada no se puede modificar ni borrar.',
      summary_record: 'Registro',
      success: 'Registro de jornada añadido correctamente.',
      checking: 'Comprobando el registro de jornada del profesional...',
      error_future: 'La fecha y la hora no pueden estar en el futuro.',
      error_order:
        'La salida debe ser posterior a la entrada y no puede haber otro registro en la misma fecha y hora.',
      error_overlap: 'Las fechas elegidas se solapan con otro registro del profesional.',
      error_type:
        'El registro no encaja con los registros que el profesional ya tiene. Compruebe las fechas y las horas.',
      error_conflict:
        'Los registros del profesional han cambiado entretanto. Compruebe los datos e inténtelo de nuevo.',
      error_not_tracking: 'Este profesional no tiene el registro de jornada activo.',
      error_generic: 'No se pudo añadir el registro de jornada. Inténtelo de nuevo.',
      validation_future: 'No puede estar en el futuro.',
      validation_exit_before_entry: 'La salida debe ser posterior a la entrada.',
      validation_exit_before_open_entry: 'La salida debe ser posterior a la entrada abierta.',
      validation_reason_required: 'Indique el motivo.',
    },
    void: {
      title: 'Anular sesión',
      description:
        'Al anular, la sesión deja de contar como tiempo trabajado y ese periodo queda libre para nuevos registros. El registro se conserva como anulado, con su nombre y el motivo.',
      reason: 'Motivo de la anulación',
      reason_placeholder: 'Ej.: registro hecho por error',
      cancel: 'Cancelar',
      confirm: 'Anular sesión',
      success: 'Sesión anulada correctamente.',
      error_conflict:
        'Los registros del profesional han cambiado entretanto. Actualice la lista e inténtelo de nuevo.',
      error_self: 'No puede anular sus propios registros.',
      error_generic: 'No se pudo anular la sesión. Inténtelo de nuevo.',
      validation_reason_required: 'Indique el motivo.',
    },
    correct: {
      title: 'Corregir sesión',
      description:
        'La sesión actual se anulará y será sustituida por los registros que indique a continuación. El registro anulado se conserva, y el tiempo que tenía queda libre para los nuevos registros.',
      current_session: 'Sesión actual',
      reason: 'Motivo de la corrección',
      notice:
        'Los nuevos registros quedarán identificados como registrados por {{admin}} en nombre de {{professional}} y no tienen ubicación. La sesión actual quedará anulada, con este motivo.',
      confirm_title: 'Confirme la corrección',
      confirm_description:
        'La sesión actual se anulará y los nuevos registros ocuparán su lugar. Una vez registrados, no se pueden modificar ni borrar.',
      summary_old: 'Sesión a anular',
      summary_new: 'Nueva sesión',
      confirm: 'Corregir sesión',
      success: 'Sesión corregida correctamente.',
    },
    details: {
      correct_action: 'Corregir',
      void_action: 'Anular sesión',
      duration: 'Duración',
      title: 'Detalles del registro',
      close: 'Cerrar',
    },
  },
  attendance_tracker_assignments_screen: {
    subtitle: {
      write: 'Puedes definir quién debe registrar la asistencia',
      read: 'Puedes consultar quién debe registrar la asistencia',
    },
    columns: {
      professional: 'Profesional',
      assignment: 'Registro de asistencia',
      last_change: 'Último cambio',
    },
    assignment_types: {
      mandatory: 'Obligatorio',
      optional: 'Opcional',
      not_applicable: 'No aplicable',
    },
    empty: 'No hay profesionales',
    last_change_summary: 'Por {{name}} el {{date}}',
    last_change_summary_no_author: '{{date}}',
    load_error_toast: 'No se pudieron cargar las asignaciones',
    update_error_toast: 'No se pudo actualizar la asignación',
  },
  date_time_picker: {
    cancel_button: 'Cancelar',
    confirm_button: 'Confirmar',
    clear_button: 'Apagar',
    today: 'Hoy',
  },
  date_range_input: {
    title: 'Elija un rango de tiempo',
    ok_button: 'Confirmar',
    cancel_button: 'Cancelar',
    presets: {
      day: 'Día',
      week: 'Semana',
      month: 'Mes',
      '7_days': '7 días',
      '15_days': '15 días',
      '30_days': '30 días',
      '180_days': '180 días',
      '365_days': '365 días',
      custom: 'Personalizado',
    },
    errors: {
      invalid_date_range: 'Rango de fechas inválido',
    },
  },
  generic_header: {
    actions: {
      edit: 'Editar',
      delete: 'Borrar plan',
      save: 'Guardar plantilla',
      import: 'Importar plantilla',
      archivePlan: 'Archivar plan',
      remove: 'Eliminar',
    },
  },
  user_card: {
    actions: {
      edit: 'Editar',
      delete: 'Borrar',
    },
  },
  role_autocomplete: {
    unselected: 'No seleccionado',
    no_matching: 'No se ha encontrado ningún {{ type }} que coincida.',
  },
  view_type_toggler: {
    tooltip_message: 'Cambia a la vista {{ type }}',
  },
  index_grid_screen: {
    total_counter: 'Total',
    add_button: 'Añadir',
    search_placeholder: 'Buscar',
    filter_placeholder: 'Filtrar',
    empty_message_add_encouraging: 'Pulse añadir para registrar {{ type }}.',
    empty_search_message: {
      title: 'Lo sentimos, no hemos encontrado ningún resultado para "{{ name }}".',
      subtitle: 'TIntenta otra búsqueda.',
    },
    empty_filters_message: {
      title: 'Las opciones de filtro seleccionadas no han dado ningún resultado.',
      subtitle: 'Prueba diferentes filtros.',
    },
    toggler: {
      grid: 'cuadrícula',
      list: 'lista',
    },
  },
  login: {
    welcome: 'Bienvenido a la plataforma Sioslife!',
    welcome_daily_care_log: 'Bienvenido a la aplicación de registro de Sioslife',
    welcome_accept_invite: '¡Acceda a su cuenta para aceptar la invitación!',
    instruction: 'Utilice sus credenciales para acceder',
    email_input: 'Dirección de e-mail',
    password_input: 'Contraseña',
    enter_button: 'Login',
    enter_button_microsoft: 'Iniciar sesión con Microsoft',
    enter_button_google: 'Iniciar sesión con Google',
    forgot_password: 'Recuperar contraseña',
    quick_access: 'Iniciar sesíon con acceso rápido',
    validate_invite: '¿Ha sido invitado para ser cuidador?',
    use_invite_code: 'Utilizar código de invitación',
    errors: {
      form_validation_invalid_field: 'Complete este campo para iniciar sesión en su cuenta',
      [SignInError.Unknown]: 'Se ha producido un error. Por favor, inténtelo más tarde.',
      [SignInError.InvalidEmail]: 'E-mail no válido',
      [SignInError.UserDisabled]: 'Usuario desactivado',
      [SignInError.UserNotFound]: 'Usuario no encontrado',
      [SignInError.WrongPassword]: 'Contraseña incorrecta',
      [SignInError.FailedToGetMe]:
        'No se pudo cargar la información del usuario. Por favor, entre en contacto con el servicio de Atención al Cliente.',
    },
  },
  recover_password: {
    title: '¿Ha olvidado su contraseña?',
    subtitle: '¡Nosotros le ayudamos!',
    instructions:
      'Para poder definir una nueva contraseña, indique por favor su correo electrónico. Le enviaremos un mensaje con un enlace que le permitirá realizar el cambio.',
    email_input: 'Dirección de e-mail',
    send_button: 'Enviar',
    password_remembered: '¡Acabo de recordar mi contraseña!',
    go_back_button: 'Volver a la pantalla de acceso',
    errors: {
      form_validation_invalid_field:
        'Complete este campo para completar la recuperación de la contraseña',
      send_request_error_message:
        'Se ha producido un error. No ha sido posible realizar la solicitud.',
    },
    success: {
      send_request_success_message:
        'Se han enviado las instrucciones para alterar su contraseña al correo electrónico que nos ha indicado.',
    },
  },
  replace_password: {
    title: 'Definir una nueva contraseña',
    instructions:
      '¡Vale! ¡Buen trabajo! Vamos a definir una nueva contraseña. Escriba su nueva contraseña en los dos espacios a continuación para finalizar el proceso.',
    new_password_input: 'Nueva contraseña',
    repeat_password_input: 'Repetir la nueva contraseña',
    submit_button: 'Confirmar',
    password_remembered: '¡Acabo de recordar mi contraseña!',
    go_back_button: 'Ir a la pantalla de acceso',
    errors: {
      form_validation_invalid_field:
        'Complete este campo para completar la definición de la nueva contraseña',
      form_validation_invalid_password_minimum_characters:
        'La contraseña debe tener al menos 6 caracteres',
      form_validation_invalid_password_blank_spaces:
        'La contraseña no puede empezar ni terminar con espacios en blanco',
      form_validation_invalid_repeat_password_mismatch:
        'Por favor, comprueba que las contraseñas son iguales',
      change_password_error_message:
        'Se ha producido un error. No ha sido posible definir su nueva contraseña.',
    },
    success: {
      change_password_success_message:
        'Se ha definido la contraseña. Ahora puede entrar con su nueva contraseña.',
    },
  },
  validate_invite: {
    title: 'Utilizar código de invitación',
    instructions_1:
      'Para ser cuidador {{brand}} deberá contar con una invitación de acceso a la plataforma.',
    instructions_2:
      'Si ya ha solicitado una invitación, por favor compruebe su buzón de email y/o sus mensajes de SMS y busque un mensaje {{brand}}. En ese mensaje encontrará el código que deberá indicar abajo.',
    code_input: 'Código de la invitación',
    send_button: 'Validar invitación y avanzar',
    errors: {
      form_validation_invalid_field:
        'Complete este campo para completar la validación de la invitación',
      invalid_invite_error_message:
        'La invitación que ha intentado usar está invalidada. Por favor, verifique si ha indicado el código correcto. En caso afirmativo, solicite una nueva invitación.',
    },
  },
  accept_invite: {
    relationship_invite_title: 'Aceptar la invitación para ser cuidador de',
    relationship_invite_instructions:
      'Para ser cuidador deberá tener una cuenta de acceso a la plataforma {{brand}}. Si es la primera vez que está aceptando una invitación, pulse en "{{button_create}}". Si ya ha aceptado otras invitaciones anteriormente, use su cuenta existente y pulse en "{{button_login}}".',
    affiliation_invite_title: 'Acepta la invitación para unirte y ser cuidador de la entidad',
    affiliation_invite_instructions:
      'Para unirte y ser cuidador deberá tener una cuenta de acceso a la plataforma {{brand}}. Si es la primera vez que está aceptando una invitación, pulse en "{{button_create}}". Si ya ha aceptado otras invitaciones anteriormente, use su cuenta existente y pulse en "{{button_login}}".',
    affiliation_type_description: 'Esta invitación le permite ser {{type}} de la entidad {{name}}.',
    create_account_button: 'Aceptar y crear nueva cuenta',
    login_button: 'Entrar en una cuenta ya existente',
    errors: {
      invalid_invite_error_message:
        'La invitación que ha intentado usar está invalidada. Por favor, solicite una nueva invitación.',
    },
  },
  quick_access_login: {
    instruction: 'Elegir el usuario para iniciar sesión',
    all_users: 'Todos los usuarios',
    error:
      'No es posible continuar con el Acceso Rápido, inténtelo de nuevo. Si el problema persiste, póngase en contacto con el administrador del sistema.',
    back_to_email_and_password_login: 'Iniciar sesión con correo electrónico y contraseña',
    go_back_to_the_recent_users_list: 'Volver a la lista de usuarios recientes',
    hello_user: 'Hola, {{name}}!',
    insert_your_quick_access_code: 'Introduzca su código de acceso rápido',
    login_failed: 'No se pudo iniciar sesión. Inténtalo de nuevo.',
    login_failed_code: 'Código de acceso rápido incorrecto',
    try_again: 'Intentar de nuevo',
    regain_access: 'Recuperar código de acceso',
    remove_recent: '¿Quiere eliminar al usuario de la lista de usuarios recientes?',
    regain_access_message:
      'Tu clave de acceso rápido podrás restablecerla en el Área de Gestión de la plataforma Sioslife, en tu perfil profesional.',
    regain_access_description:
      'Si no tienes permisos de acceso al Área de Gestión, por favor habla con un administrador de tu entidad.',
    back_to_previous: 'Volver a la pantalla anterior',
  },
  notifications: {
    date: 'Fecha',
    time: 'Hora',
    title: 'Notificaciones',
    check_read: 'Marcar como leída',
    check_unread: 'Marcar como no leída',
    read_all_notifications: 'Marcar todas como leídas',
    read_all_notifications_dialog_title: '¿Marcar todas las notificaciones como leídas?',
    read_all_notifications_dialog_content:
      'Podrá volver a marcar individualmente cada notificación como no leída posteriormente, si así lo desea.',
    read_all_notifications_disabled_tooltip_message:
      'Ya has marcado todas las notificaciones como leídas.',
    read_all_notifications_success_message: 'Todas las notificaciones se marcaron como leídas.',
    read_all_notifications_error_message:
      'No se pudieron marcar todas las notificaciones como leídas.',
  },
  push_notifications: {
    warnings: {
      unsubscribed_generic_warning_title: 'No puedo recibir notificaciones en tiempo real',
      unsubscribed_not_supported_warning_message:
        'El navegador que está utilizando no admite la recepción de notificaciones. Utilice Chrome, Firefox o Edge para recibir notificaciones en tiempo real.',
      unsubscribed_error_warning_message:
        'Ha ocurrido un error por lo que no puede recibir notificaciones en tiempo real.',
      unsubscribed_permissions_denied_warning_message:
        'Se han denegado los permisos para recibir notificaciones. Por favor, habilite las notificaciones para el navegador que está utilizando.',
    },
  },
  invite_modal: {
    caregiver_invite_title: 'Aceptar la invitación para ser cuidador',
    affiliation_type_description: 'Esta invitación le permite ser {{type}} de la entidad {{name}}.',
    relationship_type_input: '¿Qué tipo de relación que le vincula a {{name}}?',
    invite_recipient_not_current_user_description:
      'Está usando una cuenta con información diferente a la información disponible en esta invitación. Por favor, revise con atención si desea continuar.',
    accept_button: 'Sí, acepto',
    decline_button: 'No, rechazo',
    relationship_already_exists_description:
      'Ya es cuidador de {{name}} por haber aceptado una invitación con anterioridad. No necesita hacer nada más.',
    affiliation_already_exists_description:
      'Ya tiene afiliación con la entidad {{name}} por haber aceptado una invitación con anterioridad. No necesita hacer nada más.',
    loading_invite_description_1: 'Cargando detalles de la invitación...',
    loading_invite_description_2: 'Por favor, espere...',
    invite_done_unknown_description: 'La invitación ya no se puede usar.',
    invite_done_refused_description: 'La invitación ya ha sido rechazada. Ya no se puede usar.',
    invite_done_accepted_description: 'La invitación ya se ha aceptada. Ya no se puede usar.',
    invite_done_cancelled_description: 'La invitación ha sido cancelada. Ya no se puede usar.',
    invite_done_expired_description: 'La invitación ha caducado. Ya no se puede usar.',
    errors: {
      refuse_invite_error_message:
        'Se ha producido un error. No ha sido posible rechazar la invitación.',
      accept_invite_error_message:
        'Se ha producido un error. No ha sido posible aceptar la invitación.',
      fetch_invite_error_message:
        'Se ha producido un error. No ha sido posible abrir la invitación.',
      fetch_invite_invalid_error_message: 'La invitación que ha intentado abrir está invalidada.',
    },
    success: {
      refuse_relationship_invite_success_message:
        'La invitación ha sido rechazada. {{name}} no forma parte de su lista de contactos.',
      accept_relationship_invite_success_message:
        'La invitación ha sido aceptada. {{name}} forma ahora parte de su lista de contactos.',
      refuse_affiliation_invite_success_message:
        'La invitación ha sido rechazada. {{name}} no forma parte de su lista de afiliaciones.',
      accept_affiliation_invite_success_message:
        'La invitación ha sido aceptada. {{name}} forma ahora parte de su lista de afiliaciones.',
    },
  },
  profile: {
    title: 'Perfil',
    exit_button: 'Salir',
    personal_information_section_title: 'Editar perfil',
    change_theme_section_title: 'Mi tema',
    legal_information_section_title: 'Información legal',
    relationships_section_title: 'Mis relaciones',
    affiliations_section_title: 'Mis afiliaciones',
    logout_normal_button: 'Terminar sesión',
    logout_loading_button: 'Terminando sesión...',
    logout_error_button: 'Error al terminar sesión',
    theme_input: 'Tema',
    dark_option: 'Oscuro',
    light_option: 'Claro',
    system_option: 'Tema del sistema (automático)',
  },
  create_caregiver_account: {
    register_title: 'Registro',
    essential_information_step_description:
      '¡Bienvenido a la plataforma {{brand}}! Vamos a crear una cuenta de usuario para que pueda convertirse en cuidador y terminar así el proceso de aceptación de la invitación que ha recibido.',
    essential_information_step: 'Información esencial',
    personal_information_step: 'Información personal',
    optional_information_step: 'Información opcional',
    note_title: 'Nota',
    note_statement:
      'Los datos indicados con "*" son obligatorios para la creación de la cuenta de acceso para el cuidador.',
    continue_button: 'Continuar',
    create_account_button: 'Aceptar invitación y crear cuenta',
    start_using_button: 'Empezar a usar',
    go_back_to_login_button: 'Ir a la pantalla de acceso',
    go_back_button: 'Voltar para trás',
    success_title: '¡Listo!',
    success_description:
      '¡Enhorabuena! ¡Ahora ya es usuario de la plataforma {{brand}}! Gracias por unirse a esta comunidad. Ahora puede avanzar a su cuenta de usuario.',
    error_title: '¡No ha sido posible crear la cuenta de usuario!',
    error_description:
      'Lamentablemente ha habido algún fallo al crear su cuenta de usuario. Por favor, entre en contacto con el servicio de Atención al Cliente para obtener ayuda.',
    relationship_type_input: 'Tipo de relación con {{name}}',
    profession_input: 'Ámbito profesional',
    profession_description_input: 'Profesión/Ocupación',
    email_address_input: 'Dirección de e-mail',
    password_input: 'Contraseña',
    repeat_password_input: 'Repetir la contraseña',
    legal_documents_title: 'Documentos legales',
    legal_documents_statement:
      'Confirmo que he leído y acepto las <0>Condiciones Generales de Uso</0> y la <1>Política de Privacidad y Protección de Datos</1>.',
    full_name_input: 'Nombre completo',
    display_name_input: 'Cómo te gusta que te llamen',
    mobile_number_input: 'Número de móvil',
    profile_picture_input: 'Foto de perfil',
    gender_input: 'Género',
    gender_placeholder_input: 'Seleccione un género',
    language_input: 'Idioma',
    language_placeholder_input: 'Seleccione un idioma',
    birthday_input: 'Fecha de nacimiento',
    birthday_input_placeholder: 'Seleccione una fecha',
    tax_identification_number_input: 'Número de identificación fiscal',
    profile_picture_info_message: 'Para obtener la mejor resolución, la imagen debe ser JPG/JPEG.',
    display_name_info_message:
      'En este campo, indique el nombre con el que le llaman diariamente. Este es el nombre que verán otros usuarios en el sistema cuando interactúen con usted.',
    errors: {
      form_validation_invalid_field:
        'Complete este campo para completar la creación de la cuenta del cuidador',
      form_validation_invalid_email: 'El e-mail proporcionado no es válido',
      form_validation_invalid_password_minimum_characters:
        'La contraseña debe tener al menos 6 caracteres',
      form_validation_invalid_password_blank_spaces:
        'La contraseña no puede empezar ni terminar con espacios en blanco',
      form_validation_invalid_repeat_password_mismatch:
        'Por favor, comprueba que las contraseñas son iguales',
      form_validation_invalid_full_name_maximum_characters:
        'El nombre completo debe tener menos de {{max}} caracteres',
      form_validation_invalid_display_name_maximum_characters:
        'El nombre debe tener menos de {{max}} caracteres',
      form_validation_invalid_mobile_phone_number: 'El número de móvil proporcionado no es válido',
      form_validation_not_mobile_phone_number:
        'El número proporcionado no coincide con un número de móvil',
      form_validation_invalid_tax_identification_number:
        'El número de identificación fiscal proporcionado no es válido',
      form_validation_invalid_legal_documents:
        'Acepte los documentos legales para completar la creación de la cuenta del cuidador',
      enter_account_error_message:
        'Se ha producido un error. No ha sido posible entrar en su cuenta.',
      invalid_invite_error_message:
        'La invitación que ha intentado usar para la creación de una cuenta está invalidada.',
    },
  },
  office: {
    generics: {
      cancel: 'Cancelar',
      save: 'Guardar',
      exit: 'Salir',
      back: 'Volver',
      apply: 'Aplicar',
      save_pdf: 'Guardar en PDF (.pdf)',
      save_xlsx: 'Guardar en Excel (.xlsx)',
    },
    localized_string_input: {
      fields: {
        placeholder: 'Escribir una traducción',
        rules: {
          required: 'Campo obligatorio',
          max_length: 'No puede tener más de {{ char }} caracteres.',
        },
      },
    },
    users_filters: {
      title: 'Filtros',
      clear_all: 'Borrar todos',
      sections: {
        socialResponses: {title: 'Servicios sociales'},
        shifts: {title: 'Turnos'},
        activeStatus: {
          title: 'Estados',
          options: {
            active: 'Activo',
            inactive: 'Inactivo',
          },
        },
        taskStatus: {
          title: 'Estados',
          options: {
            planned: 'Planeado',
            handled: 'Tratado',
            done: 'Realizado',
            not_done: 'No realizado',
            not_handled: 'No tratado',
          },
        },
        taskGroups: {
          title: 'Tareas',
        },
        patientOccurrences: {
          title: 'Ocurrencias usuarios',
        },
        generalOccurrences: {
          title: 'Ocurrencias generales',
        },
      },
    },
    profile_type_tabs: {
      informal_caregiver: 'Informal',
      professional_caregiver: 'Profesional',
      system_administrator: 'Admin. de Sistema',
      partner: 'Compañero',
    },
    informal_caregiver: {
      tabs: {
        activity: 'Actividad',
        profile: 'Perfil',
        media: 'Multimedia',
        reminders: 'Recordatorios',
      },
      add_menu: {
        button: 'Añadir',
        title: '¿Qué desea añadir?',
        create_reminder: 'Recordatorio',
        send_photo: 'Fotografía',
        send_video: 'Vídeo',
        send_invite: 'Invitación para el cuidador',
      },
      relationship_picker: {
        caption: 'Estás viendo el perfil de:',
        dialog_title: 'Seleccionar otro contacto',
      },
      caregiver_app_info_message:
        'Seguimos trabajando para mejorar esta zona. Para aprovechar al máximo las funciones de {{brand}} como familiar, amigo o cuidador informal, utilice nuestra aplicación para dispositivos móviles <0>Android</0> o <1>iOS</1>.',
    },
    professional_caregiver: {
      tabs: {
        statistics: 'Estadísticas',
        digital_inclusion: 'Inclusión digital',
        seniors: 'Mayores',
        applications: 'Aplicaciones',
        services: 'Servicios',
        professional_caregivers: 'Equipo',
        activity: 'Actividad',
        devices: 'Dispositivos',
        user_cards: 'Tarjetas',
        licenses: 'Licencias',
        entity_photos: 'Fotografías',
        playlists: 'Listas de reproducción',
        start: 'Comenzar',
        planner: 'Calendario',
        occurrences: 'Ocurrencias',
        settings: 'Ajustes',
        sioslifeAcademy: 'Academia',
        myToDos: 'Mis tareas',
      },
      profile: {
        generic_header: {
          title: 'Perfil',
          subtitle: {
            write: 'Puedes configurar el perfil y otra información',
            read: 'Puedes consultar toda la información del perfil',
          },
          breadcrumbs: {
            team: 'Equipo',
            professionals: 'Profesionales',
            profile: 'Perfil',
          },
        },
      },
      affiliation_picker: {
        caption: 'Estás a trabajar la entidad:',
        dialog_title: 'Seleccionar otra entidad',
      },
      settings: {
        generic_header: {
          breadcrumbs: {
            settings: 'Configuraciones',
            institution: 'Institución',
            licenses: 'Licencias',
            templates: 'Plantillas',
            occurrences: 'Ocurrencias',
            quick_access_devices: 'Acceso Rápido',
          },
          subtitle: {
            institution: {
              write: 'Puede configurar la información de la institución',
              read: 'Puede consultar toda la información sobre la institución',
            },
            activities_sector: 'Puede configurar las actividades del sector',
            licenses: 'Puede consultar las licencias',
            templates: {
              write: 'Puede configurar todas las plantillas',
              read: 'Puede consultar todas las plantillas',
            },
            occurrences: 'Configurar y gestionar tipos de ocurrencias',
            quick_access_devices: 'Configurar y gestionar dispositivos de acceso rápido',
          },
        },
        templates: {
          empty: 'No hay plantillas',
          name: 'Nombre',
          area: 'Área',
          description: 'Descripción',
          update: 'Ultimo cambio',
          edit: 'Editar',
          delete: 'Borrar',
          revert: 'Revertir',
          duplicate: 'Duplicar',
          cancel: 'Cancelar',
          delete_title: 'Está seguro de que desea borra la plantilla "{{name}}"?',
          delete_subtitle: 'Se mantendrá el plan de asistencia para los usuarios',
          snackbar: {
            delete_success_message: 'La plantilla "{{name}}" ha sido borrada con éxito.',
            delete_error_message: 'No se ha podido borrar la plantilla "{{name}}".',
            duplicate_success_message: 'La plantilla "{{name}}" ha sido duplicada.',
            duplicate_error_message: 'No se ha podido duplicar la plantilla "{{name}}".',
            edit_name_success_message: 'La plantilla "{{name}}" ha sido editada con éxito.',
            edit_name_error_message: 'No se ha podido editar la plantilla "{{name}}".',
          },
          edit_name: 'Editar nombre',
          alert: 'Los cambios realizados en la plantilla no afectarán a los usuarios.',
          no_permissions: 'No tienes permisos para editar plantillas.',
        },
        occurrences: {
          patients: 'Usuarios',
          general: 'General',
          general_plural: 'Generales',
          occurrence: 'Ocurrencia',
          active: 'Activo',
          action_required: 'Requiere acción',
          notify: 'Perfiles para notificar',
          all_profiles: 'Todos los perfiles',
          add: 'Añadir',
          add_modal_title: 'Añadir tipo de ocurrencia',
          add_modal_name: 'Nombre do tipo de ocurrencia',
          add_name_error: 'Nombre já existe',
          delete_modal_title:
            '¿Estás seguro de que quieres borrar el tipo de ocurrencia "{{name}}"?',
          revert_modal_title:
            '¿Estás seguro de que quieres revertir el tipo de ocurrencia "{{name}}"?',
          delete_modal_subtitle: 'El tipo de ocurrencia ya no estará disponible para registro.',
          revert_modal_subtitle: 'El tipo de ocurrencia volverá a sus valores predeterminados.',
          name_error: 'El nombre de la ocurrencia debe tener menos de {{max}} caracteres',
          add_error_toast: 'No se ha podido añadir el tipo de ocurrencia "{{name}}.',
          add_success_toast: 'El tipo de ocurrencia "{{name}}" ha sido añadido con éxito!',
          edit_error_toast: 'No se ha podido editar el tipo de ocurrencia "{{name}}.',
          edit_success_toast: 'El tipo de ocurrencia "{{name}}" ha sido editado con éxito!',
          delete_error_toast: 'No se ha podido borrar el tipo de ocurrencia "{{name}}".',
          delete_success_toast: 'El tipo de ocurrencia "{{name}}" ha sido borrado con éxito!',
          revert_success_toast: 'El tipo de ocurrencia "{{name}}" ha sido revertido con éxito!',
          delete_default:
            'Configuración predeterminada - No es posible borrar este tipo de ocurrencia.',
          edit_name_default: 'Configuración predeterminada - No es posible editar el nombre.',
          default: 'Configuración predeterminada',
          empty_general: 'No hay tipos de ocurrencias generales',
          empty_patients: 'No hay tipos de ocurrencias para los usuarios',
          no_permissions: 'No tienes permisos para editar tipos de ocurrencias.',
        },
        quick_access: {
          empty: 'No hay dispositivos de acceso rápido',
          name: 'Nombre',
          type: 'Tipo',
          access: 'Ultimo acceso',
          professional: 'Profesional',
          deactivate: 'Desactivar',
          activate_device: 'Activar en este dispositivo',
          activate_disabled: 'El accesso rápido ya está activado en este dispositivo',
          activate_disabled_ist:
            'El accesso rápido ya está activado en este dispositivo en la institución "{{name}}"',
          no_permissions: 'No tienes permisos para editar dispositivos de acceso rápido.',
          activate_modal: {
            title: 'Activar el accesso rápido en este dispositivo',
            device_name: 'Nombre del dispositivo',
            device_type: 'Tipo de dispositivo',
            success_message: 'El accesso rápido ha sido activado en este dispositivo!',
            error_message: 'No se ha podido activar el accesso rápido en este dispositivo!',
            name_repeat_error: 'El nombre del dispositivo ya existe',
          },
          edit_modal: {
            title: 'Editar dispositivo',
            success_message: 'El dispositivo de acceso rápido ha sido editado con éxito!',
            error_message: 'No se ha podido editar el dispositivo de acceso rápido!',
          },
          deactivate_modal: {
            title:
              'Tienes a certeza de que quieres desactivar el dispositivo de acceso rápido "{{name}}"?',
            subtitle:
              'El dispositivo podrá seguir utilizándose normalmente con correo electronico y contraseña.',
            success_message:
              'El dispositivo de acceso rápido "{{name}}" ha sido desactivado con éxito!',
            error_message: 'No se ha podido desactivar el dispositivo de acceso rápido "{{name}}".',
            button: 'Desactivar',
            no_name: 'Sin nombre',
          },
        },
      },
      seniors: {
        edit: {
          profile: {
            generic_header: {
              subtitle: {
                write: 'Puedes configurar tu perfil y otra información importante',
              },
            },
          },
        },
        table_view_screen: {
          name: 'Nombre',
          social_response: 'Servicios sociales',
          age: 'Edad',
          room: 'Cuarto',
          status: 'Estado',
          role: 'Cargo',
          phone: 'Teléfono',
          email: 'E-mail',
        },
      },
      shifts_allocations: {
        generic_header: {
          breadcrumbs: {
            team: 'Equipo',
            shifts_allocations: 'Asignación de turnos',
          },
        },
        exit_edit_mode: 'Salir del modo edición',
        table: {
          collaborators: 'Colaboradores',
          today: 'Hoy',
          select_shift: 'Seleccionar turno',
        },
      },
      roles_and_permissions: {
        generic_header: {
          breadcrumbs: {
            team: 'Equipo',
            roles_and_permissions: 'Cargo y Permisos',
          },
          subtitle: 'Crear cargo y gestionar permisos',
        },
        create: 'Crear',
        edit: 'Editar',
        delete: 'Borrar',
        default_role: 'Configuración Predeterminada',
        confirm_delete: {
          title: 'Eliminar cargo - {{ name }}',
          copy: {
            title: 'Está seguro de que desea eliminar el cargo?',
            subtitle: 'Esta acción no puede deshacerse.',
          },
          snackbar: {
            error_message: 'Se ha producido un error al borrar el cargo.',
          },
        },
      },
      occurrences: {
        empty_title: 'Aún no hay ocurrencias registradas',
        empty_subtitle: ' Pulse añadir para registrar una ocurrencia.',
        table_header_date: 'Fecha',
        table_header_time: 'Hora',
        table_header_type: 'Tipo',
        table_header_patient: 'Usuario',
        table_header_responsible: 'Responsable',
        table_header_registered: 'Registrado por',
        table_header_status: 'Estado',
        table_header_knowledge: 'Conocimiento',
        add_occurrence: 'Añadir ocurrencia',
        type_placeholder: 'Seleccione un tipo de ocurrencia',
        local_placeholder: 'Escribe la localización de la ocurrencia',
        patient_placeholder: 'Seleccione un usuario',
        responsible_placeholder: 'Seleccione un responsable',
        details_placeholder: 'Escribe los detalles de la ocurrencia',
        status: {
          registered: 'Registrado',
          not_handled: 'Ser tratado',
          handled: 'Tratado',
        },
        details: 'Detalles',
        information: 'Información de la ocurrencia',
        date_hour: 'Fecha y hora',
        acknowledge: 'Tomar conocimiento',
        add_notes: 'Añadir notas',
        write_notes: 'Escribe una nota',
        acknowledge_success: 'Se ha tomado conocimiento de la ocurrencia.',
        acknowledge_error: 'No se ha podido tomar conocimiento de la ocurrencia.',
        handle_success: 'La ocurrencia ha sido tratada con éxito.',
        handle_error: 'No se ha podido tratar la ocurrencia.',
        responsible: 'Responsable',
        registered_by: 'Registrado por',
        took_acknowledge: 'Tomó conocimiento',
        history: {
          acknowledge: 'Tomó conocimiento de la ocurrencia',
          state_update: 'Actualizó el estado de la ocurrencia',
        },
        notes_success: 'Se han añadido las notas a la ocurrencia.',
        notes_error: 'No se han podido añadir las notas a la ocurrencia.',
      },
    },
    system_administrator: {
      tabs: {
        statistics: 'Estadísticas',
        activity: 'Actividad',
        devices: 'Dispositivos',
        apiDocs: 'Doc. de API',
        applications: 'Aplicaciones',
        entities: 'Entidades',
        cards: 'Tarjetas',
        licenses: 'Licencias',
        seniors: 'Mayores',
        caregivers: 'Cuidadores',
        playlists: 'Listas de reproducción',
        gdprErasureRequests: 'Derecho al olvido (RGPD)',
      },
    },
    partner: {
      tabs: {
        home: 'Comienzo',
        apiDocs: 'Doc. de API',
      },
    },
    legacy_integration: {
      entity_services_disabled_alert_title: 'Incapaz de realizar la acción.',
      entity_services_disabled_alert_message:
        'Lo sentimos, no tienes acceso a esta área. Por favor, entre en contacto con el servicio de Atención al Cliente para obtener más información.',
    },
    none: {
      tabs: {
        home: 'Comienzo',
      },
    },
    permissions: {
      access_denied_message: 'Lo sentimos, no tiene permisos para acceder a esta página.',
    },
    access: {
      denied_message_first: 'Para acceder a esta área es necesario disponer del módulo de ',
      denied_message_second: ' de Sioslife.',
      care_management: 'Gestión de Cuidados',
      digital_inclusion: 'Inclusión Digital',
      speak: 'Háblanos!',
      tooltip:
        'Para acceder a esta opción es necesario disponer del módulo de <strong>{{ module }}</strong> de Sioslife.',
    },
    pending_tasks: {
      alert:
        '¡Atención!<br>Identificamos que existen {{number}} tareas ya realizadas, pendientes de validación.',
      see_tasks: 'Ver tareas',
      header_title: 'Tareas pendientes',
      info: 'Las tareas presentadas se consideran realizadas de forma automática, siempre que los usuarios utilicen el sistema interactivo Sioslife.<br>La inscripción sólo se considerará efectiva después de que se hayan validado las tareas respectivas.',
      empty: 'No hay tareas pendientes',
      all: 'Seleccionar todas',
      validate: 'Validar tareas',
      modal_title: '¿Estás seguro de que quieres validar {{number}} tareas?',
      modal_observation: 'Escribir un comentario',
      modal_validate: 'Validar',
      snackbar_success: 'Todas las tareas han sido validadas con éxito.',
      snackbar_error: 'No se han podido validar las tareas.',
      modal_task_validation_title:
        '¡Atención!<br>Identificamos que esta tarea ya ha sido realizada está pendiente de validación.',
    },
  },
  seniors: {
    root: {
      title: 'Mayores',
      add_button: 'Añadir',
      search_input_placeholder: 'Buscar mayores',
      name_header: 'Nombre',
      gender_header: 'Género',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      no_seniors: 'Aún no hay personas mayores inscritas.',
      deleted_senior_dialog_title: 'Perfil del mayor "{{name}}" eliminado',
      deleted_senior_dialog_message: 'El perfil del mayor ya no existe en la lista de mayores.',
      deleted_senior_dialog_ok_button: 'Ok',
      senior: 'Mayor',
      seniors: 'Mayores',
      modals: {
        add_senior_flow: {
          form: {
            title: 'Crear mayor',
            cancel: 'Cancelar',
            add: 'Añadir',
            inputs: {
              active: 'Activo',
              name: {
                label: 'Nombre completo',
                placeholder: 'Nombre y apellidos',
                required_message: 'Escribe un nombre y un apellido',
                pattern_message: 'El nombre introducido no es válido',
                max_message:
                  'El nombre que introduzca no puede superar los {{ characters }} caracteres',
              },
              name_alias: {
                label: 'Cómo te gusta que te llamen',
                placeholder: 'Nombre',
                required_message: 'Introduzca un nombre',
                pattern_message: 'El nombre introducido no es válido',
                max_message:
                  'El nombre que introduzca no puede superar los {{ characters }} caracteres',
              },
              birthdate: {
                label: 'Fecha de nacimiento',
                placeholder: 'Seleccione una fecha de nacimiento',
                required_message: 'Seleccione una fecha de nacimiento',
              },
            },
          },
          confirm: {
            title: 'Crear mayor',
            confirm_title: '¿Seguro que quieres añadir "{{ name }}"?',
            confirm_subtitle: 'El nuevo senior será añadido al sistema.',
            cancel: 'Cancelar',
            add: 'Añadir',
          },
          snackbar: {
            success_message: '¡El senior "{{nombre}}" ha sido creado con éxito!',
            error_message: 'Error al guardar',
          },
        },
      },
    },
    edit: {
      senior_dcm: {
        careplan: {
          load_error: 'No se han podido cargar los datos del plan de asistencia.',
          caregiver: {
            title: 'Responsável',
            unnasigned: 'No definido',
            unnasigned_tooltip: 'No tiene permisos para editar el Responsable',
          },
          activities: {
            add_button: 'Añadir',
            load_error: 'No se han podido cargar grupos de actividades para añadir.',
            empty_state: {
              title: 'Aún no hay actividades registradas.',
              subtitle: 'Pulse añadir para registrar una actividad.',
            },
          },
          on_save: {
            snackbar: {
              success_message: 'Plan de asistencia guardado',
              error_message: 'No se ha podido guardar el plan de cuidados.',
            },
          },
          on_update: {
            snackbar: {
              success_message: 'Plan de cuidados actualizado',
              error_message: 'No se pudo actualizar el plan de cuidados.',
            },
          },
          on_delete: {
            title: '¿Estás seguro de que quieres borrar el plan de cuidados del usuario {{name}}?',
            description: 'Esta acción eliminará el plan de cuidados de forma permanente.',
            snackbar: {
              success_message: 'Plan de cuidados eliminado',
              error_message: 'No se ha podido eliminar el plan de cuidados.',
            },
          },
        },
        individual_plan: {
          add_cares: 'Añadir cuidado',
          caregiver: {
            title: 'Responsable',
            coordinator: 'Coordinador',
            unassigned: 'No definido',
            unassigned_tooltip: 'No tienes permisos para editar el Responsable',
          },
          observations: 'Observaciones',
          created_at: 'Creado el',
          valid_until: 'Válido hasta',
          not_defined: 'No definido',
          empty_state: {
            title: 'Aún no hay diagnósticos registrados en el plan individual.',
            subtitle: 'Pulse para añadir un diagnóstico.',
          },
          new_diagnostic: 'Nuevo diagnóstico',
          diagnostic: 'Diagnóstico',
          goal: 'Objetivo general',
          new_goal: 'Nuevo objetivo general',
          new_objective: 'Nuevo objetivo específico',
          history: {
            action: 'Historial',
            title: 'Historial del plan individual',
            close: 'Cerrar',
            version: 'Versión {{revision}}',
            unknown_author: 'Usuario desconocido',
            load_error: 'No se pudo cargar el historial.',
            source: {
              manual: 'Manual',
              ai: 'IA',
              restore: 'Restauración',
            },
            summary: {
              entity: {
                plan: 'Plan',
                diagnostic: 'Diagnóstico',
                goal: 'Objetivo general',
                objective: 'Objetivo específico',
              },
              entity_plural: {
                plan: 'planes',
                diagnostic: 'diagnósticos',
                goal: 'objetivos generales',
                objective: 'objetivos específicos',
              },
              field: {
                definition: 'definición',
                responsibleUserId: 'responsable',
                cares: 'cuidados',
                activityTypesIds: 'actividades asociadas',
                indicators: 'indicadores',
                isActive: 'estado',
                coordinatorUserId: 'coordinador',
                validUntil: 'validez',
                observations: 'observaciones',
                diagnosticsOrder: 'orden de los diagnósticos',
              },
              created: '{{entity}} creado: "{{name}}"',
              created_unnamed: '{{entity}} creado',
              created_many: '{{count}} {{entities}} creados',
              deleted: '{{entity}} eliminado: "{{name}}"',
              deleted_unnamed: '{{entity}} eliminado',
              deleted_many: '{{count}} {{entities}} eliminados',
              updated: 'Cambio en {{entity}}: {{field}}',
              updated_many: 'Cambio en {{count}} {{entities}}: {{field}}',
              more: 'y {{count}} cambios más',
              restored_from: 'Restaurado desde la versión {{revision}}',
            },
            restore: {
              button: 'Restaurar esta versión',
              prompt_title: '¿Restaurar la versión {{revision}}?',
              prompt_description:
                'El plan individual pasará a tener el contenido de esta versión. Se crea una nueva versión y no se elimina ninguna versión anterior, por lo que puede volver atrás en cualquier momento.',
              confirm: 'Restaurar',
              success: 'Versión {{revision}} restaurada.',
              conflict: 'El plan se modificó entretanto. Se ha actualizado, inténtelo de nuevo.',
              error: 'No se pudo restaurar la versión.',
            },
            view_version: 'Ver versión',
            back: 'Volver',
            detail_load_error: 'No se pudo cargar esta versión.',
            no_diagnostics: 'Esta versión no tiene diagnósticos.',
            detail_note: 'Estado del plan en esta versión (solo lectura).',
            change_type_unknown: 'Cambio',
            change_type: {
              baseline: 'Estado inicial (antes del historial)',
              'plan-created': 'Plan creado',
              'plan-updated': 'Plan actualizado',
              'plan-archived': 'Plan archivado',
              'plan-reactivated': 'Plan reactivado',
              'diagnostic-created': 'Diagnóstico creado',
              'diagnostic-updated': 'Diagnóstico actualizado',
              'diagnostic-deleted': 'Diagnóstico eliminado',
              'goal-created': 'Objetivo general creado',
              'goal-updated': 'Objetivo general actualizado',
              'goal-deleted': 'Objetivo general eliminado',
              'objective-created': 'Objetivo específico creado',
              'objective-updated': 'Objetivo específico actualizado',
              'objective-deleted': 'Objetivo específico eliminado',
              'bulk-append': 'Diagnósticos añadidos en bloque',
              'bulk-replace': 'Diagnósticos sustituidos en bloque',
              restored: 'Versión restaurada',
            },
          },
          ai_generate: {
            title: 'Generar plan con IA',
            description:
              'Indique el tema clínico que desea priorizar o deje que la IA decida con el plan automático. El texto solo indica el tema, no son instrucciones para la IA.',
            focus_label: 'Enfoque del plan',
            focus_placeholder:
              'Ej.: plan centrado en las actividades cognitivas para reducir el deterioro mental',
            automatic: 'Plan automático',
            with_focus: 'Generar con enfoque',
          },
          ai_proposal: {
            title: 'Propuesta de la IA',
            subtitle:
              'Esto es una vista previa. No se guarda nada en el plan individual hasta que apruebe.',
            discard: 'Descartar',
            approve: 'Aprobar',
            approved: 'Propuesta de la IA añadida al plan individual.',
            replaced:
              'Plan individual sustituido por la propuesta de la IA. La versión anterior quedó guardada en el historial.',
            apply_prompt: {
              title: '¿Desea borrar los datos del plan individual existente?',
              description:
                'El plan individual ya tiene diagnósticos. Puede borrarlos y quedarse solo con la propuesta de la IA, o añadir la propuesta a los diagnósticos existentes. Si los borra, la versión anterior queda guardada en el historial del plan.',
              replace: 'Borrar plan',
              append: 'Añadir al plan',
            },
            rate_limited:
              'Ha alcanzado el límite de generaciones con IA por hora. Inténtelo de nuevo dentro de {{minutes}} min.',
            save_error: 'No se pudo guardar la propuesta en el plan individual.',
            invalid_references:
              'No se pudo guardar: la propuesta supera los límites permitidos o tiene referencias no válidas.',
            error: 'No se pudo generar el plan con IA. Inténtelo de nuevo.',
          },
          on_delete: {
            title:
              '¿Está seguro de que desea archivar el plan individual del usuario "{{nombre}}"?',
            description: 'Esta acción archivará el plan individual. Podrá consultarlo más tarde',
            snackbar: {
              success_message: 'Plan individual archivado',
              error_message: 'Plan individual no pudo ser archivado',
            },
          },
          add_diagnostic: {
            title: 'Añadir diagnóstico',
            edit: 'Editar diagnóstico',
            form: {
              definition: {
                label: 'Definición',
                placeholder: 'Breve descripción del diagnóstico',
                error_message: 'El definición debe tener entre 1 y {{max}} caracteres',
              },
            },
          },
          on_diagnostic_remove: {
            header: 'Eliminar diagnóstico',
            title: '¿Está seguro de que desea eliminar el diagnóstico "{{nombre}}"?',
            description: 'Esta acción eliminará el diagnóstico de forma permanente',
          },
          add_diagnostic_goal: {
            title: 'Añadir objetivo general',
            edit: 'Editar objetivo general',
            form: {
              definition: {
                label: 'Definición',
                placeholder: 'Breve descripción del objetivo general',
                error_message: 'La definición debe tener entre 1 y {{max}} caracteres',
              },
              responsibleUser: {
                label: 'Responsable',
                placeholder: 'Seleccione un responsable',
              },
            },
          },
          on_diagnostic_goal_remove: {
            header: 'Eliminar objetivo general',
            title: '¿Está seguro de que desea eliminar el objetivo general "{{nombre}}"?',
            description: 'Esta acción eliminará el objetivo general de forma permanente',
          },
          add_diagnostic_goal_objective: {
            title: 'Añadir objetivo específico',
            edit: 'Editar objetivo específico',
            form: {
              definition: {
                label: 'Definición',
                placeholder: 'Breve descripción del objetivo específico',
                error_message: 'La definición debe tener entre 1 y {{max}} caracteres',
              },
            },
            cares: {
              add_non_automatic: 'Añadir cuidado no automático',
              non_automatic_title: 'Cuidados no automáticos',
              max_reached: 'Puede añadir como máximo {{max}} cuidados no automáticos.',
              too_long: 'El cuidado no automático no puede tener más de {{max}} caracteres.',
            },
            snackbar: {
              error: 'No se pudo guardar el objetivo específico.',
              invalid_references:
                'No se pudo guardar: uno de los cuidados o indicadores seleccionados no está disponible para esta entidad.',
            },
            table: {
              definition: 'Objetivo específico',
              cares: 'Cuidado',
              indicators: 'Indicadores',
              targets: 'Objetivos',
              execution: 'Ejecución',
            },
          },
          on_diagnostic_goal_objective_remove: {
            header: 'Eliminar objetivo específico',
            title: '¿Está seguro de que desea eliminar el objetivo específico "{{nombre}}"?',
            description: 'Esta acción eliminará el objetivo específico de forma permanente',
          },
        },
      },
    },
  },
  senior_tabs: {
    tabs: {
      profile: 'Perfil',
      caregivers: 'Cuidadores',
      applications: 'Aplicaciones',
      devices: 'Dispositivos',
      activity: 'Actividade',
      statistics: 'Estadísticas',
      user_cards: 'Tarjetas',
      health: 'Salud',
      affiliations: 'Afiliaciones',
      playlists: 'Listas de reproducción',
      external_accounts: 'Cuentas Externas',
    },
    errors: {
      senior_does_not_exist_error: 'No se pudo obtener el mayor al que intentó acceder.',
    },
  },
  health_profile_form: {
    clinical_conditions_title: 'Patologías/Limitaciones',
    visual_difficulty_label: 'Dificultades visuales',
    hearing_difficulty_label: 'Dificultades auditivas',
    cognitive_deficit_label: 'Déficit cognitivo',
    parkinson_label: 'Parkinson',
    alzheimer_label: 'Alzheimer',
    other_dementias_label: 'Otras demencias',
    reduced_mobility_upper_limbs_label: 'Movilidad reducida de miembros superiores',
    reduced_mobility_lower_limbs_label: 'Movilidad reducida de miembros inferiores',
    paralysis_left_limb_label: 'Parálisis del miembro superior izquierdo',
    paralysis_right_limb_label: 'Parálisis del miembro superior derecho',
    wheelchair_label: 'Silla de ruedas',
    illiterate_label: 'Analfabetismo',
    disable_clinical_conditions_tooltip: 'No tiene permisos para editar las condiciones clínicas',
    errors: {
      update_health_profile_error_message:
        'Se ha producido un error. No ha sido posible actualizar el perfil de salud.',
    },
    noHealthMeasurements: 'No hay mediciones de salud.',
  },
  health_charts: {
    noData: 'No hay datos para mostrar.',
    heartRate: {
      subtitle1: 'normal hasta 120lpm',
    },
    bloodOxygen: {
      subtitle1: 'mínimo 50%',
      subtitle2: 'máximo 100%',
    },
    bloodPressure: {
      subtitle1: 'Sistólica: hasta 120mmHg',
      subtitle2: 'Diastólica: hasta 80mmHg',
    },
    bloodGlucose: {
      subtitle1: 'mínimo 80mg/dL',
      subtitle2: 'máximo 180mg/dL',
    },
    bodyTemperature: {
      subtitle1: 'normal hasta 36ºC',
    },
    bodyWeight: {
      subtitle1: undefined,
    },
    errors: {
      fetch: 'Se ha producido un error al cargar los datos de salud.',
    },
  },
  health_measurements: {
    title: 'Últimas Mediciones',
    units: {
      beatsPerMinute: 'lpm',
      breathsPerMinute: 'rpm',
    },
  },
  senior_caregivers_listing: {
    invite_caregiver_button: 'Invitar cuidador',
    invites_history_button: 'Ver invitaciones',
    no_caregivers: 'No hay cuidadores.',
    name_header: 'Nombre',
    relationship_type_header: 'Tipo',
    remove_relationship_header: 'Eliminar relación',
    remove_relationship_dialog_title:
      '¿Estás seguro de que quieres eliminar la relación entre el mayor {{senior}} y el cuidador {{caregiver}}?',
    remove_relationship_dialog_message:
      'Al eliminar esta relación, el mayor {{senior}} ya no podrá comunicarse con el cuidador {{caregiver}}.',
    remove_relationship_button: 'Eliminar',
    remove_relationship_button_tooltip: 'No tiene permisos para eliminar la relación',
    remove_relationship_error_message:
      'No ha sido posible eliminar la relación entre el mayor {{senior}} y el cuidador {{caregiver}}.',
    remove_relationship_success_message:
      'La relación entre el mayor {{senior}} y el cuidador {{caregiver}} ha sido eliminada.',
  },
  senior_relationships_invites_history: {
    title: 'Invitaciones',
    no_invites: 'No hay invitaciones.',
    invite_caregiver_button: 'Invitar cuidador',
    cancel_button: 'Cancelar',
    process_closed: 'Cerrado',
    name_header: 'Nombre',
    contact_header: 'Contacto',
    state_header: 'Estado de la invitación',
    status_header: 'Proceso',
    cancel_invite_dialog_title:
      '¿Estás seguro de que quieres cancelar la invitación para {{caregiver}} ser {{senior}} cuidador?',
    cancel_invite_dialog_message:
      'Al cancelar esta invitación, el cuidador {{caregiver}} no podrá aceptar la invitación para comunicarse con el mayor {{senior}}.',
    cancel_invite_dialog_action_yes_button: 'Cancelar invitación',
    cancel_invite_dialog_action_no_button: 'Mantener invitación',
    cancel_invite_error_message:
      'No ha sido posible cancelar la invitación para {{caregiver}} ser {{senior}} cuidador.',
    cancel_invite_success_message:
      'La invitación para {{caregiver}} ser {{senior}} cuidador ha sido cancelada.',
  },
  informal_caregiver_invite_form: {
    title: 'Invitar cuidador',
    name_input: 'Nombre',
    contact_method_input: 'Medio de contacto',
    email_input: 'Dirección de e-mail',
    mobile_phone_number_input: 'Número de móvil',
    save_button: 'Enviar invitación',
    saving_button: 'Enviando...',
    save_dialog_title: '¿Enviar invitación a {{name}}?',
    save_dialog_message:
      'Al enviar, {{name}} recibirá una invitación para comunicarse y tendrá acceso a cierta información de {{senior}}.',
    save_dialog_action_save_button: 'Enviar',
    router_prompt_title: '¿Estás seguro de que quieres salir sin enviar la invitación?',
    router_prompt_message: 'Al salir, perderá toda la información introducida en la invitación.',
    cancel_toast: 'La invitación ha sido cancelada.',
    success_message: 'La invitación ha sido enviada a {{name}}.',
    errors: {
      form_validation_invalid_field: 'Complete este campo para completar la invitación',
      form_validation_invalid_mobile_phone_number: 'El número de móvil proporcionado no es válido',
      form_validation_not_mobile_phone_number:
        'El número proporcionado no coincide con un número de móvil',
      form_validation_invalid_email: 'El dirección de e-mail proporcionado no es válido',
      error_message: 'No ha sido posible enviar la invitación a {{name}}.',
    },
  },
  devices_associations: {
    identifier_header: 'Identificador',
    type_header: 'Tipo',
    state_header: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    remove_device_header: 'Eliminar dispositivo',
    no_devices: 'No hay dispositivos.',
    associate_device_button: 'Asociar dispositivo',
    remove_device_dialog_title:
      '¿Estás seguro de que quieres eliminar la asociación entre el dispositivo con identificador "{{identifier}}" y {{name}}?',
    remove_device_dialog_message:
      'Al eliminar la asociación, {{name}} ya no podrá usar el dispositivo para disfrutar del sistema {{brand}}.',
    remove_device_button: 'Eliminar',
    remove_device_button_disabled_tooltip: 'No tiene permisos para eliminar el dispositivo',
    remove_device_error_message:
      'No ha sido posible eliminar la asociación con el dispositivo "{{identifier}}".',
    remove_device_success_message:
      'La asociación con el dispositivo "{{identifier}}" ha sido eliminada.',
  },
  user_cards_associations: {
    name_header: 'Nombre',
    state_header: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    remove_card_header: 'Eliminar tarjeta',
    associate_card_button: 'Asociar tarjeta',
    no_cards: 'No hay tarjetas.',
    remove_card_dialog_title:
      '¿Estás seguro de que quieres eliminar la asociación entre la tarjeta "{{card}}" y {{name}}?',
    remove_card_dialog_message:
      'Al eliminar la asociación, {{name}} ya no podrá usar la tarjeta para disfrutar del sistema {{brand}}.',
    remove_card_button: 'Eliminar',
    remove_card_button_disabled_tooltip: 'No tiene permisos para eliminar la tarjeta',
    remove_card_error_message:
      'No ha sido posible eliminar la asociación con la tarjeta "{{identifier}}".',
    remove_card_success_message: 'La asociación con la tarjeta "{{identifier}}" ha sido eliminada.',
  },
  associate_user_card_form: {
    title: 'Asociar tarjeta',
    card_name_input: 'Nombre de la tarjeta',
    entity_input: 'Entidad',
    card_name_info_message:
      'Este campo solo se puede rellenar si previamente se ha seleccionado una entidad.',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    save_dialog_title: '¿Asociar la tarjeta "{{card}}" con {{name}}?',
    save_dialog_message:
      'Al asociar la tarjeta, {{name}} podrá usarla para disfrutar del sistema {{brand}}.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Asociar',
    router_prompt_title: '¿Está seguro de que desea salir sin asociar la tarjeta?',
    router_prompt_message: 'Al salir, perderá toda la información introducida.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sí, salir',
    cancel_toast: 'La asociación ha sido cancelada.',
    no_affiliations_info_message:
      'El usuario no tiene afiliación con ninguna entidad. Por lo tanto, no es posible asociar tarjetas al usuario.',
    success: {
      save_success_button: '¡Guardado!',
      success_message: 'La tarjeta ha sido asociada con {{name}}.',
    },
    errors: {
      error_message: 'No ha sido posible asociar la tarjeta con {{name}}.',
      error_on_save_button: 'Error al guardar',
      form_validation_invalid_field:
        'Complete este campo para completar la asociación de la tarjeta',
    },
  },
  associate_device_form: {
    title: 'Asociar dispositivo',
    identifier_input: 'Identificador del dispositivo',
    entity_input: 'Entidad',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    save_dialog_title: '¿Asociar el dispositivo con identificador "{{device}}" con {{name}}?',
    save_dialog_message:
      'Al asociar el dispositivo, {{name}} podrá usarlo para disfrutar del sistema {{brand}}.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Asociar',
    router_prompt_title: '¿Estás seguro de que quieres salir sin asociar el dispositivo?',
    router_prompt_message: 'Al salir, perderá toda la información introducida.',
    cancel_toast: 'La asociación ha sido cancelada.',
    no_affiliations_info_message:
      'El usuario no tiene afiliación con ninguna entidad. Por lo tanto, no es posible asociar dispositivos al usuario.',
    success: {
      save_success_button: '¡Guardado!',
      success_message: 'El dispositivo ha sido asociado con {{name}}.',
    },
    errors: {
      error_message: 'No ha sido posible asociar el dispositivo con {{name}}.',
      error_on_save_button: 'Error al guardar',
      form_validation_invalid_field:
        'Complete este campo para completar la asociación de el dispositivo',
    },
  },
  external_accounts: {
    types: {
      call: 'Voximplant',
      partner_p3_n1: 'Hope Care',
      partner_p3_n2: 'Altice SmartAL',
      partner_p3_n3: 'DeepClick',
      partner_p3_n4: 'Itelcare (Kerox)',
    },
    list: {
      type_header: 'Tipo',
      delete_account_header: 'Eliminar',
      delete_account_button: 'Eliminar',
      associate_account_button: 'Añadir cuenta',
      no_accounts: 'No existen cuentas externas.',
      error_message: 'No se pudieron obtener las cuentas externas.',
      delete_account_dialog_title: '¿Eliminar la cuenta {{type}}?',
      delete_account_dialog_description: '¿Está seguro de que desea eliminar esta cuenta externa?',
      delete_account_dialog_cannot_be_undone: 'Esta acción no se puede deshacer.',
      delete_account_dialog_confirm_button: 'Eliminar',
      delete_account_dialog_cancel_button: 'Cancelar',
      delete_account_success_message: 'La cuenta {{type}} se eliminó correctamente.',
      delete_account_error_message: 'No se pudo eliminar la cuenta {{type}}.',
    },
    associate: {
      title: 'Añadir cuenta externa',
      type_input: 'Tipo de cuenta',
      save_button: 'Crear',
      saving_button: 'Creando...',
      save_dialog_title: '¿Crear la cuenta {{type}}?',
      save_dialog_message: '¿Está seguro de que desea crear esta cuenta externa?',
      save_dialog_action_cancel_button: 'Cancelar',
      save_dialog_action_save_button: 'Crear',
      no_available_types_message:
        'Ya existe una cuenta externa de todos los tipos disponibles para este usuario.',
      success_message: 'La cuenta {{type}} se creó correctamente.',
      errors: {
        generic_message: 'No se pudo crear la cuenta externa.',
        conflict_message: 'Ya existe una cuenta externa de este tipo para este usuario.',
        provider_error_message:
          'El proveedor externo falló al crear la cuenta. Inténtelo de nuevo más tarde.',
        error_on_save_button: 'Error',
      },
    },
    details: {
      delete_button: 'Eliminar cuenta',
      fields: {
        password: 'Contraseña',
        application_name: 'Nombre de la aplicación',
        username: 'Usuario',
        language: 'Idioma',
        project_identification: 'Identificación del proyecto',
        serial: 'Número de serie',
        profile: 'Perfil',
        timezone: 'Zona horaria',
        institution_id: 'ID de la institución',
        instance_name: 'Nombre de la instancia',
        manufacturer: 'Fabricante',
        model: 'Modelo',
        user_id: 'ID de usuario',
        patient_id: 'ID del paciente',
        email: 'E-mail',
      },
      errors: {
        not_found_message: 'No se pudo obtener la cuenta externa.',
      },
    },
  },
  caregivers: {
    root: {
      title: 'Cuidadores',
      search_input_placeholder: 'Buscar cuidadores',
      name_header: 'Nombre',
      email_header: 'E-mail',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      no_caregivers: 'No hay cuidadores.',
    },
  },
  professional_caregivers: {
    root: {
      title: 'Profesionales',
      invite_button: 'Invitar profesional',
      search_input_placeholder: 'Buscar profesionales',
      name_header: 'Nombre',
      email_header: 'E-mail',
      affiliation_type_header: 'Tipo',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      no_professional_caregivers: 'No hay profesionales.',
      deleted_professional_caregiver_dialog_title: 'Perfil del profesional {{name}} eliminado',
      deleted_professional_caregiver_dialog_message:
        'El perfil del profesional ya no existe en la lista de profesionales.',
      deleted_professional_caregiver_dialog_ok_button: 'Ok',
      colaborator: 'colaborador',
      colaborators: 'colaboradores',
      modals: {
        add_staff_flow: {
          form: {
            title: 'Añadir colaborador',
            cancel: 'Cancelar',
            send_invite: 'Enviar invitación',
            create_profile: 'Crear perfil',
            inputs: {
              name: {
                label: 'Nombre',
                placeholder: 'Nombre y apellidos',
                required_message: 'Escriba el nombre completo',
                pattern_message: 'El campo nombre no es válido',
                max_message:
                  'El nombre que introduzca no puede superar los {{ characters }} caracteres',
              },
              email: {
                label: 'Email',
                placeholder: 'Escriba un correo electrónico',
                required_message: 'El campo email es obligatorio',
                pattern_message: 'El campo Email no es válido',
              },
              roles: {
                label: 'Cargo',
                placeholder: 'Seleccionar cargo',
              },
              invitation_type: {
                label: '¿Cómo crear el perfil?',
                placeholder: 'Seleccione una opción',
                required_message: 'Debe seleccionar una opción de envío',
                via_email: 'Invitar por dirección de correo electrónico',
                via_sms: 'Invitar por SMS',
                via_quickAccessCode: 'Crear con código de acceso rápido',
              },
              phone_number: {
                label: 'Número de teléfono/móvil',
                placeholder: 'Introduzca un número de teléfono/móvil',
                required_message: 'El campo número de teléfono/móvil es obligatorio',
                is_valid_message: 'Número de teléfono/móvil no válido',
              },
              quick_access_code: {
                label: 'Código de acceso rápido',
                placeholder: 'Introduzca un código de acceso rápido de 4 dígitos',
                required_message: 'El campo de código de acceso rápido es obligatorio',
                is_valid_message: 'Código de acceso rápido no válido',
              },
            },
          },
          confirm: {
            title: 'Envío de invitación',
            title_quickAccessCode: 'Crear perfil',
            confirm_title: '¿Estás seguro de que quieres enviar una invitación a {{name}}?',
            confirm_title_quickAccessCode:
              '¿Estás seguro de que quieres crear un perfil para {{name}}?',
            confirm_subtitle:
              '{{nombre}} tendrá ahora acceso a cierta información sobre {{institution}}.',
            cancel: 'Cancelar',
            send_invite: 'Enviar invitación',
            create_profile: 'Crear perfil',
          },
          success: {
            title: 'Enviar invitación',
            title_quickAccessCode: 'Crear perfil',
            confirm_title: 'La invitación ha sido enviada.',
            confirm_title_quickAccessCode: 'El perfil ya está creado.',
            confirm_subtitle:
              'El empleado se incorporará plenamente al equipo tras confirmar la invitación',
            confirm_subtitle_quickAccessCode:
              'El empleado ha sido añadido por completo al equipo y debe cambiar su código de acceso rápido lo antes posible.',
            confirm: 'Encerrar',
          },
          snackbar: {
            success_message: 'La invitación ha sido enviada a {{name}}.',
            error_message: 'Ha ocurrido un error al enviar la invitación',
            success_message_create_profile: 'El perfil ha sido creado para {{name}}.',
            error_message_create_profile: 'Ha ocurrido un error al crear el perfil',
          },
        },
      },
    },
  },
  caregiver_tabs: {
    tabs: {
      profile: 'Perfil',
      affiliations: 'Afiliaciones',
      applications: 'Aplicaciones',
      user_cards: 'Tarjetas',
      playlists: 'Listas de reproducción',
    },
    errors: {
      caregiver_does_not_exist_error: 'No se pudo obtener el cuidador al que intentó acceder.',
    },
  },
  user_affiliations: {
    entity_name_header: 'Nombre de la entidad',
    entity_type_header: 'Tipo de entidad',
    affiliation_type_header: 'Tipo de afiliación',
    remove_affiliation_header: 'Eliminar afiliación',
    remove_affiliation_button: 'Eliminar',
    remove_affiliation_dialog_title:
      '¿Eliminar la afiliación entre el usuario {{user}} y la entidad {{entity}}?',
    remove_affiliation_dialog_message:
      'Al eliminar esta afiliación, el usuario {{user}} ya no tendrá acceso a la entidad {{entity}}.',
    remove_affiliation_dialog_action_yes_button: 'Sí, eliminar',
    remove_affiliation_dialog_action_no_button: 'Cancelar',
    update_affiliation_dialog_title:
      '¿Cambiar el tipo de afiliación del usuario {{user}} con la entidad {{entity}}?',
    update_affiliation_dialog_message:
      'Al cambiar, el tipo de afiliación del usuario se actualizará a "{{type}}" en la entidad {{entity}}.',
    update_affiliation_dialog_action_yes_button: 'Sí, cambiar',
    update_affiliation_dialog_action_no_button: 'Cancelar',
    no_affiliations: 'No hay afiliaciones.',
    errors: {
      remove_affiliation_error_message:
        'Se ha producido un error. No ha sido posible eliminar la afiliación.',
      update_affiliation_error_message:
        'Se ha producido un error. No ha sido posible cambiar el tipo de afiliación.',
    },
  },
  user_applications: {
    entity_filter: 'Entidad',
    search_input_placeholder: 'Buscar aplicaciones',
    name_header: 'Nombre',
    category_header: 'Categoría',
    state_header: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    disabled_state_tooltip: 'No tiene permisos para cambiar el estado de la aplicación',
    no_applications: 'No hay aplicaciones.',
    enable_application_dialog_title: '¿Estás seguro de que quieres activar la aplicación {{name}}?',
    enable_application_dialog_message: 'Al activar la aplicación, estará disponible para {{name}}.',
    enable_application_for_entity_dialog_message:
      'Al activar la aplicación, estará disponible para {{name}} en la entidad {{entity}}.',
    enable_application_dialog_action_yes_button: 'Sí, activar',
    enable_application_dialog_action_no_button: 'Cancelar',
    disable_application_dialog_title:
      '¿Estás seguro de que quieres desactivar la aplicación {{name}}?',
    disable_application_dialog_message:
      'Al desactivar la aplicación, ya no estará disponible para {{name}}.',
    disable_application_for_entity_dialog_message:
      'Al desactivar la aplicación, ya no estará disponible para {{name}} en la entidad {{entity}}.',
    disable_application_dialog_action_yes_button: 'Sí, desactivar',
    disable_application_dialog_action_no_button: 'Cancelar',
    no_affiliations_info_message:
      'El usuario no tiene afiliación con ninguna entidad. Por lo tanto, no es posible consultar las aplicaciones del usuario.',
    errors: {
      enable_application_error_message:
        'Se ha producido un error. No ha sido posible activar la aplicación.',
      disable_application_error_message:
        'Se ha producido un error. No ha sido posible desactivar la aplicación.',
      update_state_application_error_message:
        'Se ha producido un error. No ha sido posible actualizar el estado de la aplicación.',
    },
  },
  user_form: {
    title_create_senior: 'Añadir mayor',
    title_current_user_profile: 'Perfil',
    delete_button: 'Eliminar este perfil',
    delete_caregiver_dialog_title: '¿Eliminar perfil del cuidador {{name}}?',
    delete_caregiver_dialog_content:
      'Al eliminar este perfil, se eliminará definitivamente de la lista de cuidadores y se eliminará la afiliación de {{name}} con la entidad {{entity}}.',
    delete_senior_dialog_title:
      '¿Estás seguro de que quieres borrar "{{name}}" de la lista de mayores?',
    delete_senior_dialog_content: 'El mayor será marcado como inactivo.',
    delete_caregiver_invite_title: '¿Cancelar la invitación para {{name}}?',
    delete_caregiver_invite_content: 'Al cancelar, {{name}} no podrá aceptar la invitación.',
    entity_input: 'Entidad',
    full_name_input: 'Nombre completo',
    display_name_input: 'Nombre',
    state_input: 'Estado {{ role }}',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    gender_input: 'Género',
    gender_placeholder_input: 'Seleccione un género',
    birthday_input: 'Fecha de nacimiento',
    date_placeholder_input: 'Seleccione una fecha',
    admission_date_input: 'Fecha de ingreso',
    departure_date_input: 'Fecha de salida',
    address_input: 'Dirección',
    address_placeholder_input: 'Escriba su dirección',
    religion_input: 'Religión',
    religion_placeholder_input: 'Seleccione una religión',
    profession_input: 'Ámbito profesional',
    profession_description_input: 'Profesión/Ocupación',
    social_network_city_input: 'Ubicación social',
    language_input: 'Idioma',
    language_placeholder_input: 'Seleccione un idioma',
    region_input: 'Región',
    region_placeholder_input: 'Seleccione una región',
    input_disabled_by_unknown_region: 'Necesita añadir la "Región" para editar este campo',
    email_input: 'Dirección de e-mail',
    email_placeholder_input: 'Añadir correo electrónico',
    mobile_phone_number_input: 'Número de móvil',
    mobile_phone_number_placeholder_input: 'Añadir un número de teléfono móvil',
    mobile_and_landline_phone_number_input: 'Número de móvil/teléfono',
    tax_payer_number_input: 'Número de identificación fiscal - NIF',
    tax_payer_number_input_placeholder: 'Añadir un número de identificación fiscal',
    patient_number_input: 'Número de usuario - SNS',
    patient_number_input_placeholder: 'Añadir un número de usuario',
    social_security_number_input: 'Número de seguridad social - NUSS',
    social_security_number_input_placeholder: 'Añadir un número de seguridad social',
    room_input: 'Habitación',
    room_placeholder_input: 'El número de la habitación en la que se aloja la persona mayor',
    social_responses_input: 'Servicios sociales',
    social_responses_placeholder_input: 'Seleccione las servicios sociales de su institución',
    profile_picture_input: 'Foto de perfil',
    process_number_input: 'Número de proceso',
    process_number_input_placeholder: 'Añade el número de proceso',
    recurring_fee_input: 'Cuota mensual',
    recurring_fee_input_placeholder: '500',
    marital_status_input: 'Estado civil',
    marital_status_input_placeholder: 'Selecciona el estado civil',
    aditional_info_input: 'Otra información',
    additional_info_input_placeholder: 'Escribe...',
    education_level_input: 'Nivel de estudios',
    education_level_input_placeholder: 'Selecciona el nivel de estudios',
    citizen_card_input: 'Número de Documento Nacional de Identidad - DNI',
    citizen_card_input_placeholder: 'Añade el número de Documento Nacional de Identidad',
    cancel_button: 'Anular',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    deleting_button: 'Eliminando...',
    create_senior_dialog_title: '¿Guardar la creación del mayor "{{name}}"?',
    create_senior_dialog_message: 'Al guardar, el nuevo mayor se añadirá a la lista de mayores.',
    edit_senior_dialog_title: '¿Guardar cambios en el perfil del mayor "{{name}}"?',
    edit_senior_dialog_message:
      'Al guardar, el mayor se actualizará con la información recién ingresada.',
    edit_caregiver_dialog_title: '¿Guardar cambios en el perfil del cuidador "{{name}}"?',
    edit_caregiver_dialog_message:
      'Al guardar, el cuidador se actualizará con la información recién ingresada.',
    edit_caregiver_affiliation_type_dialog_title:
      '¿Guardar cambios en el tipo de afiliación del cuidador {{name}}?',
    edit_caregiver_affiliation_type_dialog_message:
      'Al guardar, el tipo de afiliación del cuidador se actualizará con la información recién ingresada.',
    edit_current_user_profile_dialog_title: '¿Guardar cambios en su perfil?',
    edit_current_user_profile_dialog_message:
      'Al guardar, su perfil se actualizará con la información recién ingresada.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Guardar',
    senior_router_prompt_edit_title:
      '¿Está seguro de que desea salir sin guardar los cambios en el perfil del mayor de "{{name}}"?',
    senior_router_prompt_create_title:
      '¿Está seguro de que desea salir sin guardar la creación del mayor "{{name}}"?',
    senior_router_prompt_create_message:
      'Al salir, el mayor no se guardará y se perderá toda la información introducida en el.',
    senior_router_prompt_edit_message:
      'Al salir, perderá todos los cambios introducidos recientemente en el perfil del mayor.',
    caregiver_edit_router_prompt_edit_title:
      '¿Está seguro de que desea salir sin guardar los cambios en el perfil del cuidador "{{name}}"?',
    caregiver_edit_router_prompt_edit_message:
      'Al salir, perderá todos los cambios introducidos recientemente en el perfil del cuidador.',
    caregiver_edit_affiliation_type_router_prompt_edit_title:
      '¿Está seguro de que desea salir sin guardar los cambios en el tipo de afiliación del cuidador {{name}}?',
    caregiver_edit_affiliation_type_router_prompt_edit_message:
      'Al salir, perderá todos los cambios introducidos recientemente en el tipo de afiliación del cuidador.',
    current_user_router_prompt_edit_title:
      '¿Está seguro de que desea salir sin guardar los cambios en su perfil?',
    current_user_router_prompt_edit_message:
      'Al salir, perderá todos los cambios introducidos recientemente en su perfil.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sí, salir',
    senior_create_cancel_toast: 'Se canceló la creación del nuevo mayor.',
    senior_edit_cancel_toast: 'Se canceló la edición del mayor "{{name}}".',
    caregiver_edit_cancel_toast: 'Se canceló la edición del cuidador "{{name}}".',
    current_user_edit_cancel_toast: 'Se canceló la edición de su perfil.',
    caregiver_edit_affiliation_type_cancel_toast:
      'Se canceló la edición del tipo de afiliación del cuidador {{name}}.',
    current_user_edit_email_not_allowed_tooltip_title:
      'Actualmente no es posible cambiar su dirección de e-mail.',
    caregiver_edit_email_not_allowed_tooltip_title:
      'Actualmente no es posible cambiar el dirección de e-mail del cuidador.',
    current_user_edit_state_not_allowed_tooltip_title:
      'Actualmente no es posible cambiar su estado.',
    profile_picture_info_message: 'Para obtener la mejor resolución, la imagen debe ser JPG/JPEG.',
    display_name_other_user_info_message:
      'En este campo, indique el nombre con el que el usuario es llamado diariamente. Este es el nombre que los familiares, amigos y otros cuidadores verán en el sistema cuando interactúen con el usuario.',
    display_name_current_user_info_message:
      'En este campo, indique el nombre con el que le llaman diariamente. Este es el nombre que verán otros usuarios en el sistema cuando interactúen con usted.',
    roles_ids_input: 'Permisos',
    roles_ids_placeholder_input: 'Seleccionar permisos',
    input_placeholder: 'Escreva um {{ type }}',
    not_specified: 'No especificado',
    input_read_only_tooltip: 'No tiene permisos para editar la información del perfil',
    quick_access_code_input: 'Código de acceso rápido',
    quick_access_code_redefine_button: 'Redefinir',
    quick_access_code_disabled:
      'Ya existe un código de acceso rápido. Haga clic en "Redefinir" para cambiarlo.',
    success: {
      save_success_button: '¡Guardado!',
      delete_success_button: '¡Eliminado!',
      create_senior_success_message: '¡El mayor "{{name}}" se creó con éxito!',
      edit_senior_success_message: 'Se guardaron los cambios en el perfil del mayor.',
      edit_caregiver_success_message: 'Se guardaron los cambios en el perfil del cuidador.',
      edit_caregiver_roles_ids_success_message:
        'Se guardaron los cambios en el cargo del cuidador.',
      edit_current_user_success_message: 'Se guardaron los cambios en su perfil.',
      edit_current_user_app_language_to_chosen_language_success_message:
        'El idioma de la aplicación se ha actualizado a {{language}}.',
      edit_current_user_app_language_to_browser_language_success_message:
        'El idioma de la aplicación se ha actualizado al idioma de su navegador, el {{language}}.',
      edit_current_user_app_language_to_default_language_success_message:
        'El idioma de la aplicación se ha actualizado al idioma usado por defecto, el {{language}}.',
      success_delete_senior: 'El mayor {{name}} ha sido eliminado.',
      success_delete_caregiver: 'El cuidador {{name}} ha sido eliminado.',
      success_delete_invite: 'La invitación para {{name}} ha sido cancelada.',
    },
    errors: {
      error_on_save_button: 'Error al guardar',
      error_on_delete_senior_button: 'Error al eliminar el mayor',
      error_on_delete_caregiver_button: 'Error al eliminar el cuidador',
      error_on_delete_invite: 'Error al eliminar el invitación',
      edit_form_validation_invalid_field:
        'Complete este campo para completar la edición del perfil',
      caregiver_edit_affiliation_type_form_validation_invalid_field:
        'Complete este campo para completar la edición del tipo de afiliación del cuidador',
      form_validation_invalid_phone_number: 'El número de móvil proporcionado no es válido',
      form_validation_not_mobile_phone_number:
        'El número proporcionado no coincide con un número de móvil',
      form_validation_invalid_email: 'El dirección de e-mail proporcionado no es válido',
      form_validation_invalid_number:
        'Según la región {{region}} el número debe tener {{digits}} caracteres',
      form_validation_invalid_room_name: 'El nombre de la habitación proporcionado no es válido',
      form_validation_room_maximum_characters:
        'El nombre de habitación debe tener menos de {{max}} caracteres',
      form_validation_invalid_display_name_maximum_characters:
        'El nombre debe tener menos de {{max}} caracteres',
      create_active_senior_licenses_number_exceeded_error:
        'No se puede crear un mayor activo. La entidad ya ha alcanzado el número máximo de mayores activos por el número de licencias activas.',
      activate_senior_licenses_number_exceeded_error:
        'No se puede activar el mayor. La entidad ya ha alcanzado el número máximo de mayores activos por el número de licencias activas.',
      form_validation_invalid_quick_access_code:
        'El código de acceso rápido debe tener {{digits}} dígitos',
      form_validation_process_number_max_chars:
        'El número de proceso debe tener menos de {{max}} caracteres',
      form_validation_recurring_fee_max_chars:
        'El cuota mensual debe tener menos de {{max}} caracteres',
      form_validation_aditional_info_max_chars:
        'Las otras informaciones deben tener menos de {{max}} caracteres',
    },
  },
  user_attachments: {
    list: {
      headers: {
        name: 'Nombre',
        description: 'Descripción',
        author: 'Añadido por',
        date: 'Fecha',
        size: 'Tamaño',
      },
      actions: {
        edit: 'Editar',
        delete: 'Eliminar',
        download: 'Descargar',
      },
    },
    empty: {
      title: 'Aún no hay adjuntos registrados.',
      subtitle: 'Haga clic en "Añadir" para añadir un adjunto.',
    },
    modals: {
      create: {
        title: 'Añadir adjuntos',
      },
      edit: {
        title: 'Editar adjunto',
        name: 'Nombre',
        description: 'Descripción',
        errors: {
          name_required: 'El nombre del adjunto es obligatorio',
          name_max_length: 'El nombre del adjunto debe tener menos de {{max}} caracteres',
          description_max_length:
            'La descripción del adjunto debe tener menos de {{max}} caracteres',
        },
      },
      delete: {
        title: '¿Está seguro de que quiere eliminar el adjunto<br/>"{{name}}"?',
        description: 'El adjunto será eliminado permanentemente.',
      },
    },
    success: {
      create: 'Anexos añadidos con éxito',
      edit: 'Anexo editado con éxito',
      delete: 'Anexo eliminado con éxito',
    },
    error: {
      create: 'Error al añadir adjuntos',
      edit: 'Error al editar el adjunto',
      delete: 'Error al eliminar el adjunto',
      download: 'Error al descargar el adjunto',
    },
    access_denied: {
      create: 'No tiene permisos para añadir adjuntos',
      edit: 'No tiene permisos para editar adjuntos',
      delete: 'No tiene permisos para eliminar adjuntos',
    },
  },
  entity_services: {
    root: {
      title: 'Servicios',
      add_button: 'Crear servicio',
      search_input_placeholder: 'Buscar servicios',
      name_header: 'Nombre',
      display_order_header: 'Orden',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      no_services: 'No hay servicios configurados.',
      deleted_service_dialog_title: 'Servicio "{{name}}" eliminado',
      deleted_service_dialog_message1: '"{{name}}" fue eliminado.',
      deleted_service_dialog_message2:
        'El servicio ya no está disponible en la aplicación {{caregiversAppName}} y en {{seniorsDeviceName}}.',
      deleted_service_dialog_ok_button: 'Ok',
    },
    edit: {
      title_create: 'Crear servicio',
      title_edit: 'Editar servicio',
      title_view: 'Ver servicio',
      exit_button: 'Salir',
      save_button: 'Guardar',
      saving_button: 'Guardando...',
      title_input: 'Nombre',
      state_input: 'Estado',
      phone_number_input: 'Número de teléfono',
      phone_number_use_general_number: 'Usar número general',
      phone_number_use_specific_number: 'Usar número específico',
      display_order_input: 'Orden',
      description_input: 'Descripción',
      details_input: 'Detalles',
      attach_document_input: 'Añadir documento PDF',
      document_informative_note_input: 'Mensaje sobre información del contrato',
      icon_input: 'Icono',
      image_input: 'Imagen',
      button_preview: 'Vista previa del botón',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      number_input: 'Número',
      upload: 'Adjuntar',
      remove_document: 'Eliminar',
      view_document: 'Vista',
      image_info:
        'La imagen debe tener el tipo JPG/JPEG. Para obtener mejores resultados, utilice la resolución de 292x288px.',
      delete_button: 'Eliminar servicio',
      deleting_button: 'Eliminando...',
      delete_dialog_title: '¿Eliminar servicio "{{name}}"?',
      delete_dialog_content:
        'Al eliminar este servicio, todos los usuarios de la aplicación {{caregiversAppName}} y {{seniorsDeviceName}} perderán el acceso a él. Además, el servicio también se eliminará definitivamente de la lista de servicios de la entidad.',
      delete_dialog_action_yes_button: 'Sí, eliminar',
      delete_dialog_action_no_button: 'Cancelar',
      router_prompt_edit_title:
        '¿Está seguro de que desea salir sin guardar los cambios en los detalles del servicio de "{{name}}"?',
      router_prompt_create_title:
        '¿Está seguro de que desea salir sin guardar la creación del servicio "{{name}}"?',
      router_prompt_create_message:
        'Al salir, el servicio no se guardará y se perderá toda la información introducida en el.',
      router_prompt_edit_message:
        'Al salir, perderá todos los cambios introducidos recientemente en los detalles del servicio.',
      router_prompt_action_cancel_button: 'Cancelar',
      router_prompt_action_ok_button: 'Sí, salir',
      create_service_dialog_title: '¿Guardar la creación del servicio "{{name}}"?',
      create_service_dialog_message:
        'Al guardar, el nuevo servicio se añadirá a la lista de servicios.',
      edit_service_dialog_title: '¿Guardar cambios en los detalles del servicio "{{name}}"?',
      edit_service_dialog_message:
        'Al guardar, el servicio se actualizará con la información recién ingresada.',
      save_service_dialog_action_cancel_button: 'Cancelar',
      save_service_dialog_action_save_button: 'Guardar',
      service_create_cancel_toast: 'Se canceló la creación del nuevo servicio.',
      service_edit_cancel_toast: 'Se canceló la edición del servicio "{{name}}".',
      errors: {
        error_on_save_button: 'Error al guardar',
        error_on_delete_button: 'Error al eliminar',
        edit_form_validation_invalid_field:
          'Complete este campo para completar la edición del servicio',
        create_form_validation_invalid_field:
          'Complete este campo para completar la creación del servicio',
        create_form_validation_invalid_icon:
          'Elija un icono para completar la creación del servicio',
        create_form_validation_invalid_image:
          'Sube una imagen para completar la creación del servicio',
        form_validation_invalid_phone_number: 'El número de teléfono proporcionado no es válido',
        service_does_not_exist_error: 'No se pudo obtener el servicio al que intentó acceder.',
      },
      success: {
        save_success_button: '¡Guardado!',
        delete_success_button: '¡Eliminado!',
        create_success_message: '¡El servicio "{{name}}" se creó con éxito!',
        edit_success_message: 'Se guardaron los cambios en los detalles del servicio.',
      },
    },
  },
  applications: {
    system_administrator: {
      root: {
        title: 'Aplicaciones',
        search_input_placeholder: 'Buscar aplicaciones',
        name_header: 'Nombre',
        category_header: 'Categoría',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_applications: 'No hay aplicaciones.',
        add_button: 'Añadir aplicación',
      },
      edit: {
        entities: {
          search_input_placeholder: 'Buscar entidades',
          name_header: 'Nombre',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_entities: 'No hay entidades.',
          enable_application_dialog_message:
            'Al activar la aplicación, estará disponible para la entidad {{name}}.',
          enable_application_dialog_action_yes_button: 'Sí, activar',
          enable_application_dialog_action_no_button: 'Cancelar',
          disable_application_dialog_message:
            'Al desactivar la aplicación, ya no estará disponible para la entidad {{name}}.',
          disable_application_dialog_action_yes_button: 'Sí, desactivar',
          disable_application_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_application_error_message:
              'Se ha producido un error. No ha sido posible activar la aplicación.',
            disable_application_error_message:
              'Se ha producido un error. No ha sido posible desactivar la aplicación.',
            update_state_application_error_message:
              'Se ha producido un error. No ha sido posible actualizar el estado de la aplicación.',
          },
        },
      },
    },
    professional_caregiver: {
      root: {
        search_input_placeholder: 'Buscar aplicaciones',
        name_header: 'Nombre',
        category_header: 'Categoría',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_applications: 'No hay aplicaciones.',
        enable_application_dialog_message:
          'Al activar la aplicación, estará disponible para todos los usuarios de la entidad {{name}}.',
        enable_application_dialog_action_yes_button: 'Sí, activar',
        enable_application_dialog_action_no_button: 'Cancelar',
        disable_application_dialog_message:
          'Al desactivar la aplicación, ya no estará disponible para todos los usuarios de la entidad {{name}}.',
        disable_application_dialog_action_yes_button: 'Sí, desactivar',
        disable_application_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_application_error_message:
            'Se ha producido un error. No ha sido posible activar la aplicación.',
          disable_application_error_message:
            'Se ha producido un error. No ha sido posible desactivar la aplicación.',
          update_state_application_error_message:
            'Se ha producido un error. No ha sido posible actualizar el estado de la aplicación.',
        },
      },
      edit: {
        users: {
          search_input_placeholder: 'Buscar usuarios',
          name_header: 'Nombre',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_users: 'No hay usuarios.',
          enable_application_dialog_message:
            'Al activar la aplicación, estará disponible para {{name}}.',
          enable_application_dialog_action_yes_button: 'Sí, activar',
          enable_application_dialog_action_no_button: 'Cancelar',
          disable_application_dialog_message:
            'Al desactivar la aplicación, ya no estará disponible para {{name}}.',
          disable_application_dialog_action_yes_button: 'Sí, desactivar',
          disable_application_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_application_error_message:
              'Se ha producido un error. No ha sido posible activar la aplicación.',
            disable_application_error_message:
              'Se ha producido un error. No ha sido posible desactivar la aplicación.',
            update_state_application_error_message:
              'Se ha producido un error. No ha sido posible actualizar el estado de la aplicación.',
          },
        },
      },
    },
  },
  application_tabs: {
    tabs: {
      details: 'Detalles',
      users: 'Usuarios',
      entities: 'Entidades',
    },
    errors: {
      application_does_not_exist_error: 'No se pudo obtener la aplicación al que intentó acceder.',
    },
  },
  application_form: {
    name_input: 'Nombre',
    state_input: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    description_input: 'Descripción',
    is_system_application_input: 'Aplicación del sistema',
    is_system_application_value_yes: 'Sí',
    is_system_application_value_no: 'No',
    available_for_user_types_input: 'Tipos de usuarios para los que está disponible la aplicación',
    icon_input: 'Icono',
    picture_input: 'Imagen',
    category_input: 'Categoría',
    activity_type_input: 'Tipo de actividad',
    windows_key_input: 'Llave Windows',
    android_key_input: 'Llave Android',
    input_disabled_tooltip: 'No tiene permisos para editar la aplicación',
  },
  devices: {
    root: {
      search_input_placeholder: 'Buscar dispositivos',
      identifier_header: 'Identificador',
      type_header: 'Tipo',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      user_header: 'Usuario',
      no_devices: 'No hay dispositivos.',
      add_button: 'Añadir dispositivo',
    },
  },
  device_tabs: {
    device_title: 'Dispositivo "{{name}}"',
    tabs: {
      details: 'Detalles',
      health: 'Salud',
      information: 'Información',
    },
    errors: {
      device_does_not_exist_error: 'No se pudo obtener el dispositivo al que intentó acceder.',
    },
  },
  user_cards: {
    root: {
      title: 'Tarjetas',
      search_input_placeholder: 'Buscar tarjetas',
      name_header: 'Nombre',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      user_header: 'Usuario',
      no_cards: 'No hay tarjetas.',
      add_button: 'Añadir tarjeta',
      deleted_card_dialog_title: 'Tarjeta "{{name}}" eliminada',
      deleted_card_dialog_message: 'La tarjeta ya no existe en el sistema.',
      deleted_card_dialog_ok_button: 'Ok',
    },
    edit: {
      errors: {card_does_not_exist_error: 'No se pudo obtener la tarjeta al que intentó acceder.'},
    },
  },
  gdpr_erasure_requests: {
    root: {
      title: 'Derecho al olvido (RGPD)',
      add_button: 'Solicitar eliminación',
      no_requests: 'No hay solicitudes de eliminación.',
      status_header: 'Estado',
      requested_at_header: 'Solicitado el',
      completed_at_header: 'Completado el',
      error_header: 'Error',
      user_id_hash_header: 'Hash del usuario',
      user_email_hash_header: 'Hash del correo',
      status_processing: 'En proceso',
      status_completed: 'Completado',
      status_failed: 'Fallido',
    },
    create_modal: {
      title: 'Solicitar eliminación de datos personales',
      email_label: 'Correo electrónico del usuario',
      email_placeholder: 'usuario@ejemplo.com',
      email_required: 'El correo electrónico es obligatorio.',
      email_invalid: 'Introduce un correo electrónico válido.',
      cancel_button: 'Cancelar',
      submit_button: 'Continuar',
      submitting_button: 'Enviando...',
      confirm_title: 'Confirmar eliminación de datos',
      confirm_description:
        'Esta acción eliminará/anonimizará permanentemente los datos personales del usuario asociado a este correo.',
      confirm_cannot_be_undone: 'Esta acción es irreversible y no se puede deshacer.',
      confirm_button: 'Eliminar definitivamente',
      cancel_confirm_button: 'Cancelar',
    },
    errors: {
      forbidden: 'No tienes permiso para realizar esta acción.',
      user_not_found: 'No existe ningún usuario con ese correo.',
      unknown: 'Se produjo un error al procesar la solicitud. Inténtalo de nuevo.',
    },
    success: {
      request_created: 'Solicitud de eliminación creada correctamente.',
    },
  },
  entity_photos: {
    root: {
      add_button: 'Añadir',
      deleted_photo_dialog_title: 'Fotografía apagada',
      deleted_photo_dialog_message:
        'La fotografía ya no está disponible en la pantalla de bloqueo de la computadora {{brand}}.',
      deleted_photo_dialog_ok_button: 'Ok',
      entity_photos_only_for_computer_info_message:
        'Las fotos que añadas se pueden ver en los ordenadores y tabletas Sioslife.',
    },
    edit: {
      title_create: 'Crear fotografías',
      title_edit: 'Editar fotografía',
      photo_input: 'Fotografía',
      photos_input: 'Fotografías',
      description_input: 'Descripción',
      exit_button: 'Salir',
      save_button: 'Guardar',
      saving_button: 'Guardando...',
      photos_create_cancel_toast: 'Se canceló la creación de las nuevas fotografías.',
      photo_edit_cancel_toast: 'Se canceló la edición de la fotografía.',
      delete_button: 'Eliminar fotografía',
      deleting_button: 'Eliminando...',
      delete_dialog_title: '¿Está seguro de que desea  eliminar fotografía?',
      delete_dialog_content:
        'Al eliminar la fotografía, ya no aparecerá en la pantalla de bloqueo de la computadora {{brand}} y se eliminará definitivamente.',
      delete_dialog_action_yes_button: 'Sí, eliminar',
      delete_dialog_action_no_button: 'Cancelar',
      router_prompt_edit_title:
        '¿Está seguro de que desea salir sin guardar los cambios en la fotografía?',
      router_prompt_edit_message:
        'Al salir, perderá todos los cambios introducidos recientemente en la fotografía.',
      router_prompt_create_title:
        '¿Está seguro de que desea salir sin guardar la creación de las fotografías?',
      router_prompt_create_message:
        'Al salir, las fotografías no se guardarán y se perderá toda la información introducida en ellas.',
      router_prompt_action_cancel_button: 'Cancelar',
      router_prompt_action_ok_button: 'Sí, salir',
      photos_info: 'Las fotografías deben tener el tipo JPG/JPEG.',
      choose_photos_button: 'Elige fotografías',
      input_disabled_tooltip: 'No tiene permisos para editar los detalles de la fotografía',
      errors: {
        error_on_save_button: 'Error al guardar',
        error_on_delete_button: 'Error al eliminar',
        photo_does_not_exist_error: 'No se pudo obtener la fotografía al que intentó acceder.',
        create_form_validation_invalid_field:
          'Complete este campo para completar la creación de las fotografías',
        form_validation_invalid_description_maximum_characters:
          'La descripción debe tener menos de {{max}} caracteres',
      },
      success: {
        save_success_button: '¡Guardado!',
        delete_success_button: '¡Eliminado!',
        create_success_message: '¡Las fotografías se crearon con éxito!',
        edit_success_message: 'Se guardaron los cambios en la fotografía.',
      },
      warnings: {
        partial_create_success_message:
          '¡Algunas fotografías se crearon con éxito! Sin embargo, no fue posible crear {{total}} fotografía(s): {{files}}.',
      },
    },
  },
  entities: {
    root: {
      title: 'Entidades',
      search_input_placeholder: 'Buscar entidades',
      name_header: 'Nombre',
      type_header: 'Tipo',
      state_header: 'Estado',
      state_value_true: 'Activo',
      state_value_false: 'Inactivo',
      no_entities: 'No hay entidades.',
      add_button: 'Añadir entidade',
      deleted_entity_dialog_title: 'Entidad "{{name}}" eliminada',
      deleted_entity_dialog_message: 'La entidad ya no existe en el sistema.',
      deleted_entity_dialog_ok_button: 'Ok',
    },
    edit: {
      tabs: {
        details: 'Detalles',
        seniors: 'Mayores',
        professional_caregivers: 'Profesionales',
        applications: 'Aplicaciones',
        devices: 'Dispositivos',
        user_cards: 'Tarjetas',
        activity: 'Actividad',
        statistics: 'Estadísticas',
        licenses: 'Licencias',
        playlists: 'Listas de reproducción',
      },
      seniors: {
        search_input_placeholder: 'Buscar mayores',
        name_header: 'Nombre',
        gender_header: 'Género',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_seniors: 'No hay mayores.',
        enable_senior_dialog_title: '¿Activar el mayor {{name}}?',
        enable_senior_dialog_message: 'Al activar el senior, podrá disfrutar el sistema {{brand}}.',
        enable_senior_dialog_action_yes_button: 'Sí, activar',
        enable_senior_dialog_action_no_button: 'Cancelar',
        disable_senior_dialog_title: '¿Desactivar el mayor {{name}}?',
        disable_senior_dialog_message:
          'Al desactivar el senior, ya no podrá disfrutar el sistema {{brand}}.',
        disable_senior_dialog_action_yes_button: 'Sí, desactivar',
        disable_senior_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_senior_error_message:
            'Se ha producido un error. No ha sido posible activar el mayor.',
          disable_senior_error_message:
            'Se ha producido un error. No ha sido posible desactivar el mayor.',
          update_state_senior_error_message:
            'Se ha producido un error. No ha sido posible actualizar el estado del mayor.',
        },
      },
      professional_caregivers: {
        search_input_placeholder: 'Buscar profesionales',
        invite_button: 'Invitar profesional',
        name_header: 'Nombre',
        email_header: 'E-mail',
        affiliation_type_header: 'Tipo',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_professional_caregivers: 'No hay profesionales.',
      },
      applications: {
        search_input_placeholder: 'Buscar aplicaciones',
        name_header: 'Nombre',
        category_header: 'Categoría',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_applications: 'No hay aplicaciones.',
        enable_application_dialog_message:
          'Al activar la aplicación, estará disponible para la entidad {{name}}.',
        enable_application_dialog_action_yes_button: 'Sí, activar',
        enable_application_dialog_action_no_button: 'Cancelar',
        disable_application_dialog_message:
          'Al desactivar la aplicación, ya no estará disponible para la entidad {{name}}.',
        disable_application_dialog_action_yes_button: 'Sí, desactivar',
        disable_application_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_application_error_message:
            'Se ha producido un error. No ha sido posible activar la aplicación.',
          disable_application_error_message:
            'Se ha producido un error. No ha sido posible desactivar la aplicación.',
          update_state_application_error_message:
            'Se ha producido un error. No ha sido posible actualizar el estado de la aplicación.',
        },
      },
      devices: {
        search_input_placeholder: 'Buscar dispositivos',
        identifier_header: 'Identificador',
        type_header: 'Tipo',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        user_header: 'Usuario',
        no_devices: 'No hay dispositivos.',
      },
      user_cards: {
        search_input_placeholder: 'Buscar tarjetas',
        name_header: 'Nombre',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        user_header: 'Usuario',
        no_cards: 'No hay tarjetas.',
        front_card_design_button: 'Diseño Frontal de la Tarjeta',
        back_card_design_button: 'Diseño Posterior de la Tarjeta',
      },
      playlists: {
        search_input_placeholder: 'Buscar listas',
        type_filter: 'Tipo',
        name_header: 'Nombre',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_playlist: 'No hay listas de reproducción.',
        enable_playlist_dialog_title: '¿Activar la lista de reproducción "{{name}}"?',
        enable_playlist_dialog_message:
          'Al activar la lista de reproducción, estará disponible para la entidad {{name}}.',
        enable_playlist_dialog_action_yes_button: 'Sí, activar',
        enable_playlist_dialog_action_no_button: 'Cancelar',
        disable_playlist_dialog_title: '¿Desactivar la lista de reproducción "{{name}}"?',
        disable_playlist_dialog_message:
          'Al desactivar la lista de reproducción, ya no estará disponible para la entidad {{name}}.',
        disable_playlist_dialog_action_yes_button: 'Sí, desactivar',
        disable_playlist_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_playlist_error_message:
            'Se ha producido un error. No ha sido posible activar la lista de reproducción.',
          disable_playlist_error_message:
            'Se ha producido un error. No ha sido posible desactivar la lista de reproducción.',
          update_state_playlist_error_message:
            'Se ha producido un error. No ha sido posible actualizar el estado de la lista de reproducción.',
        },
      },
      playlist_items: {
        title: 'Lista de reproducción "{{name}}"',
        search_input_placeholder: 'Buscar elementos',
        title_header: 'Título',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_items: 'No hay elementos en la listas de reproducción.',
        enable_item_dialog_title: '¿Activar el elemento "{{name}}" en la lista de reproducción?',
        enable_item_dialog_message:
          'Al activar el elemento en la lista de reproducción, estará disponible para la entidad {{name}}.',
        enable_item_dialog_action_yes_button: 'Sí, activar',
        enable_item_dialog_action_no_button: 'Cancelar',
        disable_item_dialog_title:
          '¿Desactivar el elemento "{{name}}" en la lista de reproducción?',
        disable_item_dialog_message:
          'Al desactivar el elemento en la lista de reproducción, ya no estará disponible para la entidad {{name}}.',
        disable_item_dialog_action_yes_button: 'Sí, desactivar',
        disable_item_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_item_error_message:
            'Se ha producido un error. No ha sido posible activar el elemento en la lista de reproducción.',
          disable_item_error_message:
            'Se ha producido un error. No ha sido posible desactivar el elemento en la lista de reproducción.',
          update_state_item_error_message:
            'Se ha producido un error. No ha sido posible actualizar el estado del elemento en la lista de reproducción.',
          playlist_does_not_exist_error:
            'No se pudo obtener la lista de reproducción al que intentó acceder.',
        },
      },
      licenses: {
        number_of_monthly_active_licenses_not_defined: 'No definido',
        number_of_monthly_active_licenses_title: 'Licencias activas mensuales',
        save_button: 'Guardar',
        saving_button: 'Guardando...',
        edit_dialog_title: '¿Guardar cambios en el número de licencias activas mensuales?',
        edit_dialog_content:
          'Al guardar, el número de licencias activas mensuales se actualizará a {{total}} para la entidad {{entity}}.',
        edit_dialog_action_cancel_button: 'Cancelar',
        edit_dialog_action_save_button: 'Guardar',
        router_prompt_title:
          '¿Está seguro de que desea salir sin guardar los cambios en el número de licencias activas mensuales?',
        router_prompt_message:
          'Al salir, perderá todos los cambios introducidos recientemente en el número de licencias activas mensuales para la entidad {{entity}}.',
        router_prompt_action_cancel_button: 'Cancelar',
        router_prompt_action_ok_button: 'Sí, salir',
        number_of_monthly_active_licenses_edit_cancel_toast:
          'Se canceló la edición del número de licencias activas mensuales.',
        success: {save_success_button: '¡Guardado!'},
        errors: {
          error_on_save_button: 'Error al guardar',
          edit_form_validation_invalid_field:
            'Complete este campo para completar la edición del número de licencias activas mensuales',
          form_validation_invalid_number_of_monthly_active_licenses:
            'El número de licencias activas mensuales no es válido',
        },
      },
      errors: {
        entity_does_not_exist_error: 'No se pudo obtener el entidad al que intentó acceder.',
      },
    },
  },
  affiliated_entity_profile: {
    errors: {entity_does_not_exist_error: 'No se pudo obtener el entidad al que intentó acceder.'},
  },
  section_tabs: {exit_button: 'Salir'},
  profession_input: {no_options: 'Sin opciones', loading: 'Subiendo...'},
  social_network_city_input: {no_options: 'Sin opciones', loading: 'Subiendo...'},
  user_menu: {
    logout: 'Terminar sesión',
    profile: 'Perfil',
    settings: 'Ajustes',
    office: 'Área de gestión',
    dailyCareLog: 'Registro de cuidados',
    errors: {
      logout_error_message: 'Se ha producido un error. No ha sido posible terminar la sesión.',
    },
  },
  error_box: {
    title: 'Incapaz de realizar la acción.',
    description:
      'Lo sentimos, hubo un error al intentar realizar la acción. Por favor, entre en contacto con el servicio de Atención al Cliente con los detalles proporcionados.',
    details_button: 'Ver detalles',
  },
  country_select: {
    title: 'Prefijo país',
    no_options: 'Sin opciones',
  },
  tablet_button: {
    empty_title: '(Título del botón)',
  },
  icons_picker: {
    title: 'Selector de iconos',
    cancel_button: 'Cancelar',
    confirm_button: 'Confirmar',
  },
  image_cropper_dialog: {
    title: 'Preparar imagen',
    zoom: 'Zoom',
    rotation: 'Rotación',
    save_button: 'Guardar',
    cancel_button: 'Cancelar',
  },
  loading_content: {
    loading: 'Subiendo...',
  },
  pdf_viewer: {
    error: 'No se ha podido mostrar el PDF.',
  },
  activity_card: {
    today: 'Hoy dia',
    yesterday: 'Ayer',
    this_week: 'Esta semana',
    this_month: 'Este mes',
    previous_month: 'En el mes pasado',
    past: 'Pasado',
    entity: 'Entidad',
    device: 'Dispositivo',
    duration: 'Duración',
    end_type: 'Tipo final',
    participants: 'Las participantes',
    application: 'Aplicación',
    application_battle_with: 'con',
    level_on_app: 'Nivel {{level}} en la aplicación',
    missed_call: 'trató de llamar pero la llamada no fue respondida.',
    rejected_call: 'intentó llamar pero la llamada fue rechazada.',
    call_started: 'llamó a',
    drawing_sent: 'Dibujo enviado a',
    drawing_saved: 'Dibujo compartido con',
    drawing_shared: 'Dibujo compartido en el Álbum de Familia de',
    painting_sent: 'Pintura enviada a',
    painting_saved: 'Pintura compartida con',
    painting_shared: 'Pintura compartida en el Álbum de Familia de',
    and: 'y',
    photo_sent: 'Fotografía enviada a',
    photo_saved: 'Fotografía compartida con',
    photo_shared: 'Fotografía compartida en el Álbum de Familia de',
    camera_used: 'Cámara usada',
    response: 'Respuesta',
    video_message_sent_to: 'Mensaje de vídeo enviado a',
    video_message_sent_by: 'Mensaje de vídeo enviado por',
    video_sent: 'Vídeo enviado a',
    video_saved: 'Vídeo compartido con',
    video_shared: 'Vídeo compartido en el Álbum de Familia de',
    currently_happening: 'Pasando ahora mismo',
  },
  notification_card: {
    open_invite: 'Abrir invitación',
    open_occurrences: 'Ir a ocurrencia',
    relationship_invite: {
      message_sender:
        '{{sender}} le ha enviado una invitación para añadir a {{target}} a su lista de contactos.',
      message_sender_same_as_target:
        '{{sender}} le ha enviado una invitación para añadirte a tu lista de contactos.',
      message_sender_unknown:
        'Ha recibido una invitación para añadir a {{target}} a su lista de contactos.',
    },
    affiliation_invite: {
      message_sender:
        '{{sender}} le ha enviado una invitación para afiliarse a la entidad {{target}}.',
      message_sender_same_as_target: '{{sender}} le ha enviado una invitación para afiliarse.',
      message_sender_unknown: 'Ha recibido una invitación para afiliarse a la entidad {{target}}.',
    },
    user_signed_in: '{{name}} ha iniciado su sesión en {{brand}}.',
    photo_sent: '{{name}} te envió una fotografía.',
    photo_shared: '{{name}} ha compartido una fotografía.',
    drawing_sent: '{{name}} te envió un dibujo.',
    drawing_shared: '{{name}} ha compartido un dibujo.',
    painting_sent: '{{name}} te envió una pintura.',
    painting_shared: '{{name}} ha compartido una pintura.',
    video_sent: '{{name}} te envió un vídeo.',
    video_shared: '{{name}} ha compartido un vídeo.',
    without_license: '{{name}} no tiene licencia para usar los sistemas {{brand}}.',
    missed_call: '{{name}} ha intentado llamarle pero no ha obtenido respuesta.',
    video_message: '{{name}} le ha enviado un mensaje de vídeo.',
    sos_request: 'Solicitud de ayuda SOS de {{name}}. Por favor, confirme si está bien.',
    reminder_response: {
      message: '{{name}} {{response}} al recordatorio "{{reminder}}".',
      responses: {
        positive: 'ha respondido "Sí"',
        negative: 'ha respondido "No"',
        not_responding: 'no ha respondido',
      },
    },
    relationship_invite_request_result: {
      message_accepted: '{{name}} ha aceptado a {{target}} como contacto.',
      message_declined: '{{name}} no ha aceptado a {{target}} como contacto.',
    },
    caregiver_administrator_role_request_result: {
      message_accepted: '{{name}} ha aceptado {{target}} como Administrador.',
      message_declined: '{{name}} no ha aceptado {{target}} como Administrador.',
    },
    relationship_role_change: {
      message_senior_myself: '{{name}} ha definido su perfil como {{role}} de su {{brand}}.',
      message_senior_other: '{{name}} ha definido {{target}} como {{role}} de su {{brand}}.',
      message_caregiver_myself:
        '{{name}} ha definido su perfil como {{role}} del {{brand}} de {{senior}}.',
      message_caregiver_other:
        '{{name}} ha definido {{target}} como {{role}} del {{brand}} de {{senior}}.',
      message_unknown_myself:
        'Su perfil ha sido definido como {{role}} del {{brand}} de {{senior}}.',
      message_unknown_other:
        '{{target}} ha sido definido como {{role}} del {{brand}} de {{senior}}.',
    },
    personal_data_sharing_preferences_change: {
      message: '{{name}} ha alterado las definiciones para compartir datos de su {{brand}}.',
      current_data_settings_title: 'Las definiciones actuales son:',
      can_access_profile_data: 'Información de perfil',
      can_access_contacts: 'Contactos',
      can_access_applications: 'Aplicaciones',
      can_access_multimedia: 'Multimedia',
      can_access_reminders: 'Recordatorios',
      can_access_activity_feed: 'Registro de actividad',
    },
    relationship_end: {
      message_myself:
        '{{name}} ha eliminado su perfil de su lista de contactos. A partir de este punto no van a conseguir comunicar a través los sistemas {{brand}}.',
      message_other:
        '{{name}} ha eliminado {{target}} de su lista de contactos. A partir de aquí {{target}} no va a conseguir entrar en contacto con {{name}} a través los sistemas {{brand}}.',
    },
    media_processing_status: {
      video_message: {
        [ProcessStatus.Done]: 'El mensaje de vídeo fue enviado a {{name}}.',
        [ProcessStatus.Failed]:
          'Se ha producido un error al enviar el mensaje de vídeo. {{name}} no recibirá el mensaje.',
      },
      video_shared: {
        [ProcessStatus.Done]: 'El vídeo ha sido compartido con {{name}}.',
        [ProcessStatus.Failed]:
          'Se ha producido un error al compartir el vídeo. {{name}} no recibirá el vídeo.',
      },
      video_saved: {
        [ProcessStatus.Done]: 'El vídeo se ha guardado con éxito.',
        [ProcessStatus.Failed]: 'Se ha producido un error al guardar el vídeo.',
      },
      photo_shared: {
        [ProcessStatus.Done]: 'La fotografia ha sido compartida con {{name}}.',
        [ProcessStatus.Failed]:
          'Se ha producido un error al compartir la fotografia. {{name}} no recibirá la fotografia.',
      },
      photo_saved: {
        [ProcessStatus.Done]: 'La fotografia se ha guardada con éxito.',
        [ProcessStatus.Failed]: 'Se ha producido un error al guardar la fotografia.',
      },
      unknown: {
        [ProcessStatus.Done]: 'El multimedia se ha guardado con éxito.',
        [ProcessStatus.Failed]: 'Se ha producido un error al guardar el multimedia.',
      },
    },
    media_empty: '{{name}} te pidió que compartieras fotografías y videos.',
    new_occurrence: 'Nueva ocurrencia ha sido creada "{{name}}".',
    new_occurrence_patient: 'Nueva ocurrencia ha sido creada "{{name}}" para "{{patient}}".',
    new_occurrence_assigned:
      '{{registered}} lo señaló como responsable de la ocurrencia "{{name}}".',
    new_occurrence_assigned_patient:
      '{{registered}} lo señaló como responsable de la ocurrencia "{{name}}" del usuario {{patient}}.',
  },
  section_list: {
    refresh: 'Actualizar',
    no_more_data: 'No hay más datos',
    without_data: 'No hay datos',
    today: 'Hoy',
    old_f: 'Más antiguas',
    old_m: 'Más antiguos',
  },
  activity_timeline: {title: 'Actividad'},
  clipboard_text_field: {
    copy_to_clipboard_button: 'Copiar',
    copy_to_clipboard_tooltip_message: 'Copiar en el portapapeles',
    success: {copy_to_clipboard_success_message: '¡Texto copiado en el portapapeles!'},
  },
  activity_end_types: {
    [ActivityEndType.UserAction]: 'Acción del usuario',
    [ActivityEndType.SystemTimeout]: 'Tiempo de espera del sistema',
  },
  device_types: {
    [DeviceType.Handset]: 'Teléfono móvil',
    [DeviceType.Tablet]: 'Tableta',
    [DeviceType.Desktop]: 'Computadora',
    [DeviceType.Laptop]: 'Ordenador portátil',
    [DeviceType.Unknown]: 'Desconocido',
  },
  camera_facing: {
    [CameraFacing.Front]: 'Cámara frontal',
    [CameraFacing.Rear]: 'Cámara trasera',
    [CameraFacing.External]: 'Cámara externa',
    [CameraFacing.Unknown]: 'Cámara desconocida',
  },
  reminder_response_values: {
    [ReminderResponseValue.Positive]: 'Sí / Está bien',
    [ReminderResponseValue.Negative]: 'No',
    [ReminderResponseValue.NotResponding]: 'No respondio',
  },
  invite_states: {
    [InviteState.Accepted]: 'Aceptada',
    [InviteState.Cancelled]: 'Cancelada',
    [InviteState.Open]: 'Abierta',
    [InviteState.Pending]: 'Pendiente',
    [InviteState.Refused]: 'Rechazada',
    [InviteState.RefusedBySenior]: 'Rechazada por el mayor',
    [InviteState.WaitingForSeniorApproval]: 'Pendiente de aprobación por parte del senior',
    [InviteState.AcceptedBySenior]: 'Aceptada por el mayor',
    [InviteState.Expired]: 'Caducada',
  },
  process_statuses: {
    [ProcessStatus.Done]: 'Concluido',
    [ProcessStatus.InProgress]: 'Em progreso',
    [ProcessStatus.ToDo]: 'Para hacer',
    [ProcessStatus.Undefined]: 'No definido',
    [ProcessStatus.Failed]: 'Fallado',
  },
  entity_types: {
    [EntityType.DayCareCenter]: 'Centro de día',
    [EntityType.Family]: 'Familia',
    [EntityType.Generic]: 'Genérico',
    [EntityType.HomeSupport]: 'Apoyo a domicilio',
    [EntityType.Institution]: 'Institución',
    [EntityType.InsuranceCompany]: 'Aseguradora',
    [EntityType.TechCompany]: 'Empresa de tecnología',
  },
  care_taks_not_done_reasons: {
    [CareTaskNotDoneReason.SeniorRefusal]: 'Rechazo de Senior',
    [CareTaskNotDoneReason.seniorAbsence]: 'Ausencia del usuario',
    [CareTaskNotDoneReason.LackOfMaterial]: 'Falta de material',
    [CareTaskNotDoneReason.SeniorWasNotFeelingWell]: 'Senior no se sentía bien',
    [CareTaskNotDoneReason.Other]: 'Otra razón (obs)',
  },
  time_units: {
    seconds: 's',
    minutes: 'min',
    hours: 'h',
  },
  entity_form: {
    exit_button: 'Cancelar',
    create_entity_title: 'Crear entidad',
    picture_input: 'Foto',
    name_input: 'Nombre de la institución',
    name_placeholder_input: 'Introduzca el nombre de la institución',
    state_input: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    description_input: 'Descripción',
    type_input: 'Tipo',
    language_input: 'Idioma',
    language_placeholder_input: 'Seleccione un idioma',
    region_input: 'Región',
    region_placeholder_input: 'Seleccione una región',
    time_zone_input: 'Zona horaria',
    time_zone_placeholder_input: 'Seleccione una zona horaria',
    email_input: 'Dirección de e-mail',
    email_placeholder_input: 'Añadir correo electrónico',
    short_name_input: 'Nombre corto',
    short_name_placeholder_input: 'Introduzca un nombre corto',
    anniversary_input: 'Fecha de fundación/aniversario',
    anniversary_placeholder_input: 'Seleccione una fecha',
    capacity_input: 'Capacidad',
    capacity_placeholder_input: 'Capacidad de acogida de la institución',
    address_input: 'Dirección',
    address_placeholder_input: 'Introduzca una dirección',
    website_input: 'Sitio web',
    website_placeholder_input: 'Añadir el sitio web',
    number_input: 'Código',
    landline_phone_number_input: 'Número de teléfono',
    mobile_phone_number_input: 'Número de móvil',
    phone_number_placeholder: 'Insira um número de telefone',
    services_name_input: 'Nombre de los servicios',
    services_name_placeholder_input: 'Nome dos serviços',
    services_phone_number_input: 'Número de teléfono de los servicios',
    services_image_url_input: 'Imagen de los servicios',
    services_image_info_message: 'Para obtener la mejor resolución, la imagen debe ser JPG/JPEG.',
    picture_info_message: 'Para obtener la mejor resolución, la imagen debe ser JPG/JPEG.',
    special_purpose_input: 'Finalidad de la entidad',
    special_purpose_placeholder_input: 'Seleccione un finalidad',
    social_responses_input: 'Servicios sociales',
    social_responses_placeholder_input: 'Seleccione las servicios sociales de su institución',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    deleting_button: 'Eliminando...',
    delete_button: 'Eliminar entidad',
    delete_dialog_title: '¿Eliminar entidad "{{name}}"?',
    delete_dialog_content: 'Al eliminar la entidad, se eliminará definitivamente del sistema.',
    delete_dialog_action_yes_button: 'Sí, eliminar',
    delete_dialog_action_no_button: 'Cancelar',
    router_prompt_create_title:
      '¿Está seguro de que desea salir sin guardar la creación de la entidad "{{name}}"?',
    router_prompt_create_message:
      'Al salir, la entidad no se guardará y se perderá toda la información introducida en ella.',
    router_prompt_edit_title:
      '¿Está seguro de que desea salir sin guardar los cambios en el perfil de la entidad "{{name}}"?',
    router_prompt_edit_message:
      'Al salir, perderá todos los cambios introducidos recientemente en el perfil de la entidad.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sí, salir',
    edit_cancel_toast: 'Se canceló la edición de la entidad "{{name}}".',
    create_cancel_toast: 'Se canceló la creación de la nueva entidad.',
    edit_special_purpose_not_allowed_tooltip_title:
      'No tiene permisos para indicar la finalidad de la entidad.',
    edit_is_active_not_allowed_tooltip_title:
      'No tiene permisos para indicar si la entidad está activa.',
    input_disabled_tooltip: 'No tiene permisos para editar la información de la entidad',
    not_specified: 'No especificado',
    alert: {
      short_name_input: 'Este es el nombre que se mostrará en el sistema.',
    },
    success: {
      save_success_button: '¡Guardado!',
      delete_success_button: '¡Eliminado!',
      create_success_message: '¡La entidad "{{name}}" se creó con éxito!',
      edit_success_message: 'Se guardaron los cambios en el perfil de la entidad.',
    },
    errors: {
      error_on_save_button: 'Error al guardar',
      error_on_delete_button: 'Error al eliminar',
      edit_form_validation_invalid_field:
        'Complete este campo para completar la edición de la entidad',
      create_form_validation_invalid_field:
        'Complete este campo para completar la creación de la entidad',
      form_validation_invalid_full_name_maximum_characters:
        'El nombre completo debe tener menos de {{max}} caracteres',
      form_validation_invalid_landline_phone_number:
        'El número de teléfono proporcionado no es válido',
      form_validation_invalid_mobile_phone_number: 'El número de móvil proporcionado no es válido',
      form_validation_not_mobile_phone_number:
        'El número proporcionado no coincide con un número de móvil',
      form_validation_invalid_services_phone_number:
        'El número de teléfono de los servicios proporcionado no es válido',
      form_validation_invalid_email: 'El dirección de e-mail proporcionado no es válido',
      form_validation_invalid_website: 'El sitio web proporcionado no es válido',
      form_validation_invalid_capacity: 'La capacidad indicada no es válida',
      form_validation_required_social_responses: 'Indique los servicios de la organización',
    },
  },
  professional_caregiver_invite_form: {
    exit_button: 'Salir',
    title: 'Invitar profesional',
    entity_input: 'Entidad',
    name_input: 'Nombre',
    contact_method_input: 'Medio de contacto',
    email_input: 'Dirección de e-mail',
    mobile_phone_number_input: 'Número de móvil',
    affiliation_type_input: 'Tipo de afiliación',
    save_dialog_title: '¿Enviar invitación a {{name}}?',
    save_dialog_message:
      'Al enviar, {{name}} recibirá una invitación para ser profesional y tendrá acceso a cierta información de la entidad {{entity}}.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Enviar',
    router_prompt_title: '¿Está seguro de que desea salir sin enviar la invitación?',
    router_prompt_message: 'Al salir, perderá toda la información introducida en la invitación.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sí, salir',
    cancel_toast: 'La invitación ha sido cancelada.',
    save_button: 'Enviar invitación',
    saving_button: 'Enviando...',
    success: {
      save_success_button: '¡Enviado!',
      success_message: 'La invitación ha sido enviada a {{name}}.',
    },
    errors: {
      error_on_save_button: 'Error al enviar',
      form_validation_invalid_field: 'Complete este campo para completar la invitación',
      form_validation_invalid_mobile_phone_number: 'El número de móvil proporcionado no es válido',
      form_validation_not_mobile_phone_number:
        'El número proporcionado no coincide con un número de móvil',
      form_validation_invalid_email: 'El dirección de e-mail proporcionado no es válido',
    },
  },
  invite_contact_methods: {
    [InviteContactMethod.Email]: 'Dirección de e-mail',
    [InviteContactMethod.MobilePhoneNumber]: 'Número de móvil',
  },
  search_select_input: {
    no_options: 'Sin opciones',
    loading: 'Subiendo...',
    more_button: 'Más',
    items_showing_information: 'Mostrando {{value}} de {{total}} elementos',
  },
  user_card_form: {
    exit_button: 'Salir',
    create_user_card_title: 'Crear tarjeta',
    edit_user_card_title: 'Editar tarjeta',
    view_user_card_title: 'Detalles de la tarjeta',
    name_input: 'Nombre',
    state_input: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    tag_input: 'Número de identificación',
    entity_input: 'Entidad',
    user_input: 'Usuario',
    user_info_message:
      'Este campo solo se puede rellenar si previamente se ha seleccionado una entidad.',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    deleting_button: 'Eliminando...',
    delete_button: 'Eliminar tarjeta',
    delete_dialog_title: '¿Está seguro de que desea eliminar tarjeta "{{name}}"?',
    delete_dialog_content: 'Al eliminar la tarjeta, se eliminará definitivamente del sistema.',
    create_dialog_title: '¿Guardar la creación de la tarjeta "{{name}}"?',
    create_dialog_message: 'Al guardar, la nueva tarjeta se agregará al sistema.',
    edit_dialog_title: '¿Guardar cambios en la tarjeta "{{name}}"?',
    edit_dialog_message:
      'Al guardar, la tarjeta se actualizará con la información recién ingresada.',
    router_prompt_create_title:
      '¿Está seguro de que desea salir sin guardar la creación de la tarjeta "{{name}}"?',
    router_prompt_create_message:
      'Al salir, la tarjeta no se guardará y se perderá toda la información introducida en ella.',
    router_prompt_edit_title:
      '¿Está seguro de que desea salir sin guardar los cambios en la tarjeta "{{name}}"?',
    router_prompt_edit_message:
      'Al salir, perderá todos los cambios introducidos recientemente en la tarjeta.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sí, salir',
    edit_cancel_toast: 'Se canceló la edición de la tarjeta "{{name}}".',
    create_cancel_toast: 'Se canceló la creación de la nueva tarjeta.',
    disabled_tooltip: 'No tiene permisos para editar la tarjeta',
    success: {
      save_success_button: '¡Guardado!',
      delete_success_button: '¡Eliminado!',
      create_success_message: '¡La tarjeta "{{name}}" se creó con éxito!',
      edit_success_message: 'Se guardaron los cambios en la tarjeta.',
      senior_inactive_success: 'El mayor {{name}} fue marcado como inactivo.',
    },
    errors: {
      error_on_save_button: 'Error al guardar',
      error_on_delete_button: 'Error al eliminar',
      edit_form_validation_invalid_field:
        'Complete este campo para completar la edición de la tarjeta',
      create_form_validation_invalid_field:
        'Complete este campo para completar la creación de la tarjeta',
      error_on_delete_user: 'Error al eliminar el usuario',
      error_on_marking_senior_as_inactive: 'Error al marcar el mayor como inactivo',
    },
  },
  device_form: {
    exit_button: 'Salir',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    create_device_title: 'Crear dispositivo',
    type_input: 'Tipo',
    state_input: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    number_input: 'Número',
    entity_input: 'Entidad',
    user_input: 'Usuario',
    language_input: 'Idioma',
    language_placeholder_input: 'Seleccione un idioma',
    sign_in_type_input: 'Tipo de inicio de sesión',
    sign_in_type_placeholder_input: 'Seleccione un tipo',
    mac_address_input: 'Dirección MAC',
    serial_number_input: 'Número de série',
    user_info_message:
      'Este campo solo se puede rellenar si previamente se ha seleccionado una entidad.',
    edit_type_not_allowed_tooltip_title: 'No tiene permisos para indicar el tipo de dispositivo.',
    edit_serial_number_not_allowed_tooltip_title:
      'No tiene permisos para indicar el número de série.',
    edit_mac_address_not_allowed_tooltip_title: 'No tiene permisos para indicar la dirección MAC.',
    edit_number_not_allowed_tooltip_title: 'No tiene permisos para indicar el número.',
    edit_sign_in_type_not_allowed_tooltip_title:
      'No tiene permisos para indicar el tipo de inicio de sesión.',
    input_disabled_tooltip: 'No tiene permisos para editar la información del dispositivo',
    deleting_button: 'Eliminando...',
    delete_button: 'Eliminar dispositivo',
    delete_dialog_title:
      '¿Estás seguro de que quieres eliminar dispositivo con identificador "{{name}}"?',
    delete_dialog_content: 'Al eliminar el dispositivo, se eliminará definitivamente del sistema.',
    create_dialog_title: '¿Guardar la creación del dispositivo con identificador "{{name}}"?',
    create_dialog_message: 'Al guardar, el nuevo dispositivo se agregará al sistema.',
    edit_dialog_title: '¿Guardar el cambios en el dispositivo con identificador "{{name}}"?',
    edit_dialog_message:
      'Al guardar, el dispositivo se actualizará con la información recién ingresada.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Guardar',
    router_prompt_create_title:
      '¿Estás seguro de que quieres salir sin guardar creación del dispositivo con identificador "{{name}}"?',
    router_prompt_create_message:
      'Al salir, el dispositivo no se guardará y se perderá toda la información introducida en el.',
    router_prompt_edit_title:
      '¿Está seguro de que desea salir sin guardar los cambios en el dispositivo con identificador "{{name}}"?',
    router_prompt_edit_message:
      'Al salir, perderá todos los cambios introducidos recientemente en el dispositivo.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sí, salir',
    edit_cancel_toast: 'Se canceló la edición del dispositivo con identificador "{{name}}".',
    create_cancel_toast: 'Se canceló la creación del nuevo dispositivo.',
    success: {
      create_message: '¡El dispositivo con identificador "{{name}}" se creó con éxito!',
      edit_message: 'Se guardaron los cambios en el dispositivo con identificador "{{name}}".',
      deleted_message: '¡El dispositivo con identificador "{{name}}" se eliminó.',
    },
    errors: {
      create_message: 'No se pudo crear el dispositivo con identificador "{{name}}.',
      edit_message: 'No se pudo guardar los cambios en el dispositivo con identificador "{{name}}.',
      edit_form_validation_invalid_field:
        'Complete este campo para completar la edición del dispositivo',
      create_form_validation_invalid_field:
        'Complete este campo para completar la creación del dispositivo',
      deleted_message: 'No se pudo eliminar el dispositivo con identificador "{{name}}".',
    },
    isInDebugMode_input: 'Modo de depuración',
    isInDebugMode_placeholder_input: 'No definido',
  },
  user_sign_in_types: {
    [UserSignInType.Card]: 'Tarjeta',
    [UserSignInType.Quick]: 'Rápido',
    [UserSignInType.Code]: 'Código',
  },
  entity_special_purposes: {
    [EntitySpecialPurpose.Commercial]: 'Comercial',
    [EntitySpecialPurpose.Demonstration]: 'Demostraciones',
    [EntitySpecialPurpose.Research]: 'Investigación',
    [EntitySpecialPurpose.Testing]: 'Pruebas',
  },
  entity_statistics_dashboard: {
    title: 'Estadísticas',
    refresh_button: 'Actualizar',
    text_charts: {
      number_of_active_seniors_title: 'Mayores',
      number_of_active_seniors_subtitle: 'Mayores activos',
      number_of_active_professional_caregivers_title: 'Cuidadores profesionales',
      number_of_active_professional_caregivers_subtitle:
        'Cuidadores activos afiliados a la entidad',
      number_of_active_informal_caregivers_title: 'Cuidadores informales',
      number_of_active_informal_caregivers_subtitle:
        'Cuidadores activos com relaciones con mayores',
      number_of_pending_invites_title: 'Invitaciones pendientes',
      number_of_pending_invites_subtitle: 'Invitaciones en espera de respuesta',
      number_of_calls_title: 'Llamadas',
      number_of_calls_subtitle: 'Dración mayor o igual a 5 segundos',
      number_of_outgoing_calls_title: 'Llamadas realizadas',
      number_of_outgoing_calls_subtitle: 'Dración mayor o igual a 5 segundos',
      number_of_incoming_calls_title: 'Llamadas recibidas',
      number_of_incoming_calls_subtitle: 'Dración mayor o igual a 5 segundos',
    },
    table_charts: {
      number_of_active_devices_title: 'Número de dispositivos activos',
      number_of_active_digital_inclusion_devices_title:
        'Número de dispositivos de inclusión digital',
      number_of_active_quick_access_devices_title: 'Número de dispositivos de acceso rápido',
      number_of_active_devices_device_type_column: 'Dispositivo',
      number_of_active_devices_total_column: 'Total',
      top_users_title: 'Top usuarios',
      top_users_subtitle: 'Horas en actividades (duración mayor o igual a 5 segundos)',
      top_users_user_column: 'Usuarios',
      top_users_total_usage_time_column: 'Tiempo de uso',
      top_users_usage_time_column_unit_label: 'horas',
    },
    area_charts: {
      general_system_usage_title: 'Uso general del sistema',
      general_system_usage_subtitle: 'Horas en actividades (duración mayor o igual a 5 segundos)',
      general_system_usage_x_label: 'Fecha',
      general_system_usage_y_label: 'Duración',
      general_system_usage_y_unit_label: 'horas',
    },
    bar_charts: {
      calls_evolution_title: 'Evolución de llamadas',
      calls_evolution_subtitle: 'Duración mayor o igual a 5 segundos',
      calls_evolution_x_label: 'Fecha',
      calls_evolution_y_label: 'Llamadas',
    },
    pie_charts: {
      most_used_applications_title: 'Aplicaciones mas usadas',
      most_used_applications_subtitle: 'Horas en actividades (duración mayor o igual a 5 segundos)',
      most_used_applications_x_label: 'Aplicación',
      most_used_applications_y_label: 'Fecha',
      most_used_applications_y_unit_label: 'horas',
    },
    stack_charts: {
      applications_usage_title: 'Uso por aplicación',
      applications_usage_subtitle: 'Horas en actividades (duración mayor o igual a 5 segundos)',
      applications_usage_singular_stack_label: 'Aplicación',
      applications_usage_plural_stack_label: 'Aplicaciones',
      applications_usage_x_label: 'Fecha',
      applications_usage_y_label: 'Duración',
      applications_usage_y_unit_label: 'horas',
    },
  },
  senior_statistics_dashboard: {
    title: 'Estadísticas',
    refresh_button: 'Actualizar',
    statistics_per_application_title: 'Estadísticas por aplicación',
    statistics_per_application_application_label: 'Aplicación',
    statistics_per_application_entity_label: 'Entidad',
    statistics_per_application_entity_placeholder: 'Seleccione una entidad',
    statistics_per_application_info_message:
      'Seleccione una entidad para seleccionar una aplicación y consultar sus estadísticas.',
    text_charts: {
      number_of_informal_caregivers_title: 'Cuidadores informales',
      number_of_informal_caregivers_subtitle: 'Relaciones con mayor',
      number_of_pending_invites_title: 'Invitaciones pendientes',
      number_of_pending_invites_subtitle: 'Invitaciones en espera de respuesta',
      number_of_calls_title: 'Llamadas',
      number_of_calls_subtitle: 'duración mayor o igual a 5 segundos',
      number_of_outgoing_calls_title: 'Llamadas realizadas',
      number_of_outgoing_calls_subtitle: 'duración mayor o igual a 5 segundos',
      number_of_incoming_calls_title: 'Llamadas recibidas',
      number_of_incoming_calls_subtitle: 'duración mayor o igual a 5 segundos',
      average_daily_usage_last_title: 'Uso diario promedio',
      average_daily_usage_subtitle: 'Promedio de los últimos 30 días',
      average_daily_usage_unit_label: 'horas',
      number_of_media_received_title: 'Multimedia recibidas',
      number_of_media_received_subtitle: 'Fotos y vídeos',
      number_of_video_messages_received_title: 'Mensajes de vídeo recibidos',
      number_of_video_messages_sent_title: 'Mensajes de vídeo enviados',
      number_of_photos_sent_title: 'Fotos enviadas',
      number_of_drawings_sent_title: 'Dibujos enviados',
      number_of_paintings_sent_title: 'Pinturas enviadas',
      last_access_date_time_title: 'Último acceso',
    },
    table_charts: {
      top_preferences_title: 'Top preferencias',
      top_preferences_subtitle: 'Horas en actividades (duración mayor o igual a 5 segundos)',
      top_preferences_application_column: 'Aplicación',
      top_preferences_total_usage_time_column: 'Tiempo de uso',
      top_preferences_total_usage_time_column_unit_label: 'horas',
    },
    area_charts: {
      general_system_usage_title: 'Uso general del sistema',
      general_system_usage_subtitle: 'Horas en actividades (duración mayor o igual a 5 segundos)',
      general_system_usage_x_label: 'Fecha',
      general_system_usage_y_label: 'Duración',
      general_system_usage_y_unit_label: 'horas',
      application_usage_title: 'Uso "{{name}}"',
      application_usage_subtitle: 'Horas en actividades (duración mayor o igual a 5 segundos)',
      application_usage_x_label: 'Fecha',
      application_usage_y_label: 'Duración',
      application_usage_y_unit_label: 'horas',
    },
    pie_charts: {
      most_used_applications_title: 'Aplicaciones mas usadas',
      most_used_applications_subtitle: 'Horas en actividades (duración mayor o igual a 5 segundos)',
      most_used_applications_x_label: 'Aplicación',
      most_used_applications_y_label: 'Fecha',
      most_used_applications_y_unit_label: 'horas',
    },
    stack_charts: {
      applications_usage_title: 'Uso por aplicación',
      applications_usage_subtitle: 'Horas en actividades (duración mayor o igual a 5 segundos)',
      applications_usage_singular_stack_label: 'Aplicación',
      applications_usage_plural_stack_label: 'Aplicaciones',
      applications_usage_x_label: 'Fecha',
      applications_usage_y_label: 'Duración',
      applications_usage_y_unit_label: 'horas',
    },
    line_charts: {
      application_evolution_title: 'Evolución "{{name}}"',
      application_evolution_subtitle: 'Cambios de nivel',
      application_evolution_x_label: 'Fecha',
      application_evolution_y_label: 'Nivel',
    },
  },
  charts: {
    no_data: 'No hay datos.',
  },
  media_gallery: {
    refresh: 'Actualizar',
    no_more_data: 'No hay más datos',
    without_data: 'No hay datos',
  },
  application_category_form: {
    exit_button: 'Salir',
    create_application_category_title: 'Crear categoría',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    default_name_input: 'Nombre',
    default_description_input: 'Descripción',
    enabled_state_color_input: 'Color del estado activado',
    pressed_state_color_input: 'Color del estado prensado',
    name_translations_input: 'Traducciones para el nombre',
    description_translations_input: 'Traducciones para la descripción',
    deleting_button: 'Eliminando...',
    delete_button: 'Eliminar categoría',
    delete_dialog_title: '¿Eliminar categoría "{{name}}"?',
    delete_dialog_content: 'Al eliminar la categoría, se eliminará definitivamente del sistema.',
    delete_dialog_action_yes_button: 'Sí, eliminar',
    delete_dialog_action_no_button: 'Cancelar',
    router_prompt_create_title:
      '¿Está seguro de que desea salir sin guardar la creación de la categoría "{{name}}"?',
    router_prompt_create_message:
      'Al salir, la categoría no se guardará y se perderá toda la información introducida en ella.',
    router_prompt_edit_title:
      '¿Está seguro de que desea salir sin guardar los cambios en la categoría "{{name}}"?',
    router_prompt_edit_message:
      'Al salir, perderá todos los cambios introducidos recientemente en la categoría.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sí, salir',
    edit_cancel_toast: 'Se canceló la edición de la categoría "{{name}}".',
    create_cancel_toast: 'Se canceló la creación de la nueva categoría.',
    create_dialog_title: '¿Guardar la creación de la categoría "{{name}}"?',
    create_dialog_message: 'Al guardar, la nueva categoría se agregará al sistema.',
    edit_dialog_title: '¿Guardar cambios en la categoría "{{name}}"?',
    edit_dialog_message:
      'Al guardar, la categoría se actualizará con la información recién ingresada.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Guardar',
    success: {
      save_success_button: '¡Guardado!',
      delete_success_button: '¡Eliminado!',
      create_success_message: '¡La categoría "{{name}}" se creó con éxito!',
      edit_success_message: 'Se guardaron los cambios en la categoría.',
    },
    errors: {
      error_on_save_button: 'Error al guardar',
      error_on_delete_button: 'Error al eliminar',
      edit_form_validation_invalid_field:
        'Complete este campo para completar la edición de la categoría',
      create_form_validation_invalid_field:
        'Complete este campo para completar la creación de la categoría',
      form_validation_invalid_color: 'El color indicado no es válido',
    },
  },
  playlist_types: {
    [PlaylistType.Karaoke]: 'Karaoke',
    [PlaylistType.Movies]: 'Vídeos',
    [PlaylistType.Music]: 'Música',
  },
  playlists: {
    system_administrator: {
      root: {
        title: 'Listas de reproducción',
        search_input_placeholder: 'Buscar listas',
        type_filter: 'Tipo',
        name_header: 'Nombre',
        no_playlists: 'No hay listas de reproducción.',
        add_button: 'Añadir lista de reproducción',
        deleted_playlist_dialog_title: 'Lista de reproducción "{{name}}" eliminada',
        deleted_playlist_dialog_message: 'La lista de reproducción ya no existe en el sistema.',
        deleted_playlist_dialog_ok_button: 'Ok',
      },
      edit: {
        tabs: {details: 'Detalles', items: 'Elementos', entities: 'Entidades'},
        items: {
          search_input_placeholder: 'Buscar elementos',
          title_header: 'Título',
          no_items: 'No hay elementos en la listas de reproducción.',
          add_button: 'Añadir elemento',
          deleted_playlist_item_dialog_title:
            'Elemento "{{name}}" eliminado de la lista de reproducción',
          deleted_playlist_item_dialog_message: 'El elemento ya no existe en el sistema.',
          deleted_playlist_item_dialog_ok_button: 'Ok',
        },
        entities: {
          search_input_placeholder: 'Buscar entidades',
          name_header: 'Nombre',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_entities: 'No hay entidades.',
          enable_playlist_dialog_title: '¿Activar la lista de reproducción "{{name}}"?',
          enable_playlist_dialog_message:
            'Al activar la lista de reproducción, estará disponible para la entidad {{name}}.',
          enable_playlist_dialog_action_yes_button: 'Sí, activar',
          enable_playlist_dialog_action_no_button: 'Cancelar',
          disable_playlist_dialog_title: '¿Desactivar la lista de reproducción "{{name}}"?',
          disable_playlist_dialog_message:
            'Al desactivar la lista de reproducción, ya no estará disponible para la entidad {{name}}.',
          disable_playlist_dialog_action_yes_button: 'Sí, desactivar',
          disable_playlist_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_playlist_error_message:
              'Se ha producido un error. No ha sido posible activar la lista de reproducción.',
            disable_playlist_error_message:
              'Se ha producido un error. No ha sido posible desactivar la lista de reproducción.',
            update_state_playlist_error_message:
              'Se ha producido un error. No ha sido posible actualizar el estado de la lista de reproducción.',
          },
        },
        errors: {
          playlist_does_not_exist_error:
            'No se pudo obtener la lista de reproducción al que intentó acceder.',
        },
      },
      playlist_item: {
        tabs: {details: 'Detalles', entities: 'Entidades'},
        entities: {
          search_input_placeholder: 'Buscar entidades',
          name_header: 'Nombre',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_entities: 'No hay entidades.',
          enable_item_dialog_title: '¿Activar el elemento "{{name}}" en la lista de reproducción?',
          enable_item_dialog_message:
            'Al activar el elemento en la lista de reproducción, estará disponible para la entidad {{name}}.',
          enable_item_dialog_action_yes_button: 'Sí, activar',
          enable_item_dialog_action_no_button: 'Cancelar',
          disable_item_dialog_title:
            '¿Desactivar el elemento "{{name}}" en la lista de reproducción?',
          disable_item_dialog_message:
            'Al desactivar el elemento en la lista de reproducción, ya no estará disponible para la entidad {{name}}.',
          disable_item_dialog_action_yes_button: 'Sí, desactivar',
          disable_item_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_item_error_message:
              'Se ha producido un error. No ha sido posible activar el elemento en la lista de reproducción.',
            disable_item_error_message:
              'Se ha producido un error. No ha sido posible desactivar el elemento en la lista de reproducción.',
            update_state_item_error_message:
              'Se ha producido un error. No ha sido posible actualizar el estado del elemento en la lista de reproducción.',
          },
        },
        errors: {
          item_does_not_exist_error: 'No se pudo obtener el elemento al que intentó acceder.',
        },
      },
    },
    professional_caregiver: {
      root: {
        search_input_placeholder: 'Buscar listas',
        type_filter: 'Tipo',
        name_header: 'Nombre',
        state_header: 'Estado',
        state_value_true: 'Activo',
        state_value_false: 'Inactivo',
        no_playlists: 'No hay listas de reproducción.',
        enable_playlist_dialog_title: '¿Activar la lista de reproducción "{{name}}"?',
        enable_playlist_dialog_message:
          'Al activar la lista de reproducción, estará disponible para todos los usuarios de la entidad {{name}}.',
        enable_playlist_dialog_action_yes_button: 'Sí, activar',
        enable_playlist_dialog_action_no_button: 'Cancelar',
        disable_playlist_dialog_title: '¿Desactivar la lista de reproducción "{{name}}"?',
        disable_playlist_dialog_message:
          'Al desactivar la lista de reproducción, ya no estará disponible para todos los usuarios de la entidad {{name}}.',
        disable_playlist_dialog_action_yes_button: 'Sí, desactivar',
        disable_playlist_dialog_action_no_button: 'Cancelar',
        errors: {
          enable_playlist_error_message:
            'Se ha producido un error. No ha sido posible activar la lista de reproducción.',
          disable_playlist_error_message:
            'Se ha producido un error. No ha sido posible desactivar la lista de reproducción.',
          update_state_playlist_error_message:
            'Se ha producido un error. No ha sido posible actualizar el estado de la lista de reproducción.',
        },
      },
      edit: {
        tabs: {
          items: 'Elementos',
          users: 'Usuarios',
          details: 'Detalles',
        },
        items: {
          search_input_placeholder: 'Buscar elementos',
          title_header: 'Título',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_items: 'No hay elementos en la listas de reproducción.',
          enable_item_dialog_title: '¿Activar el elemento "{{name}}" en la lista de reproducción?',
          enable_item_dialog_message:
            'Al activar el elemento en la lista de reproducción, estará disponible para todos los usuarios de la entidad {{name}}.',
          enable_item_dialog_action_yes_button: 'Sí, activar',
          enable_item_dialog_action_no_button: 'Cancelar',
          disable_item_dialog_title:
            '¿Desactivar el elemento "{{name}}" en la lista de reproducción?',
          disable_item_dialog_message:
            'Al desactivar el elemento en la lista de reproducción, ya no estará disponible para todos los usuarios de la entidad {{name}}.',
          disable_item_dialog_action_yes_button: 'Sí, desactivar',
          disable_item_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_item_error_message:
              'Se ha producido un error. No ha sido posible activar el elemento en la lista de reproducción.',
            disable_item_error_message:
              'Se ha producido un error. No ha sido posible desactivar el elemento en la lista de reproducción.',
            update_state_item_error_message:
              'Se ha producido un error. No ha sido posible actualizar el estado del elemento en la lista de reproducción.',
          },
        },
        users: {
          search_input_placeholder: 'Buscar usuarios',
          name_header: 'Nombre',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_users: 'No hay usuarios.',
          enable_playlist_dialog_title: '¿Activar la lista de reproducción "{{name}}"?',
          enable_playlist_dialog_message:
            'Al activar la lista de reproducción, estará disponible para {{name}}.',
          enable_playlist_dialog_action_yes_button: 'Sí, activar',
          enable_playlist_dialog_action_no_button: 'Cancelar',
          disable_playlist_dialog_title: '¿Desactivar la lista de reproducción "{{name}}"?',
          disable_playlist_dialog_message:
            'Al desactivar la lista de reproducción, ya no estará disponible para {{name}}.',
          disable_playlist_dialog_action_yes_button: 'Sí, desactivar',
          disable_playlist_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_playlist_error_message:
              'Se ha producido un error. No ha sido posible activar la lista de reproducción.',
            disable_playlist_error_message:
              'Se ha producido un error. No ha sido posible desactivar la lista de reproducción.',
            update_state_playlist_error_message:
              'Se ha producido un error. No ha sido posible actualizar el estado de la lista de reproducción.',
          },
        },
        errors: {
          playlist_does_not_exist_error:
            'No se pudo obtener la lista de reproducción al que intentó acceder.',
        },
      },
      playlist_item: {
        tabs: {
          users: 'Usuarios',
          details: 'Detalles',
        },
        users: {
          search_input_placeholder: 'Buscar usuarios',
          name_header: 'Nombre',
          type_header: 'Tipo',
          state_header: 'Estado',
          state_value_true: 'Activo',
          state_value_false: 'Inactivo',
          no_users: 'No hay usuarios.',
          enable_item_dialog_title: '¿Activar el elemento "{{name}}" en la lista de reproducción?',
          enable_item_dialog_message:
            'Al activar el elemento en la lista de reproducción, estará disponible para {{name}}.',
          enable_item_dialog_action_yes_button: 'Sí, activar',
          enable_item_dialog_action_no_button: 'Cancelar',
          disable_item_dialog_title:
            '¿Desactivar el elemento "{{name}}" en la lista de reproducción?',
          disable_item_dialog_message:
            'Al desactivar el elemento en la lista de reproducción, ya no estará disponible para {{name}}.',
          disable_item_dialog_action_yes_button: 'Sí, desactivar',
          disable_item_dialog_action_no_button: 'Cancelar',
          errors: {
            enable_item_error_message:
              'Se ha producido un error. No ha sido posible activar el elemento en la lista de reproducción.',
            disable_item_error_message:
              'Se ha producido un error. No ha sido posible desactivar el elemento en la lista de reproducción.',
            update_state_item_error_message:
              'Se ha producido un error. No ha sido posible actualizar el estado del elemento en la lista de reproducción.',
          },
        },
        errors: {
          item_does_not_exist_error: 'No se pudo obtener el elemento al que intentó acceder.',
        },
      },
    },
  },
  user_playlists: {
    search_input_placeholder: 'Buscar listas',
    type_filter: 'Tipo',
    entity_filter: 'Entidad',
    name_header: 'Nombre',
    state_header: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    state_disabled_tooltip: 'No tiene permisos para editar el estado de la lista de reproducción',
    no_playlists: 'No hay listas de reproducción.',
    no_affiliations_info_message:
      'El usuario no tiene afiliación con ninguna entidad. Por lo tanto, no es posible consultar las listas de reproducción del usuario.',
    enable_playlist_dialog_title:
      '¿Está seguro de que desea activar la lista de reproducción "{{name}}"?',
    enable_playlist_dialog_message:
      'Al activar la lista de reproducción, estará disponible para {{name}}.',
    enable_playlist_for_entity_dialog_message:
      'Al activar la lista de reproducción, estará disponible para {{name}} en la entidad {{entity}}.',
    enable_playlist_dialog_action_yes_button: 'Sí, activar',
    enable_playlist_dialog_action_no_button: 'Cancelar',
    disable_playlist_dialog_title:
      '¿Está seguro de que desea desactivar la lista de reproducción "{{name}}"?',
    disable_playlist_dialog_message:
      'Al desactivar la lista de reproducción, ya no estará disponible para {{name}}.',
    disable_playlist_for_entity_dialog_message:
      'Al desactivar la lista de reproducción, ya no estará disponible para {{name}} en la entidad {{entity}}.',
    disable_playlist_dialog_action_yes_button: 'Sí, desactivar',
    disable_playlist_dialog_action_no_button: 'Cancelar',
    errors: {
      enable_playlist_error_message:
        'Se ha producido un error. No ha sido posible activar la lista de reproducción.',
      disable_playlist_error_message:
        'Se ha producido un error. No ha sido posible desactivar la lista de reproducción.',
      update_state_playlist_error_message:
        'Se ha producido un error. No ha sido posible actualizar el estado de la lista de reproducción.',
    },
  },
  time_lapse_types: {
    [TimeLapseType.Custom]: 'Personalizado',
    [TimeLapseType.Last180Days]: 'Últimos 180 días',
    [TimeLapseType.Last30Days]: 'Últimos 30 días',
    [TimeLapseType.Last365Days]: 'Últimos 365 días',
    [TimeLapseType.LastMonth]: 'Mes pasado',
    [TimeLapseType.LastWeek]: 'Semana pasada',
    [TimeLapseType.ThisMonth]: 'Este mes',
    [TimeLapseType.ThisWeek]: 'Esta semana',
  },
  time_lapse: {
    dialog_title: 'Elige un intervalo de tiempo',
    range_label: 'Tipo de rango',
    start_text: 'Comienzo',
    end_text: 'Fin',
    to_text: 'hasta',
    cancel_button: 'Cancelar',
    confirm_button: 'Confirmar',
  },
  device_health: {
    no_data: 'No hay datos.',
    last_recording: 'Último registro',
    basic_information_section_title: 'Información básica',
    wifi_section_title: 'Wi-Fi',
    networks_wifi_subsection_title: 'Redes disponibles',
    current_wifi_subsection_title: 'Redes conectadas',
    mobile_data_section_title: 'Datos móviles',
    graphics_section_title: 'Gráficos',
    controllers_graphics_subsection_title: 'Controladores',
    displays_graphics_subsection_title: 'Pantallas',
    cache_images_section_title: 'Caché (imágenes)',
    creation_date_label: 'Fecha de creación',
    expiration_date_label: 'Fecha de expiración',
    system_section_title: 'Sistema',
    battery_section_title: 'Batería',
    disks_section_title: 'Discos',
    operating_system_section_title: 'Sistema operativo',
    cpu_section_title: 'CPU',
    memory_section_title: 'Memoria',
    applications_section_title: 'Aplicaciones',
    versions_section_title: 'Versiones',
    request_tracking_section_title: 'Seguimiento',
    entity_label: 'Entidad',
    user_label: 'Usuario',
    data_usage_label: 'Datos consumidos',
    ssid_label: 'SSID',
    quality_label: 'Calidad',
    ip_address_label: 'Dirección IP',
    is_data_usage_enabled_label: '¿Consumo de datos móviles habilitado?',
    carrier_label: 'Operador',
    network_type_label: 'Tipo de red',
    dialer_label: 'Marcador',
    signal_strength_label: 'Fuerza de señal',
    signal_strength_level_label: 'Nivel de fuerza de señal',
    signal_strength_no: 'Sin señal',
    signal_strength_weak: 'Débil',
    signal_strength_fair: 'Regular',
    signal_strength_good: 'Buena',
    signal_strength_strong: 'Fuerte',
    brand_label: 'Marca',
    model_label: 'Modelo',
    is_main_display_label: '¿Es la pantalla principal?',
    width_label: 'Ancho',
    height_label: 'Altura',
    current_width_label: 'Ancho actual',
    current_height_label: 'Altura actual',
    current_scale_factor_label: 'Factor de escala',
    sku_label: 'SKU',
    serial_number_label: 'Número de série',
    version_label: 'Versión',
    has_battery_label: '¿Tienes una batería?',
    is_battery_charging_label: '¿Cargando?',
    is_battery_connected_to_power_label: '¿Está enchufado?',
    battery_level_label: 'Estado',
    size_label: 'Total',
    used_disks_label: 'Usado',
    available_label: 'Disponible',
    mount_label: 'Punto de reunión',
    name_label: 'Nombre',
    distribution_label: 'Distribución',
    release_label: 'Lanzamiento',
    build_label: 'Compilación',
    web_view_version_label: 'Versión WebView',
    cores_label: 'Numero de nucleos',
    used_memory_label: 'Usada',
    country_label: 'País',
    region_label: 'Región',
    city_label: 'Ciudad',
    city_longitude_label: 'Longitud',
    city_latitude_label: 'Latitud',
    language_label: 'Idioma',
    user_ip_address_label: 'Dirección IP del usuario',
    yes_label: 'Sí',
    no_label: 'No',
    gb_unit_label: 'GB',
    mb_unit_label: 'MB',
    px_unit_label: 'px',
    agent_label: 'Agente',
    chrome_label: 'Chrome',
    electron_label: 'Electron',
    launcher_binary_label: 'Binario do Launcher',
    launcher_frontend_label: 'Frontend do Launcher',
  },
  operative_system_names: {
    [OperativeSystemName.Android]: 'Android',
    [OperativeSystemName.Linux]: 'Linux',
    [OperativeSystemName.MacOS]: 'MacOS',
    [OperativeSystemName.Other]: 'Otro',
    [OperativeSystemName.Windows]: 'Windows',
    [OperativeSystemName.iOS]: 'iOS',
  },
  mobile_network_types: {
    [MobileNetworkType.Unspecified]: 'Otro',
    [MobileNetworkType._1G]: '1G',
    [MobileNetworkType._2G]: '2G',
    [MobileNetworkType._3G]: '3G',
    [MobileNetworkType._4G]: '4G',
    [MobileNetworkType._5G]: '5G',
  },
  home: {
    refreshing: 'Actualizando...',
    release_to_refresh: 'Suelta para actualizar',
    pull_to_refresh: 'Desliza para actualizar',
    none: {
      root: {
        no_associations_message_title:
          'Su cuenta de acceso a la plataforma {{brand}} no está asociada con ningún mayor o entidad en este momento.',
        no_associations_message_subtitle:
          'Por favor, solicite una invitación a otros cuidadores o entre en contacto con el servicio de Atención al Cliente.',
      },
    },
  },
  localized_string_input: {
    cancel_button: 'Cancelar',
    confirm_button: 'Confirmar',
    has_zero_translations_tooltip_message:
      'No hay traducciones definidas de un total de {{total}} posibles.',
    has_one_translation_tooltip_message:
      'Hay una traducción definida de un total de {{total}} posibles.',
    has_many_translations_tooltip_message:
      'Hay {{count}} traducciones definidas de un total de {{total}} posibles.',
  },
  user_playlist_items: {
    title: 'Lista de reproducción "{{name}}"',
    search_input_placeholder: 'Buscar elementos',
    title_header: 'Título',
    state_header: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    state_disabled_tooltip: 'No tiene permisos para editar el estado del elemento',
    no_items: 'No hay elementos en la listas de reproducción.',
    enable_item_dialog_title:
      '¿Está seguro de que desea activar el elemento "{{name}}" en la lista de reproducción?',
    enable_item_dialog_message:
      'Al activar el elemento en la lista de reproducción, estará disponible para {{name}}.',
    enable_item_dialog_action_yes_button: 'Sí, activar',
    enable_item_dialog_action_no_button: 'Cancelar',
    disable_item_dialog_title:
      '¿Está seguro de que desea desactivar el elemento "{{name}}" en la lista de reproducción?',
    disable_item_dialog_message:
      'Al desactivar el elemento en la lista de reproducción, ya no estará disponible para {{name}}.',
    disable_item_dialog_action_yes_button: 'Sí, desactivar',
    disable_item_dialog_action_no_button: 'Cancelar',
    errors: {
      enable_item_error_message:
        'Se ha producido un error. No ha sido posible activar el elemento en la lista de reproducción.',
      disable_item_error_message:
        'Se ha producido un error. No ha sido posible desactivar el elemento en la lista de reproducción.',
      update_state_item_error_message:
        'Se ha producido un error. No ha sido posible actualizar el estado del elemento en la lista de reproducción.',
      playlist_does_not_exist_error:
        'No se pudo obtener la lista de reproducción al que intentó acceder.',
    },
  },
  playlist_form: {
    exit_button: 'Salir',
    create_playlist_title: 'Crear lista de reproducción',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    default_name_input: 'Nombre',
    name_translations_input: 'Traducciones para el nombre',
    default_description_input: 'Descripción',
    description_translations_input: 'Traducciones para la descripción',
    enabled_state_color_input: 'Color del estado activado',
    pressed_state_color_input: 'Color del estado prensado',
    state_input: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    type_input: 'Tipo',
    active_by_default_for_all_entities_input: '¿Activo por defecto para todas las entidades?',
    active_by_default_for_all_entities_placeholder_input: 'Seleccione una opción',
    yes_value: 'Sí',
    no_value: 'No',
    image_url_input: 'Imagen',
    image_info_message:
      'La imagen debe tener el tipo JPG/JPEG. Para obtener mejores resultados, utilice la resolución de 300x300px.',
    regional_lockout_input: 'Bloqueo regional',
    regional_lockout_placeholder: 'Seleccione regiones',
    deleting_button: 'Eliminando...',
    delete_button: '¿Está seguro de que desea eliminar lista de reproducción',
    delete_dialog_title: '¿Eliminar lista de reproducción "{{name}}"?',
    delete_dialog_content:
      'Al eliminar la lista de reproducción, se eliminará definitivamente del sistema.',
    delete_dialog_action_yes_button: 'Sí, eliminar',
    delete_dialog_action_no_button: 'Cancelar',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Guardar',
    router_prompt_create_title:
      '¿Está seguro de que desea salir sin guardar la creación de la lista de reproducción "{{name}}"?',
    router_prompt_create_message:
      'Al salir, la lista de reproducción no se guardará y se perderá toda la información introducida en ella.',
    router_prompt_edit_title:
      '¿Está seguro de que desea salir sin guardar los cambios en la lista de reproducción "{{name}}"?',
    router_prompt_edit_message:
      'Al salir, perderá todos los cambios introducidos recientemente en la lista de reproducción.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sí, salir',
    edit_cancel_toast: 'Se canceló la edición de la lista de reproducción "{{name}}".',
    create_cancel_toast: 'Se canceló la creación de la nueva lista de reproducción.',
    success: {
      save_success_button: '¡Guardado!',
      delete_success_button: '¡Eliminado!',
      create_success_message: '¡La lista de reproducción "{{name}}" se creó con éxito!',
      edit_success_message: 'Se guardaron los cambios en la lista de reproducción.',
    },
    errors: {
      error_on_save_button: 'Error al guardar',
      error_on_delete_button: 'Error al eliminar',
      edit_form_validation_invalid_field:
        'Complete este campo para completar la edición de la lista de reproducción',
      create_form_validation_invalid_field:
        'Complete este campo para completar la creación de la lista de reproducción',
      form_validation_invalid_color: 'El color indicado no es válido',
    },
  },
  playlist_item_form: {
    exit_button: 'Salir',
    create_playlist_item_title: 'Crear elemento en la lista de reproducción',
    save_button: 'Guardar',
    saving_button: 'Guardando...',
    deleting_button: 'Eliminando...',
    delete_button: 'Eliminar elemento de la lista de reproducción',
    delete_dialog_title:
      '¿Está seguro de que desea eliminar el elemento "{{name}}" de la lista de reproducción?',
    delete_dialog_content:
      'Al eliminar el elemento de la lista de reproducción, se eliminará definitivamente del sistema.',
    delete_dialog_action_yes_button: 'Sí, eliminar',
    delete_dialog_action_no_button: 'Cancelar',
    create_dialog_title:
      '¿Guardar la creación del elemento "{{name}}" en la lista de reproducción?',
    create_dialog_message:
      'Al guardar, el nuevo elemento de la lista de reproducción se agregará al sistema.',
    save_dialog_action_cancel_button: 'Cancelar',
    save_dialog_action_save_button: 'Guardar',
    router_prompt_create_title:
      '¿Está seguro de que desea salir sin guardar la creación del elemento "{{name}}" en la lista de reproducción?',
    router_prompt_create_message:
      'Al salir, el elemento de la lista de reproducción no se guardará y se perderá toda la información introducida en él.',
    router_prompt_edit_title:
      '¿Está seguro de que desea salir sin guardar los cambios en el elemento "{{name}}" de la lista de reproducción?',
    router_prompt_edit_message:
      'Al salir, perderá todos los cambios introducidos recientemente en el elemento de la lista de reproducción.',
    router_prompt_action_cancel_button: 'Cancelar',
    router_prompt_action_ok_button: 'Sí, salir',
    edit_cancel_toast: 'Se canceló la edición del elemento "{{name}}" de la lista de reproducción.',
    create_cancel_toast: 'Se canceló la creación del nuevo elemento en la lista de reproducción.',
    default_title_input: 'Título',
    title_translations_input: 'Traducciones para el título',
    default_description_input: 'Descripción',
    description_translations_input: 'Traducciones para la descripción',
    regional_lockout_input: 'Bloqueo regional',
    regional_lockout_placeholder: 'Seleccione regiones',
    state_input: 'Estado',
    state_value_true: 'Activo',
    state_value_false: 'Inactivo',
    playlist_input: 'Lista de reproducción',
    resource_url_input: 'URL del recurso',
    resource_input: 'Recurso',
    thumbnail_image_url_input: 'Imagen',
    thumbnail_image_info_message:
      'La imagen debe tener el tipo JPG/JPEG. Para obtener mejores resultados, utilice la resolución de 300x300px.',
    success: {
      save_success_button: '¡Guardado!',
      delete_success_button: '¡Eliminado!',
      create_success_message:
        '¡El elemento "{{name}}" se creó con éxito en la lista de reproducción!',
      edit_success_message: 'Se guardaron los cambios en el elemento de la lista de reproducción.',
    },
    errors: {
      error_on_save_button: 'Error al guardar',
      error_on_delete_button: 'Error al eliminar',
      edit_form_validation_invalid_field:
        'Complete este campo para completar la edición del elemento',
      create_form_validation_invalid_field:
        'Complete este campo para completar la creación del elemento',
      form_validation_invalid_resource_url: 'El URL del recurso indicado no es válido',
    },
  },
  licenses: {
    professional_caregiver: {
      root: {
        refresh_button: 'Actualizar',
        number_of_monthly_active_licenses_title: 'Licencias activas mensuales',
        number_of_active_seniors_title: 'Mayores activos',
        number_of_monthly_active_licenses_not_defined: 'No definido',
      },
    },
  },
  videocall: {
    you: 'Tú',
    incomingCall: 'Está llamándole...',
    incomingCallConnecting: 'Conectando..',
    incomingCallConnectingHint: '(puede tardar un poco)',
    answer: 'Atender',
    decline: 'Rechazar',
    settingsTitle: 'Ajustes',
    settingsMicrophone: 'Micrófono',
    settingsCamera: 'Cámara',
    settingsSpeaker: 'Altoparlante',
    default: 'Por defecto',
    callFailed: {
      title: 'La llamada falló',
      ok: 'Está bien',
      e486: 'Número de destino ocupado',
      e487: 'Solicitud terminada',
      e603: 'La llamada fue rechazada',
      e404: 'Número inválido',
      e480: 'El número de destino no está disponible',
      e402: 'Fondos insuficientes',
    },
  },
  entityFrontCardDesignTool: {
    title: 'Diseño de la tarjeta frontal de la entidad',
    step1: 'Entidad',
    step2: 'Usuarios',
    step3: 'Resultado',
    entityPicture: 'Logotipo de la entidad',
    chooseAnotherEntityPicture: 'Elige otro logotipo de la entidad',
    entityName: 'Nombre de la entidad',
    addEntityName: 'Añadir nombre de la entidad',
    entityNameToBePrinted: 'Nombre de la entidad a imprimir',
    entityNameTextColor: 'Color del texto del nombre de la entidad',
    entityNameTextFont: 'Tipo de  letra del texto del nombre de la entidad',
    next: 'Siguiente',
    save: 'Guardar',
    restart: 'Reiniciar',
    failedToLoadUsers: 'Error al cargar los usuarios',
  },
  entityBackCardDesignTool: {
    title: 'Diseño de la tarjeta trasera de la entidad',
    step1: 'Especificaciones',
    step2: 'Vista previa',
    entityName: 'Nombre de la entidad',
    entityNumber: 'Número de entidad',
    seniorUsersCount: 'Número de usuarios mayores',
    caregiverUsersCount: 'Número de usuarios cuidadores',
    cardName: 'Nombre de la tarjeta',
    entityPictureHeight: 'Altura de la imagen de la entidad',
    lineColor: 'Color de la línea',
    includeEntityName: 'Incluir nombre de la entidad',
    generatePreview: 'Generar vista previa',
    saveCards: 'Guardar tarjetas',
    saveSpreadsheet: 'Guardar hoja de cálculo',
    restart: 'Reiniciar',
  },
  cards: {
    pendingInvitation: 'Invitación pendiente',
    years: 'años',
  },
  dashboard_side_menu: {
    dashboard: 'Visión general',
    general_activity: 'Actividad geral',
    statistics: 'Estadísticas',
    general_statistics: 'Estadísticas generales',
    social_statistics: 'Estadísticas sociales',
  },
  digital_inclusion_side_menu: {
    applications: 'Aplicaciones',
    details: 'Detalles',
    users: 'Usuarios',
    photos: 'Fotografías',
    cards: 'Tarjetas',
    devices: 'Dispositivos',
    services: 'Servicios',
    playlists: 'Listas de reproducción',
  },
  seniorSideMenu: {
    generalStatistics: 'Estadísticas generales',
    healthStatistics: 'Estadísticas de salud',
    basicActivitiesDailyLife: 'ABVD',
    careAndActivity: 'Resumen de cuidados',
    individualPlan: 'Plan individual',
    personalData: 'Datos personales',
    careServices: 'Escala de Servicio',
  },
  teamSideMenu: {
    professionals: 'Profesionales',
    shifts: {
      title: 'Turnos',
      shiftsManagement: 'Gestión de turnos',
      shiftsAllocations: 'Asignación de turnos',
    },
    attendanceTracker: {
      title: 'Registro de asistencia',
      assignments: 'Asignaciones',
      registry: 'Registros',
    },
    careServices: 'Escala General de Servicio',
    permissions: 'Permisos',
    profileDropdown: {
      profile: 'Perfil',
      careServices: 'Escala de Servicio',
      applications: 'Aplicaciones',
      cards: 'Tarjetas',
      playLists: 'Listas de reproducción',
    },
  },
  profileSideMenu: {
    profile: 'Perfil',
  },
  settingsSideMenu: {
    institution: 'Institución',
    activities_sectors: {
      load_error: 'No se han podido cargar sectores de actividad',
    },
  },
  responsibleUserDropdown: {
    noResponsibleDefined: 'Responsable no definido',
    noCoordinatorDefined: 'Coordinador no definido',
  },
  activities_group_entry: {
    modals: {
      edit: {
        title: 'Editar detalles',
        observations_max_characters: 'Máximo de 120 caracteres.',
      },
      confirm_delete: {
        title: 'Eliminar ABVD - {{ name }}',
        copy: {
          title: '¿Estás seguro de que quieres borrar la actividad?',
          subtitle: 'Esta acción eliminará la actividad de la lista.',
        },
      },
    },
    activity_types: 'Tareas',
    no_activity_types: 'Aún no hay tareas adicionadas',
    configuration_type_not_defined: 'No definido',
    configuration_type_label: 'Tipo',
    exceptions: 'Excepciones',
    edit_details: 'Editar detalles',
    add_activities: 'Añadir',
    configure_first: 'Configure primero el tipo de repetición del grupo de actividades.',
    actions: {
      exit: 'Salir',
      edit: 'Editar',
      delete: 'Borrar',
      history: 'Historia',
    },
    snackbar: {
      create: {
        success: '¡La atividad se ha adicionado con éxito!',
        error: 'No se ha podido adicionada la atividad.',
      },
      update: {
        success: '¡La atividad se ha actualizado con éxito!',
        error: 'No se ha podido actualizar la atividad.',
      },
      delete: {
        success: '¡La atividad se ha borrado con éxito!',
        error: 'No se ha podido borrar la atividad.',
      },
      configuration: {
        success: '¡La atividad se ha cargado con éxito!',
        error: 'No se ha podido cargar la atividad.',
      },
    },
    error: 'No se ha podido cargar el registro del grupo de atividade.',
  },
  activities_type_entry: {
    modals: {
      edit: {
        title: 'Detalles de la tarea',
      },
      delete: {
        title: 'Detalles - {{ type }}',
        copy: {
          title: '¿Está seguro de que desea eliminar {{ type }} de las actividades?',
          subtitle: 'La actividad se eliminará de la lista de actividades de la persona mayor.',
        },
      },
      view: {
        leave: 'Salir',
        view_only: 'Apenas para vizualización, no puede editar.',
      },
    },
    snackbar: {
      update: {
        success: 'La tarea se ha actualizado con éxito!',
        error: 'No se ha podido actualizar la tarea.',
      },
      delete: {
        success: 'La tarea se ha eliminado con éxito!',
        error: 'No se ha podido borrar la tarea.',
      },
      configuration: {
        success: 'La tarea se ha cargado con éxito!',
        error: 'No se ha podido cargar la tarea.',
      },
    },
    templates: {
      name: 'Nombre del modelo',
      description: 'Descripción',
      activities_save: 'Actividades para ahorrar',
      activities_import: 'Seleccione las actividades',
      check_all: 'Marca todos',
      snackbar: {
        success_message:
          'LA plantilla "{{name}}" se guardó correctamente. Puede editar o borrar modelos en la configuración.',
        error_message: 'No se ha podido guardar la pantilla "{{name}}".',
        import_success_message: 'Plantilla "{{name}}" se aplicó correctamente.',
        import_error_message: 'Plantilla "{{name}}" no se peudo aplicar.',
      },
      template: 'Plantilla',
      confirmation_title: 'La plantilla "{{name}}" seleccionada será utilizado neste usuario',
      confirmation_subtitle:
        'Puedes editar o borrar actividades seleccionadas en cualquier momento',
      confirmation_replace_title:
        'Al importar la plantilla "{{name}}", se reemplazarán los datos del plan actual',
      confirmation_replace_subtitle: 'Para continuar escriba la palabra "CONFIRMO”',
      confirm: 'confirmo',
      name_error: 'El nombre de la plantilla debe tener menos de {{max}} caracteres',
      description_error: 'La descripción de la plantilla debe tener menos de {{max}} caracteres',
      select: 'Seleccione una plantilla',
    },
  },
  dynamic_form: {
    required_message: 'Campo obligatorio.',
    pattern_message: {
      invalid: 'Campo inválido.',
      max_characters: 'Máximo de {{ max }} caracteres.',
    },
    error_loading: 'Ha ocurrido un error al cargar el formulario.',
    feature_fields: {
      autocomplete: {
        load_more: 'Cargar más',
        loading: 'Cargando...',
        no_options: 'Sin opciones',
        placeholder: 'Seleccione un item',
        counter: 'A mostrar {{ current }} de {{ max }} itens',
      },
    },
  },
  modal: {
    ok: 'Ok',
    cancel: 'Anular',
    confirm: 'Confirmar',
    add: 'Añadir',
    save: 'Guardar',
    remove: 'Eliminar',
    import: 'Importar',
    back: 'Volver',
    next: 'Próximo',
    delete: 'Borrar',
    revert: 'Revertir',
    archive: 'Archivar',
  },
  daily_care_log: {
    generics: {
      back: 'Volver',
    },
    senior_actions: {
      open_actions: 'Abrir acciones de {{name}}',
      title: 'Acciones para {{name}}',
    },
    tabs: {
      home: 'Comienzo',
      occurrences: 'Ocurrencias',
      attendance_tracker: 'Fichaje',
    },
    date_carousel: {
      yesterday: 'Ayer',
      today: 'Hoy',
      tomorrow: 'Mañana',
    },
    task_table: {
      tasks: 'Tareas',
      empty: 'No hay tareas registradas.',
      select_all: 'Seleccionar todos os usuarios',
      sort: 'Ordernar',
      drawer: {
        clear: 'Apagar',
        save: 'Guardar',
        filters: {
          title: 'Filtros',
          social_responses: 'Servicios sociales',
        },
        sort: {
          title: 'Ordenar por',
          name: 'Nombre',
          autonomy: 'Autonomía',
          status: 'Estado',
          ascending: 'Creciente',
          descending: 'Decreciente',
        },
      },
    },
    cards: {
      entity_user: {
        task_conclusion_summary_label: {
          concluded: 'Concluido',
          task: 'Tarea',
        },
      },
    },
    home: {
      my_shifts: 'Mis turnos',
      care_services: 'Escala de servicios',
      other_shifts: 'Otros turnos',
      no_allocated_shifts: 'No hay turnos asignados para este día',
      no_other_shifts: 'No hay otros turnos para este día',
      no_care_services: 'No hay servicios asignados para este día',
      previous_days_shift: 'Turno del día anterior',
    },
    seniors: {
      title: 'Mayores',
      no_seniors: 'Aún no hay personas mayores inscritas.',
    },
    task_details: {
      task_not_done_reason: 'Razón para no hacerlo',
      task_done: 'Realizada',
      task_not_done: 'No realizada',
      add_a_note: 'Escribe un comentario...',
      cancel: 'Cancelar',
      save: 'Guardar',
      register: 'Registro',
      edit: 'Editar',
      edit_entry: 'Editar registro',
      disabled_tooltip: 'No tiene permisos para editar esta tarea.',
    },
    shift_collapsible: {
      shift: 'Turno',
      other_shift_members: 'Otros miembros del turno',
      no_other_shift_members: 'Ningún otro miembro en el turno',
    },
    shift_details: {
      all_tasks_handled: 'Se han completado todas las tareas del turno!',
      tabs: {
        simple: {title: 'Simples'},
        grouped: {title: 'Agrupadas', task: 'Tarea'},
        user: {
          title: 'Usuario',
          grid: {
            empty: 'Ningún usuario',
            empty_search_message: {
              title: 'Lo sentimos, no hemos encontrado ningún resultado para "{{ name }}".',
              subtitle: 'TIntenta otra búsqueda.',
            },
          },
        },
      },
      unplanned_tasks: {
        header_title: 'Nueva tarea no planificada',
        activity_sector: 'Sector de actividad',
        activity: 'Actividad',
        name: 'Nombre de la tarea',
        execution_date: 'Fecha de ejecución',
        execution_hour: 'Hora de ejecución',
        add_success_toast: '¡La tarea se ha registrado con éxito!',
        add_error_toast: 'No se ha podido registrar la tarea.',
        upload_error_toast: 'No se ha podido cargar los archivos.',
        add_multiple_success_toast: '¡Las tareas se han registrado con éxito!',
        add_cancel_modal_title: '¿Está seguro de que desea cancelar la adición de la tarea?',
        add_cancel_modal_subtitle: 'Todos los datos no guardados se perderán.',
        add_user_placeholder: 'Añadir usuario',
        add_files: 'Añadir archivos',
        delete_attachment: 'Tienes certeza que quieres eliminar el archivo "{{ name }}"?',
        files_size: 'Tamaño máximo por archivo {{ max }}MB.',
        file_size_error: 'El tamaño supera el máximo de {{ max }}MB.',
        file_not_supported_error: 'Formato del archivo no compatible.',
        file_both_errors: 'El archivo superar el máximo de {{ max }}MB y no es compatible.',
      },
    },
    grouped_tasks_details: {
      select_bottom_bar: {
        count: '{{ count }} usuario seleccionado',
        count_plural: '{{ count }} usuarios seleccionados',
        next: 'Próximo',
      },
    },
    grouped_tasks_handling: {
      snackbar: {
        loading_tasks: {
          error: '¡Error al cargar tareas!',
        },
        updating_tasks: {
          success: '¡Las tareas se han registrado con éxito!',
          error: 'Error al registrar las tareas.',
        },
      },
    },
    attendance_tracker: {
      title: 'Registro de asistencia',
      empty_title: 'Todavía no hay registros de asistencia',
      empty_subtitle: 'Pulsa el botón para fichar',
      register: 'Fichar',
      filters: {
        sections: {
          attendanceMovements: 'Movimiento',
          attendanceStatus: 'Estado',
          attendancePeriods: 'Franja del día',
        },
        values: {
          voided: 'Anulado',
          entry: 'Entradas',
          exit: 'Salidas',
          open: 'En curso',
          closed: 'Completado',
          morning: 'Mañana',
          afternoon: 'Tarde',
          night: 'Noche',
        },
      },
      session: {
        entry: 'Entrada',
        exit: 'Salida',
        running: 'En curso ahora',
        overdue: 'Por cerrar',
        closed: 'Completado',
        elapsed: '{{duration}} transcurridos',
        overdue_alert: 'Tienes una entrada abierta desde {{date}}. Hay que registrar la salida.',
        total: 'Total: {{duration}}',
      },
      bar: {
        running: 'En curso',
        since: 'Desde {{date}}',
        not_started: 'Registro de jornada sin iniciar',
      },
      manual: {
        registered_by: 'Registrado por {{name}}',
        entry_registered_by: 'Entrada registrada por {{name}}',
        exit_registered_by: 'Salida registrada por {{name}}',
        notice: 'Registrado manualmente por {{name}} el {{date}}, en nombre del profesional.',
        reason: 'Motivo: {{reason}}',
        no_location: 'Sin ubicación (registro manual)',
        unknown_user: 'otro usuario',
      },
      voided: {
        tag: 'Anulado',
        notice:
          'Esta sesión fue anulada por {{name}} el {{date}} y ya no cuenta como tiempo trabajado.',
        reason: 'Motivo: {{reason}}',
      },
      register_entry: 'Registrar entrada',
      register_exit: 'Registrar salida',
      load_error: 'No se pudieron obtener tus registros de asistencia.',
      empty_other_day: 'No hay registros de asistencia este día.',
      details: {
        no_exit: 'Todavía no se ha registrado la salida.',
        open_in_maps: 'Abrir en Google Maps',
        time_zone: 'Zona horaria',
      },
      new: {
        title_entry: 'Registrar entrada',
        title_exit: 'Registrar salida',
        save_entry: 'Registrar',
        save_exit: 'Registrar',
        record_type: 'Tipo de registro',
        entry: 'Entrada',
        exit: 'Salida',
        professional: 'Profesional',
        entity: 'Entidad',
        date_time: 'Fecha y hora',
        location: 'Ubicación',
        latitude: 'Latitud',
        longitude: 'Longitud',
        accuracy: 'Precisión',
        accuracy_value: '± {{meters}} m',
        waiting: 'Obteniendo tu ubicación exacta...',
        accuracy_info:
          'La precisión actual es de ± {{current}} m. Es la mejor que el dispositivo puede obtener ahora y se va actualizando. En un espacio interior, acércate a una ventana o sal al exterior para mejorarla.',
        unavailable:
          'No se pudo obtener tu ubicación. Comprueba que el GPS del dispositivo está activado.',
        loading_error:
          'No se pudo obtener tu último registro de asistencia. Comprueba la conexión e inténtalo de nuevo.',
        retry: 'Reintentar',
        success_entry: '¡Entrada registrada con éxito!',
        success_exit: '¡Salida registrada con éxito!',
        type_conflict:
          'Tu último registro de asistencia ha cambiado. Comprueba el tipo de registro y regístralo de nuevo.',
        save_error: 'No se pudo registrar la asistencia. Inténtalo de nuevo.',
        purpose:
          'Tu ubicación exacta se recoge solo en el momento en que registras la asistencia, para comprobar el lugar donde se registró. Estos datos son personales y quedan asociados a tu registro.',
        permission: {
          prompt_title: 'Necesitamos tu ubicación',
          prompt_description:
            'Para registrar la asistencia hay que comprobar dónde te encuentras. La ubicación solo se solicita en esta pantalla y se guarda únicamente en el momento del registro.',
          allow: 'Permitir ubicación',
          denied_title: 'La ubicación está bloqueada',
          denied_description:
            'Se denegó el acceso a la ubicación en este navegador, por lo que no es posible registrar la asistencia. Para volver a permitirlo:',
          unsupported_title: 'Ubicación no disponible',
          unsupported_description:
            'Este navegador no admite la ubicación o la página no se abre en una conexión segura (HTTPS).',
          help: {
            chrome:
              'Haz clic en el icono a la izquierda de la dirección (candado o ajustes), elige «Configuración del sitio» y, en «Ubicación», selecciona «Permitir». Después, recarga la página.',
            edge: 'Haz clic en el candado a la izquierda de la dirección, abre «Permisos de este sitio» y, en «Ubicación», selecciona «Permitir». Después, recarga la página.',
            firefox:
              'Haz clic en el icono de permisos a la izquierda de la dirección, quita el bloqueo de «Acceder a tu ubicación» y permítelo. Después, recarga la página.',
            safari:
              'Abre «Safari» > «Ajustes» > «Sitios web» > «Ubicación», elige este sitio y selecciona «Permitir». Después, recarga la página.',
            ios: 'Abre «Ajustes» > «Privacidad y seguridad» > «Localización», actívala y permite el acceso al navegador que estás usando. Después, recarga la página.',
            android_chrome:
              'Toca el icono a la izquierda de la dirección, abre «Permisos» > «Ubicación» y selecciona «Permitir». Si el bloqueo es del teléfono, activa la ubicación en «Ajustes» > «Ubicación». Después, recarga la página.',
            generic:
              'Abre los ajustes de permisos del sitio en tu navegador, permite el acceso a la ubicación y recarga la página.',
          },
        },
      },
    },
    occurrences: {
      empty_title: 'Aún no hay ocurrencias',
      empty_filters_title: 'No se encontraron ocurrencias para los filtros seleccionados.',
      empty_subtitle: 'Presione el botón para agregar una nueva ocurrencia',
      new: 'Nueva',
      new_occurrence: 'Nueva ocurrencia',
      add: 'Añadir',
      type: 'Tipo de ocurrencia',
      date: 'Fecha de ocurrencia',
      time: 'Hora de ocurrencia',
      local: 'Local de ocurrencia',
      responsible: 'Responsable de la ocurrencia',
      patient: 'Paciente involucrado',
      details: 'Ocurrencia detalles',
      add_success_toast: '¡La ocurrencia se ha registrado con éxito!',
      add_error_toast: 'Error al registrar la ocurrencia.',
      add_cancel_modal_title: '¿Está seguro de que desea cancelar la adición de la ocurrencia?',
      add_cancel_modal_subtitle: 'Todos los datos no guardados se perderán.',
      local_error: 'El local que introduzca no puede superar los {{ characters }} caracteres',
    },
  },
  care_and_activities: {
    date: 'Fecha',
    activity: 'Actividad',
    task: 'Tarea',
    evaluation: 'Evaluación',
    responsible: 'Tratada por',
    status: 'Estado',
    empty_state: 'No hay tareas registradas.',
    permissions: 'No tiene permisos para visualizar as tareas.',
    register: 'Registro',
    notes: 'Observaciones',
    status_types: {
      [CaregiverTaskCompletion.DONE]: 'Realizado',
      [CaregiverTaskCompletion.NOT_DONE]: 'No realizado',
      [CaregiverTaskCompletion.PLANNED]: 'Planeado',
      [CaregiverTaskCompletion.MISSED]: 'No tratado',
    },
    sort_icon: {
      sort_in_ascending: 'Ordenar en orden ascendente',
      sort_in_descending: 'Ordenar en orden descendente',
    },
    handle_task_modal: {
      task_status: {
        done: 'Realizada',
        not_done: 'No realizada',
      },
      details: 'Detalles',
      observations: 'Observaciones',
      treated: 'Tratada',
      cancel: 'Cancelar',
      write_observation: 'Escriba una observación.',
      observation_required_message: 'Cuéntenos más sobre por qué',
      card_collapse: {
        title: 'Información sobre las tareas',
      },
      time_input: 'Hora de ejecución',
      date_input: 'Fecha de registro',
      select_date: 'Seleccione la fecha',
      select_time: 'Seleccione la hora',
      task_info_load_error: 'Error al cargar la información de la tarea.',
      feature_input: {
        label: {
          [CareTaskFeature.BloodGlucoseHealthMeasurement]: 'Glucosa en sangre',
          [CareTaskFeature.BloodOxygenHealthMeasurement]: 'Oxígeno en sangre',
          [CareTaskFeature.BloodPressureHealthMeasurement]: 'Tensión arterial (SIS - DIA/mmHg)',
          [CareTaskFeature.BodyFatPercentageHealthMeasurement]: 'Porcentaje de grasa',
          [CareTaskFeature.BodyHeightHealthMeasurement]: 'Altura',
          [CareTaskFeature.BodyMusclePercentageHealthMeasurement]: 'Porcentaje de músculo',
          [CareTaskFeature.BodyTemperatureHealthMeasurement]: 'Temperatura corporal',
          [CareTaskFeature.BodyWaterPercentageHealthMeasurement]: 'Porcentaje de agua',
          [CareTaskFeature.BodyWeightHealthMeasurement]: 'Peso',
          [CareTaskFeature.HeartRateHealthMeasurement]: 'Frecuencia cardiaca',
          [CareTaskFeature.NatriureticPeptideTestHealthMeasurement]:
            'Prueba del péptido natriurético',
          [CareTaskFeature.RespiratoryRateHealthMeasurement]: 'Frecuencia respiratoria',
          [CareTaskFeature.SleepHealthMeasurement]: 'Medición de la salud del sueño',
          [CareTaskFeature.StepsHealthMeasurement]: 'Pasos',
        },
        inputs: {
          value: {
            label: 'Valor',
            placeholder: 'Insertar un valor',
          },
          minutes: {
            label: 'Minutes',
            placeholder: 'Insertar minutos',
          },
          percentage: {
            label: 'Porcentaje',
            placeholder: 'Insertar un porcentaje',
          },
          mealTime: {
            label: 'Hora de comer',
            placeholder: 'Seleccione un horario',
            options: {
              'before-meal': 'Antes da refeição',
              'after-meal': 'Depois da refeição',
            },
          },
          bnp: {
            label: 'Péptido natriurético',
            placeholder: 'Insertar un valor',
          },
          nt_proBnp: {
            label: 'N-terminal (NT)-pro BNP',
            placeholder: 'Insertar un valor',
          },
          type: {
            label: 'Tipo',
            placeholder: 'Seleccione un tipo',
            options: {
              'in-bed': 'En cama',
              awake: 'Despierto',
              rem: 'REM',
              core: 'Core',
              deep: 'Profundo',
            },
          },
          startTime: {
            label: 'Hora de inicio',
          },
          endTime: {
            label: 'Hora final',
          },
        },
        units: {
          steps: 'pasos',
        },
        required_message: 'Obligatorio',
        number_pattern_validation: {
          min: 'Mín. {{ min_suffix }}',
          max: 'Máx. {{ max_suffix }}',
        },
      },
      undo_task: 'Deshacer tarea',
      undo: 'Deshacer',
      edit_entry: 'Editar registro',
      confirm: 'Confirme',
      snackbar: {
        update: {
          success: '¡La tarea se ha registrado correctamente!',
          error: 'Se ha producido un error al actualizar el registro de tareas.',
        },
        reset: {
          success: '¡La tarea ha sido reiniciada!',
          error: 'Se ha producido un error al restablecer el registro de tareas.',
        },
      },
      history: {
        title: 'Histórico',
        update: 'editó la tarea',
        status: 'Estado',
        someone: 'Alguien',
        remove: 'deshizo la tarea',
        auto: 'La tarea fue considerada como realizada por el sistema',
        validate: 'validó la tarea',
        create: 'creó la tarea',
        read: 'leyó la tarea',
        contract_update: 'actualizó el contrato',
        changes: 'Cambios',
        task_not_done_reason: 'Motivo de no realización',
        notes: 'Notas',
        files_added: 'Archivos añadidos',
        files_removed: 'Archivos eliminados',
      },
      files: 'Archivos',
    },
  },
  configurations_fields: {
    feature: {
      name: {
        placeholder: 'Escriba un nombre para la tarea',
      },
    },
  },
  schedule_modal: {
    trigger_button_label: 'Intervalo de repetición',
    activity: {
      title: 'Elegir período - Personalizado',
    },
    task: {
      title: 'Elegir período - Personalizado',
    },
  },
  schedule_field: {
    readable_periodicity: {
      not_defined: '(No definido)',
      no_repetition: '(Sin repetición)',
      defined_per_activity: 'Repetición definida por la actividad',
      at_every: 'En cada',
      hourly: 'hora',
      daily: 'día',
      weekly: 'semana',
      monthly: 'mes',
      weekly_at: 'a',
      and: 'y',
      at_hour: 'a las',
      until_end_day: 'hasta',
    },
    quantities: 'Cantidades',
  },
  activity_type_posology_schedule_form: {
    title: 'Definir Posología',
    fields: {
      psology: {
        label: 'Posología',
      },
      posology_schedule_type: {
        label: 'Posología',
        options: {
          [PosologySchedule.PeriodsOfTheDay]: 'Hora del día',
          [PosologySchedule.TimesList]: 'Lista de horas',
          [PosologySchedule.Repetition]: 'Horas',
          [PosologySchedule.SOS]: 'SOS',
        },
      },
      frequency: {
        options: {
          [Frequency.Hourly]: 'Horas',
          [Frequency.Daily]: 'Días',
          [Frequency.Weekly]: 'Semanas',
          [Frequency.Monthly]: 'Meses',
        },
      },
      frequency_value: {
        label: 'Repetir cada',
        validations: {
          min: 'El valor debe ser igual o superior a uno.',
        },
      },
      time_list: {
        hour: {
          label: 'Hora',
        },
        quantity: {
          label: 'Cantidad',
        },
        add: 'Añadir hora',
        required: 'Añade al menos un horario.',
        error: 'Tiempo ya registrado.',
      },
      periods_of_the_day_item: {
        required: 'Añade al menos un.',
      },
      repetitionHourlyFrequencyValue: 'Toma cada',
      repetitionHourlyFrequencyValueHour: 'hora',
      repetitionHourlyFrequencyValueHours: 'horas',
      repetition_quantity: {
        label: 'cantidades',
        validations: {
          min: 'El valor debe ser igual o superior a uno.',
        },
      },
      end_date: {
        label: 'Fecha final',
        validations: {
          min: 'Fecha invalida',
        },
      },
      end_hour: {
        label: 'Hora Final',
        validations: {
          min: 'Hora invalida',
        },
      },
    },
  },
  care_plan_sheet_activities_group_entry_configurationvalue: {
    repetition: {
      per_activity: 'Por actividad',
      per_task: 'Por tarea',
    },
    aivd: {
      at_time: 'a',
      transportation: 'Transporte',
    },
  },
  periodicity_form: {
    empty_state: 'Aún no hay ninguna repetición establecida.',
    fields: {
      frequency: {
        label: 'A cada',
        hourly: 'Horas',
        daily: 'Días',
        weekly: 'Semanas',
        monthly: 'Meses',
      },
      frequency_value: {
        label: 'Repetición per:',
        activity: 'Actividad',
        task: 'Tarea',
        validations: {
          required: 'Campo obligatorio',
          min: 'El valor de la frecuencia tiene que ser superior a uno.',
          max: 'La frecuencia tiene que ser inferior o igual a {{ value }}.',
        },
      },
      start_at_date: {
        label: 'Fecha de inicio',
        validations: {
          superior_to_end_date: 'A data de inicío tem de ser anterior à data de fim.',
        },
      },
      end_date: {
        label: 'Fecha final',
        validations: {
          superior_to_start_date:
            'La fecha de finalización debe ser posterior a la fecha de inicio.',
        },
      },
      end_hour: {
        label: 'Hora de finalización',
      },
      days: {
        label: 'Días de la semana',
        validations: {
          required: 'Campo obligatorio',
        },
      },
    },
  },
  role_permissions_professional_caregiver_office_screen: {
    generic_header: {
      subtitle: 'Crear funciones y gestionar permisos',
    },
    fields: {
      name: {label: 'Nombre', placeholder: 'Añadir un nombre al puesto'},
      description: {label: 'Descripción', placeholder: 'Añadir una descripción'},
    },
    view: {
      tooltip: 'Permite al usuario ver contenidos',
      label: 'Ver',
    },
    edit: {
      tooltip: 'Permite al usuario editar contenidos',
      label: 'Editar',
    },
    select_all_permissions: 'Todos los permisos',
    localized_string_input: {
      name: {
        title: 'Traducción del nombre',
      },
      description: {
        title: 'Traducciones de la descripción',
      },
    },
    router_prompt: {
      title: 'Cargo y permisos',
      content: {
        title: '¿Seguro que quieres irte?',
        subtitle: 'Los cambios realizados se perderán.',
      },
    },
    snackbar: {
      load: {
        error: 'Se ha producido un error al cargar la página.',
      },
      update: {
        success: '¡Actualizado con éxito!',
        error: 'Se ha producido un error al actualizar los permisos para este puesto',
      },
      exit: 'Los cambios no se han guardado.',
    },
  },
  shifts_page: {
    shifts: 'Turnos',
    team: 'Equipo',
    create_button: 'Crear',
    schedule: 'Horario:',
    to_hour: 'a',
    responsible: {
      label: 'Responsable',
      not_defined: 'No definido',
    },
    professionals: {
      singular: 'Profesional',
      plural: 'Profesionales',
    },
    teams: {
      singular: 'equipo',
      plural: 'equipos',
    },
    empty_state: {
      title: 'Aún no hay turnos registrados.',
      subtitle: 'Pulse crear para registrar un turno.',
    },
    shift_modal: {
      create_shift: 'Crear turno',
      edit_shift: 'Editar turno',
      shift_form: {
        name: 'Nombre del turno',
        name_placeholder: 'Turno de la mañana, tarde, noche',
        required_field_message: 'Campo obligatorio.',
        min_required_field_message: 'Obligatorio.',
        pattern_field_message: 'Campo inválido.',
        max_characters_name_message: 'Máx {{ characters }} caracteres.',
        abbreviation: 'Id',
        abbreviation_placeholder: 'M(mañana), T(tarde), N(noche)',
        color: 'Color',
        social_response: 'Servicios sociales de equipo',
        social_responses_placeholder_input: 'Introducir servicios sociales',
        people_per_shift: 'Personas en turno',
        people_per_shift_placeholder_input: 'Máximo de personas por turno',
        start_hour: 'Hora inicio',
        end_hour: 'Hora final',
        shift_responsible: 'Responsable del turno',
        shift_responsible_placeholder: 'No hay responsable asignado',
        snackbar: {
          create_shift: {
            success: '¡Turno {{ name }} creado con éxito!',
            error: 'Error al crear el turno.',
          },
          update_shift: {
            success: '¡Turno {{ name }} actualizado con éxito!',
            error: 'Error al actualizar el turno.',
          },
        },
      },
    },
    confirm_delete: {
      title: 'Eliminar turno - {{ name }}',
      copy: {
        title: 'Está seguro de que desea eliminar el turno?',
        subtitle: 'Esta acción no puede deshacerse.',
      },
    },
    cancel: 'Cancelar',
    create: 'Crear',
    save: 'Guardar',
    delete: 'Eliminar',
  },
  planner: {
    header: {
      title: 'Calendario',
      filter_all_seniors_label: 'Todos los usuarios',
      filter_input_placeholder: 'Filtros',
      range: {
        day: 'Día',
        week: 'Semana',
        month: 'Mes',
      },
      add: 'Añadir',
      add_disabled: 'No tiene permisos para añadir esta tarea.',
      toggler: {
        calendar: 'calendario',
        list: 'lista',
      },
    },
    body: {
      untreaten: '',
      untreaten_one: '{{count}} No tratado',
      untreaten_other: '{{count}} No tratados',
      empty: 'No hay tareas para mostrar.',
    },
    side: {
      see_all: 'Ver todo',
      birthdays: 'Cumpleaños',
    },
    modal: {
      create: {
        activity: {
          ativities_group: 'Actividad',
          ativities_group_placeholder: 'Seleccione una actividad',
          activities_type: 'Tarea',
          activities_type_placeholder: 'Seleccione una tarea',
          add_users_label: 'Añadir usuarios',
          add_users_placeholder: 'Añadir usuarios',
          added_users: 'Usuarios añadidos',
          add_users_type: {
            social_response: 'Servicios sociales',
            user: 'Usuarios',
          },
          add_users_mark: '',
          add_users_mark_one: 'Marcar todos {{sr}} ({{count}} usuario)',
          add_users_mark_other: 'Marcar todos {{sr}} ({{count}} usuarios)',
          add_users_mark_all: 'Seleccionar todos los usuarios',
          success: '',
          success_one: '¡La tarea se ha añadido con éxito!',
          success_other: '¡Las tareas se han añadido con éxito!',
        },
      },
      delete: {
        title: {
          single: '¿Está seguro de que desea eliminar la tarea del usuario "{{name}}"?',
          multiple: '¿Estás seguro de que quieres eliminar las tareas "{{name}}"?',
        },
        desc: {
          single: 'La tarea se eliminará del plan de cuidados del usuario.',
          multiple: 'Las tareas se eliminarán del plan de cuidados de cada usuario.',
        },
      },
    },
    item_menu: {
      activity: {
        users: 'Usuarios',
        action: {
          delete: 'Eliminar',
          delete_disabled: 'No tiene permisos para eliminar esta tarea.',
          edit: 'Editar Plan',
          plan: 'Plan',
          register: 'Registar',
        },
      },
      anniversary: {
        birthday: 'Cumpleaños',
        born: 'Nacido en',
        years: 'años',
      },
    },
    errors: {
      content: 'Se ha producido un error al cargar el calendario.',
      save_activity: 'Error al guardar la actividad. Inténtalo de nuevo.',
      delete_activity: 'Error al eliminar la(s) tarea(s). Por favor, inténtalo de nuevo.',
      unknown_item_type: "Tipo de elemento del planificador desconocido: '{{ type }}'",
    },
  },
  care_services: {
    header: {
      add_button_disabled_tooltip: 'No tienes permisos para añadir servicios',
      seniors_label: 'Mayores',
      filter_caregivers_label: 'Todos los profesionales',
    },
    form: {
      required_field_message: 'Este campo es obligatorio',
      title_label: 'Título',
      title_placeholder: 'Título del servicio',
      color_label: 'Color',
      start_date_label: 'Fecha de inicio',
      end_date_label: 'Fecha de fin',
      start_time_label: 'Hora de inicio',
      end_time_label: 'Hora de fin',
      repetition_label: 'Recurrencia',
      responsible_users_label: 'Profesionales asociados',
      responsible_users_placeholder: 'Selecciona un responsable',
      senior_label: 'Mayor',
      senior_placeholder: 'Seleccionar mayor',
      notes_label: 'Observaciones',
      notes_placeholder: 'Escriba una observación',
      permissions_disabled_tooltip: 'No tienes permiso para editar este campo',
      twenty_four_hours_label: 'Servicio 24 horas',
    },
    modals: {
      edit: {
        title: 'Editar servicio',
      },
      create: {
        title: 'Crear servicio',
      },
      delete: {
        title: 'Eliminar servicio',
        description: '¿Estás seguro de que deseas eliminar este servicio?',
        cannot_be_undone: 'Esta acción no se puede deshacer.',
      },
      repetition_options: {
        only_this_event: 'Solo este evento',
        this_and_following: 'Este y siguientes',
      },
      edit_recurrent: {
        title: 'Editar servicio recurrente',
      },
      delete_recurrent: {
        title: 'Eliminar servicio recurrente',
      },
      edit_confirmation: {
        title: 'Guardar cambios en el servicio',
        subtitle: '¿Estás seguro de que deseas editar el servicio?',
      },
    },
    toasts: {
      edit: {
        success: {
          title: 'Servicio actualizado',
          message: 'El servicio se ha actualizado correctamente.',
        },
        error: {
          unknown: {
            title: 'Error al actualizar servicio',
            message:
              'Se ha producido un error al actualizar el servicio. Inténtalo de nuevo más tarde.',
          },
          not_found: {
            title: 'Servicio no encontrado',
            message: 'El turno de servicio que estás intentando editar no fue encontrado.',
          },
          access_denied: {
            title: 'Acceso denegado',
            message: 'No tienes permiso para editar este turno de servicio.',
          },
          invalid_data: {
            title: 'Datos inválidos',
            message:
              'Los datos proporcionados son inválidos. Por favor, verifica la información e inténtalo de nuevo.',
          },
          end_date_invalid: {
            title: 'Fecha de fin inválida',
            message: 'La fecha de fin debe ser posterior a la fecha de inicio.',
          },
          repetition_required: {
            title: 'Error de edición',
            message: 'La recurrencia es requerida cuando el servicio es recurrente.',
          },
          repetition_removal_scope: {
            title: 'Error de edición',
            message: 'Para eliminar la recurrencia necesita editar todos los eventos de la serie.',
          },
          repetition_change_scope: {
            title: 'Error de edición',
            message: 'Para cambiar la recurrencia necesita editar todos los eventos de la serie.',
          },
        },
      },
      create: {
        success: {
          title: 'Servicio creado',
          message: 'El servicio se ha creado correctamente.',
        },
        error: {
          title: 'Error al crear nuevo servicio',
          message:
            'Se ha producido un error al crear el nuevo servicio. Inténtalo de nuevo más tarde.',
        },
      },
      delete: {
        error: {
          title: 'Error al eliminar servicio',
          message:
            'Se ha producido un error al eliminar el servicio. Inténtalo de nuevo más tarde.',
        },
      },
    },
    export: {
      repetition: {
        ends_on: 'Termina el',
        ends_after: 'Termina después de',
        occurrences: 'ocurrencias',
      },
    },
    permissions: {
      view: 'No tienes permisos para ver los servicios.',
    },
  },
  shared: {
    handle_care_task_modal: {
      confirm_edit: {
        title: '¿Estás seguro de que quieres editar la tarea?',
      },
      confirm_edit_cancel: {
        title: 'Aún no has guardado la información. ¿Estás seguro de que quieres irte?',
        subtitle: 'Los datos no guardados se perderán.',
      },
      confirm_reset: {
        title: '¿Estás seguro de que quieres deshacer la tarea',
        subtitle: 'La tarea volverá a su estado inicial',
      },
    },
    input_disabled_tooltip: 'No tiene permisos para editar este campo',
  },
  export: {
    plan: 'Plan',
    address: 'Dirección',
    contributor_number: 'Nº Contribuyente',
    sns_number: 'Nº SNS',
    contact: 'Contacto',
    others: 'Otros',
    details: 'Detalles',
    tasks: 'Tareas',
    task: 'Tarea',
    register: 'Registro',
    date: 'Fecha',
    start_date: 'Inicio',
    end_date: 'Fin',
    periodicity: 'Periodicidad',
    observations: 'Observaciones',
    no_records: 'Sin registros',
    no_repetition: '(Sin repetición)',
    senior_profile: {
      save_pdf: 'Ficha de seguimiento (.pdf)',
      report_title: 'Ficha de seguimiento',
      user_section_title: 'Datos del paciente',
      profile_section_subtitle: 'Perfil',
      full_name: 'Nombre completo',
      preferred_name: 'Cómo le gusta que le llamen',
      birth_date: 'Fecha de nacimiento',
      sns_number: 'Número de usuario - SNS',
      social_security_number: 'Número de la Seguridad Social - NISS',
      taxpayer_number: 'Número de contribuyente',
      additional_information: 'Información adicional',
      health_conditions_subtitle: 'Patologías/Limitaciones',
      food_intolerances_title: 'Alergias / Intolerancias',
      food_intolerances_subtitle: 'Intolerancias (Alimentación)',
      medication_allergies_subtitle: 'Alergias (Medicación)',
      medication_title: 'Medicación',
      latest_vital_signs_title: 'Historial de signos vitales (últimos 7 días)',
    },
    error: {
      pdf_generation: 'Se ha producido un error al generar el PDF. Inténtalo de nuevo más tarde.',
      xlsx_generation:
        'Se ha producido un error al generar el Excel. Inténtalo de nuevo más tarde.',
      pdf_monthly_not_supported: 'La exportación mensual no es compatible.',
    },
  },
};
